import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast, Toaster } from 'react-hot-toast';
import { 
  FaBuilding, 
  FaUsers, 
  FaBriefcase, 
  FaFileAlt, 
  FaSearch, 
  FaPlus, 
  FaEdit, 
  FaTrash, 
  FaDownload,
  FaUpload,
  FaCamera,
  FaTimes,
  FaCheck,
  FaExclamationTriangle,
  FaEye,
  FaFilter,
  FaSort,
  FaBars,
  FaSignOutAlt,
  FaChartLine,
  FaCalendarCheck,
  FaCalendarTimes,
  FaClock,
  FaSignInAlt,
  FaSignOutAlt as FaSignOut,
  FaFileExcel,
  FaGift,
  FaMoneyCheckAlt,
  FaGraduationCap,
  FaHistory
} from 'react-icons/fa';
import AdminRewardsDashboard from './AdminRewardsDashboard';
import AdminGamingReport from './AdminGamingReport';
import AdminReferralReport from './AdminReferralReport';
import AdminReferredCandidatesStipendReport from './AdminReferredCandidatesStipendReport';
import AdminSPOCReport from './AdminSPOCReport';
import AdminSPOCUploadStatusReport from './AdminSPOCUploadStatusReport';
import AdminWorkDiaryDashboard from './AdminWorkDiaryDashboard';
import AdminSalaryAdvanceDashboard from './AdminSalaryAdvanceDashboard';
import AdminHigherEducationDashboard from './AdminHigherEducationDashboard';
import AdminRetentionReport from './AdminRetentionReport';
import AdminAssessmentDueReport from './AdminAssessmentDueReport';
import SPOCCompanyReports from './SPOCCompanyReports';
import SPOCAttritionReport from './SPOCAttritionReport';

// Helper function to format time in IST (Indian Standard Time)
const formatTimeIST = (dateString) => {
  if (!dateString) return '-';
  
  let date;
  try {
    if (dateString instanceof Date) {
      date = dateString;
    } else {
      const dateStr = String(dateString).trim();
      
      // Always treat database timestamps as UTC
      // If it already has Z or timezone offset, use as-is
      if (dateStr.includes('Z') || dateStr.match(/[+-]\d{2}:\d{2}$/)) {
        date = new Date(dateStr);
      } else {
        // For any string that looks like a date-time, ALWAYS treat as UTC
        // Normalize: replace space with T
        let normalizedStr = dateStr.replace(' ', 'T');
        
        // Extract date and time parts
        const dateTimeMatch = normalizedStr.match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2}:\d{2})(\.\d+)?/);
        if (dateTimeMatch) {
          // Explicitly create UTC date by appending Z
          const utcString = `${dateTimeMatch[1]}T${dateTimeMatch[2]}${dateTimeMatch[3] || ''}Z`;
          date = new Date(utcString);
        } else {
          // Fallback: try to parse and if it has T, treat as UTC
          if (normalizedStr.includes('T')) {
            date = new Date(normalizedStr + 'Z');
          } else {
            // Last resort: parse as-is (might be interpreted as local time)
            date = new Date(dateStr);
          }
        }
      }
    }
    
    // Validate the date
    if (isNaN(date.getTime())) {
      console.warn('Invalid date:', dateString, 'Type:', typeof dateString);
      return '-';
    }
    
    // Convert to IST (UTC+5:30) using toLocaleTimeString with timeZone
    // This correctly converts from UTC to IST
    return date.toLocaleTimeString('en-IN', { 
      timeZone: 'Asia/Kolkata',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  } catch (error) {
    console.error('Error formatting time:', error, dateString);
    return '-';
  }
};

// Helper function to format date in IST
const formatDateIST = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-IN', { 
    timeZone: 'Asia/Kolkata',
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

// Component to show company details (candidates and jobs)
const CompanyDetails = ({ companyId, companyName }) => {
  const [candidates, setCandidates] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loadingCandidates, setLoadingCandidates] = useState(true);
  const [loadingJobs, setLoadingJobs] = useState(true);
  const [showCandidates, setShowCandidates] = useState(true);
  const [showJobs, setShowJobs] = useState(true);

  const getApiConfig = () => {
    const token = localStorage.getItem('gogig_spoc_access');
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';
    return { token, API_URL };
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { API_URL, token } = getApiConfig();
        
        // Fetch candidates
        const candidatesRes = await fetch(`${API_URL}/api/spoc/candidates?page=1&limit=5&company_id=${companyId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const candidatesData = await candidatesRes.json();
        setCandidates(candidatesData.candidates || []);
        
        // Fetch jobs
        const jobsRes = await fetch(`${API_URL}/api/spoc/jobs?page=1&limit=5&company_id=${companyId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const jobsData = await jobsRes.json();
        setJobs(jobsData.jobs || []);
      } catch (error) {
        console.error('Error fetching company details:', error);
      } finally {
        setLoadingCandidates(false);
        setLoadingJobs(false);
      }
    };
    
    fetchData();
  }, [companyId]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Candidates Section */}
      <div>
        <div 
          style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: 12,
            cursor: 'pointer'
          }}
          onClick={(e) => {
            e.stopPropagation();
            setShowCandidates(!showCandidates);
          }}
        >
          <h5 style={{ fontSize: 14, fontWeight: 600, color: '#374151', margin: 0 }}>
            Candidates ({candidates.length})
          </h5>
          <span style={{ color: '#667eea', fontSize: 12 }}>
            {showCandidates ? '▼' : '▶'}
          </span>
        </div>
        {showCandidates && (
          <div>
            {loadingCandidates ? (
              <div style={{ textAlign: 'center', padding: 16, color: '#6b7280', fontSize: 12 }}>
                Loading...
              </div>
            ) : candidates.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {candidates.map((candidate) => (
                  <div key={candidate.id} style={{
                    padding: 12,
                    background: '#f8fafc',
                    borderRadius: 8,
                    border: '1px solid #e5e7eb'
                  }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>
                      {candidate.name}
                    </div>
                    <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                      {candidate.email} • {candidate.mobile}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: 16, color: '#6b7280', fontSize: 12 }}>
                No candidates found
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Jobs Section */}
      <div>
        <div 
          style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: 12,
            cursor: 'pointer'
          }}
          onClick={(e) => {
            e.stopPropagation();
            setShowJobs(!showJobs);
          }}
        >
          <h5 style={{ fontSize: 14, fontWeight: 600, color: '#374151', margin: 0 }}>
            Posted Jobs ({jobs.length})
          </h5>
          <span style={{ color: '#22c55e', fontSize: 12 }}>
            {showJobs ? '▼' : '▶'}
          </span>
        </div>
        {showJobs && (
          <div>
            {loadingJobs ? (
              <div style={{ textAlign: 'center', padding: 16, color: '#6b7280', fontSize: 12 }}>
                Loading...
              </div>
            ) : jobs.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {jobs.map((job) => (
                  <div key={job.id} style={{
                    padding: 12,
                    background: '#f0fdf4',
                    borderRadius: 8,
                    border: '1px solid #bbf7d0'
                  }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>
                      {job.title}
                    </div>
                    <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                      {job.location || 'N/A'} • {
                        (job.salary_min || job.stipend_min) 
                          ? `₹${job.salary_min || job.stipend_min}` 
                          : 'N/A'
                      } { 
                        (job.salary_max || job.stipend_max) 
                          ? `- ₹${job.salary_max || job.stipend_max}` 
                          : '' 
                      }
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: 16, color: '#6b7280', fontSize: 12 }}>
                No jobs posted yet
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Custom Establishment Dropdown Component
const EstablishmentDropdown = ({ establishments, onSelect }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEstablishment, setSelectedEstablishment] = useState(null);

  const filteredEstablishments = establishments.filter(establishment =>
    establishment.establishmentId.toLowerCase().includes(searchTerm.toLowerCase()) ||
    establishment.companyName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelect = (establishment) => {
    setSelectedEstablishment(establishment);
    onSelect(establishment.establishmentId, establishment);
    setIsOpen(false);
    setSearchTerm('');
  };

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <div
        onClick={() => setIsOpen(!isOpen)}
        style={{
          width: '100%',
          borderRadius: 16,
          padding: '10px 16px',
          fontSize: 16,
          border: '1.5px solid #e0f7fa',
          background: '#f8fafc',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          transition: 'border-color 0.2s ease',
          minHeight: '44px'
        }}
        onMouseEnter={(e) => e.target.style.borderColor = '#22c55e'}
        onMouseLeave={(e) => e.target.style.borderColor = '#e0f7fa'}
      >
        <span style={{ 
          color: selectedEstablishment ? '#333' : '#999',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}>
          {selectedEstablishment 
            ? `${selectedEstablishment.establishmentId} - ${selectedEstablishment.companyName}`
            : 'Select Establishment ID'
          }
        </span>
        <FaSearch style={{ 
          color: '#22c55e', 
          fontSize: 14,
          transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
          transition: 'transform 0.2s ease'
        }} />
      </div>

      {isOpen && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          background: '#fff',
          border: '1.5px solid #e0f7fa',
          borderRadius: 16,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          zIndex: 1000,
          maxHeight: 300,
          overflow: 'hidden',
          marginTop: 4
        }}>
          {/* Search Input */}
          <div style={{
            padding: '12px 16px',
            borderBottom: '1px solid #e0f7fa',
            background: '#f8fafc'
          }}>
            <input
              type="text"
              placeholder="Search establishments..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #e0f7fa',
                borderRadius: 8,
                fontSize: 14,
                outline: 'none',
                background: '#fff'
              }}
              onClick={(e) => e.stopPropagation()}
            />
          </div>

          {/* Options */}
          <div style={{
            maxHeight: 200,
            overflowY: 'auto'
          }}>
            {filteredEstablishments.length === 0 ? (
              <div style={{
                padding: '16px',
                textAlign: 'center',
                color: '#666',
                fontSize: 14
              }}>
                No establishments found
              </div>
            ) : (
              filteredEstablishments.map((establishment, index) => (
                <div
                  key={establishment.establishmentId}
                  onClick={() => handleSelect(establishment)}
                  style={{
                    padding: '12px 16px',
                    cursor: 'pointer',
                    borderBottom: index < filteredEstablishments.length - 1 ? '1px solid #f0f0f0' : 'none',
                    transition: 'background-color 0.2s ease',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 4
                  }}
                  onMouseEnter={(e) => e.target.style.background = '#f8fafc'}
                  onMouseLeave={(e) => e.target.style.background = 'transparent'}
                >
                  <div style={{
                    fontWeight: 600,
                    color: '#333',
                    fontSize: 14,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8
                  }}>
                    <span style={{ color: '#22c55e' }}>{establishment.establishmentId}</span>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {establishment.companyName}
                    </span>
                  </div>
                  <div style={{
                    color: '#999',
                    fontSize: 12,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8
                  }}>
                    <FaSearch style={{ fontSize: 10 }} />
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {establishment.email}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          <div style={{
            padding: '8px 16px',
            background: '#f8fafc',
            borderTop: '1px solid #e0f7fa',
            fontSize: 12,
            color: '#666',
            textAlign: 'center'
          }}>
            {filteredEstablishments.length} establishment{filteredEstablishments.length !== 1 ? 's' : ''} found
          </div>
        </div>
      )}

      {/* Click outside to close */}
      {isOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 999
          }}
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

// Custom searchable Company dropdown (same UX as EstablishmentDropdown)
const CompanyDropdown = ({ companies = [], value, onSelect, placeholder = 'Select Company' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const selectedCompany = companies.find((c) => String(c.id) === String(value)) || null;

  const filteredCompanies = companies.filter((company) => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return true;
    const name = (company.company_name || '').toLowerCase();
    const code = (company.establishment_code || '').toLowerCase();
    const email = (company.email || '').toLowerCase();
    return name.includes(q) || code.includes(q) || email.includes(q);
  });

  const handleSelect = (company) => {
    onSelect(String(company.id), company);
    setIsOpen(false);
    setSearchTerm('');
  };

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <div
        onClick={() => setIsOpen(!isOpen)}
        style={{
          width: '100%',
          borderRadius: 16,
          padding: '10px 16px',
          fontSize: 16,
          border: '1.5px solid #e0f7fa',
          background: '#f8fafc',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          transition: 'border-color 0.2s ease',
          minHeight: '44px',
          gap: 8
        }}
        onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#22c55e'; }}
        onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#e0f7fa'; }}
      >
        <span style={{
          color: selectedCompany ? '#333' : '#999',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          flex: 1
        }}>
          {selectedCompany
            ? (selectedCompany.establishment_code
                ? `${selectedCompany.company_name} (${selectedCompany.establishment_code})`
                : selectedCompany.company_name)
            : placeholder}
        </span>
        <FaSearch style={{
          color: '#22c55e',
          fontSize: 14,
          flexShrink: 0
        }} />
      </div>

      {isOpen && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          background: '#fff',
          border: '1.5px solid #e0f7fa',
          borderRadius: 16,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          zIndex: 1000,
          maxHeight: 320,
          overflow: 'hidden',
          marginTop: 4
        }}>
          <div style={{
            padding: '12px 16px',
            borderBottom: '1px solid #e0f7fa',
            background: '#f8fafc'
          }}>
            <input
              type="text"
              placeholder="Search by company name or establishment ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoFocus
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #e0f7fa',
                borderRadius: 8,
                fontSize: 14,
                outline: 'none',
                background: '#fff'
              }}
              onClick={(e) => e.stopPropagation()}
            />
          </div>

          <div style={{ maxHeight: 220, overflowY: 'auto' }}>
            {filteredCompanies.length === 0 ? (
              <div style={{
                padding: '16px',
                textAlign: 'center',
                color: '#666',
                fontSize: 14
              }}>
                No companies found
              </div>
            ) : (
              filteredCompanies.map((company, index) => (
                <div
                  key={company.id}
                  onClick={() => handleSelect(company)}
                  style={{
                    padding: '12px 16px',
                    cursor: 'pointer',
                    borderBottom: index < filteredCompanies.length - 1 ? '1px solid #f0f0f0' : 'none',
                    background: String(company.id) === String(value) ? '#f0fdf4' : 'transparent',
                    transition: 'background-color 0.15s ease',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 4
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#f0fdf4'; }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background =
                      String(company.id) === String(value) ? '#f0fdf4' : 'transparent';
                  }}
                >
                  <div style={{
                    fontWeight: 600,
                    color: '#333',
                    fontSize: 14,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {company.company_name}
                  </div>
                  <div style={{
                    color: '#6b7280',
                    fontSize: 12,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8
                  }}>
                    {company.establishment_code && (
                      <span style={{ color: '#22c55e', fontWeight: 600 }}>
                        {company.establishment_code}
                      </span>
                    )}
                    {company.company_type && (
                      <span style={{ textTransform: 'uppercase' }}>{company.company_type}</span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          <div style={{
            padding: '8px 16px',
            background: '#f8fafc',
            borderTop: '1px solid #e0f7fa',
            fontSize: 12,
            color: '#666',
            textAlign: 'center'
          }}>
            {filteredCompanies.length} compan{filteredCompanies.length !== 1 ? 'ies' : 'y'} found
          </div>
        </div>
      )}

      {isOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 999
          }}
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

// SPOC fetch helper for report components (same API URLs, SPOC token)
const getSpocReportFetch = () => {
  const getToken = () => localStorage.getItem('gogig_spoc_access');
  return async (url, options = {}) => {
    const token = getToken();
    if (!token) throw new Error('Not authenticated');
    const headers = { ...(options.headers || {}), Authorization: `Bearer ${token}` };
    return fetch(url, { ...options, headers });
  };
};

const SPOCDashboard = ({ onLogout }) => {
  console.log('SPOCDashboard component rendered');
  console.log('SPOCDashboard props:', { onLogout });
  const [activeTab, setActiveTab] = useState('companies');
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';
  const spocFetch = useCallback(getSpocReportFetch(), []);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Companies state
  const [companies, setCompanies] = useState([]);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [companyStats, setCompanyStats] = useState({}); // Store candidates and jobs count for each company

  // Candidates state
  const [candidates, setCandidates] = useState([]);
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [candidatesPage, setCandidatesPage] = useState(1);
  const [candidatesTotalPages, setCandidatesTotalPages] = useState(1);
  const [candidatesTotal, setCandidatesTotal] = useState(0);
  const [candidatesSearch, setCandidatesSearch] = useState('');
  const [selectedCompanyFilter, setSelectedCompanyFilter] = useState('');
  const [candidatesSubTab, setCandidatesSubTab] = useState('registered'); // 'registered', 'unregistered', 'employees', 'inactive', 'supervisor-candidates'
  
  // Unregistered candidates state
  const [unregisteredCandidates, setUnregisteredCandidates] = useState([]);
  const [unregisteredLoading, setUnregisteredLoading] = useState(false);
  const [unregisteredPage, setUnregisteredPage] = useState(1);
  const [unregisteredTotalPages, setUnregisteredTotalPages] = useState(1);
  
  // Active employees state
  const [activeEmployees, setActiveEmployees] = useState([]);
  const [inactiveEmployees, setInactiveEmployees] = useState([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [activeEmployeesPage, setActiveEmployeesPage] = useState(1);
  const [activeEmployeesTotalPages, setActiveEmployeesTotalPages] = useState(1);
  const [activeEmployeesTotal, setActiveEmployeesTotal] = useState(0);
  const [inactiveEmployeesPage, setInactiveEmployeesPage] = useState(1);
  const [inactiveEmployeesTotalPages, setInactiveEmployeesTotalPages] = useState(1);
  const [inactiveEmployeesTotal, setInactiveEmployeesTotal] = useState(0);
  const [exportingInactiveCsv, setExportingInactiveCsv] = useState(false);
  /** 'all' or calendar year string — scopes stipend activity for Active/Inactive employees (matches API activity_year) */
  const [employeesActivityYear, setEmployeesActivityYear] = useState('all');
  const [employeesListMeta, setEmployeesListMeta] = useState({
    activityYear: 'all',
    refYear: null,
    refMonth: null
  });

  // Supervisor candidates state
  const [supervisorCandidates, setSupervisorCandidates] = useState([]);
  const [supervisorCandidatesLoading, setSupervisorCandidatesLoading] = useState(false);
  const [supervisorCandidatesPage, setSupervisorCandidatesPage] = useState(1);
  const [supervisorCandidatesTotalPages, setSupervisorCandidatesTotalPages] = useState(1);
  const [supervisorCandidatesTotal, setSupervisorCandidatesTotal] = useState(0);
  
  // Counts state for all candidate tabs
  const [registeredCount, setRegisteredCount] = useState(0);
  const [unregisteredCount, setUnregisteredCount] = useState(0);
  const [employeesCount, setEmployeesCount] = useState(0);
  const [inactiveCount, setInactiveCount] = useState(0);
  const [supervisorCandidatesSearch, setSupervisorCandidatesSearch] = useState('');
  const [selectedSupervisorFilter, setSelectedSupervisorFilter] = useState('');
  
  // Supervisor candidates CSV upload state
  const [showCandidatesUploadModal, setShowCandidatesUploadModal] = useState(false);
  const [candidatesUploadFile, setCandidatesUploadFile] = useState(null);
  const [candidatesUploading, setCandidatesUploading] = useState(false);
  const [candidatesUploadResult, setCandidatesUploadResult] = useState(null);

  // Supervisor candidate edit state
  const [showSupervisorCandidateEditModal, setShowSupervisorCandidateEditModal] = useState(false);
  const [editingSupervisorCandidate, setEditingSupervisorCandidate] = useState(null);
  const [supervisorCandidateEditForm, setSupervisorCandidateEditForm] = useState({
    establishment_name: '',
    month: '',
    punching_code: '',
    employee_code: '',
    remark: '',
    contract_code: '',
    apprentice_code: '',
    aadhar_no: '',
    date_of_birth: '',
    gender: '',
    father_name: '',
    mother_name: '',
    category: '',
    address: '',
    district: '',
    state: '',
    pincode: '',
    qualification: '',
    institute_name: '',
    specialization: '',
    bank_name: '',
    account_no: '',
    ifsc_code: '',
    seeded_account: '',
    beni_id_status: '',
    date_of_joining: '',
    stipend: '',
    job_role: '',
    establishment_address: '',
    insurance: '',
    id_card: '',
    location: '',
    status: '',
    referral_code: ''
  });
  
  // Supervisor candidate document state
  const [showSupervisorCandidateDocumentModal, setShowSupervisorCandidateDocumentModal] = useState(false);
  const [selectedSupervisorCandidate, setSelectedSupervisorCandidate] = useState(null);
  const [supervisorCandidateDocuments, setSupervisorCandidateDocuments] = useState([]);

  // Jobs state
  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [jobsPage, setJobsPage] = useState(1);
  const [jobsTotalPages, setJobsTotalPages] = useState(1);
  const [jobsSearch, setJobsSearch] = useState('');
  const [selectedJobCompanyFilter, setSelectedJobCompanyFilter] = useState('');
  
  // Job posting state
  const [showJobForm, setShowJobForm] = useState(false);
  const [jobForm, setJobForm] = useState({
    title: '',
    description: '',
    age_criteria: '',
    stipend_min: '',
    stipend_max: '',
    qualification: [],
    location: '',
    company_id: ''
  });
  const [jobFormError, setJobFormError] = useState('');
  const [jobFormSuccess, setJobFormSuccess] = useState('');
  const [submittingJob, setSubmittingJob] = useState(false);
  const [jobLogoFile, setJobLogoFile] = useState(null);
  const jobLogoInputRef = useRef(null);

  // Supervisors state
  const [supervisors, setSupervisors] = useState([]);
  const [supervisorsLoading, setSupervisorsLoading] = useState(false);
  const [supervisorsForDropdown, setSupervisorsForDropdown] = useState([]); // Full list for filter dropdowns
  const [supervisorListPage, setSupervisorListPage] = useState(1);
  const [supervisorListTotal, setSupervisorListTotal] = useState(0);
  const [supervisorListTotalPages, setSupervisorListTotalPages] = useState(1);
  const [supervisorListSearch, setSupervisorListSearch] = useState('');
  const SUPERVISOR_LIST_PAGE_SIZE = 10;
  const [showAddSupervisorModal, setShowAddSupervisorModal] = useState(false);
  const [addSupervisorForm, setAddSupervisorForm] = useState({ name: '', email: '', mobile: '', password: '', company_id: '' });
  const [addSupervisorSubmitting, setAddSupervisorSubmitting] = useState(false);

  // Supervisor Attendance state
  const [supervisorAttendance, setSupervisorAttendance] = useState([]);
  const [supervisorAttendanceLoading, setSupervisorAttendanceLoading] = useState(false);
  const [selectedSupervisorForAttendance, setSelectedSupervisorForAttendance] = useState('');
  const [attendanceMonth, setAttendanceMonth] = useState(new Date().getMonth() + 1);
  const [attendanceYear, setAttendanceYear] = useState(new Date().getFullYear());
  const [attendancePage, setAttendancePage] = useState(1);
  const [attendanceTotalPages, setAttendanceTotalPages] = useState(1);
  const [attendanceTotal, setAttendanceTotal] = useState(0);
  const [attendanceError, setAttendanceError] = useState(null);

  // Stipend upload monitoring state
  const [stipendUploadStatus, setStipendUploadStatus] = useState([]);
  const [stipendUploadLoading, setStipendUploadLoading] = useState(false);
  const [stipendUploadYear, setStipendUploadYear] = useState(new Date().getFullYear());

  // Document management state
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [candidateDocuments, setCandidateDocuments] = useState([]);
  const [documentForm, setDocumentForm] = useState({
    document_type: '',
    file: null
  });
  const [uploadingDocument, setUploadingDocument] = useState(false);

  // Document upload section state (similar to admin)
  const [docImportTab, setDocImportTab] = useState('naps');
  const [selectedDocumentType, setSelectedDocumentType] = useState('');
  const [stipendReportYear, setStipendReportYear] = useState(new Date().getFullYear());
  const [stipendReportMonth, setStipendReportMonth] = useState(new Date().getMonth() + 1);
  const [selectedEstablishmentCode, setSelectedEstablishmentCode] = useState('');
  const [companyNameInput, setCompanyNameInput] = useState('');
  const [selectedCompanyId, setSelectedCompanyId] = useState(''); // For company dropdown
  const [uploading, setUploading] = useState(false);

  // Applicants state
  const [showApplicantsModal, setShowApplicantsModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [applicants, setApplicants] = useState([]);
  const [applicantsLoading, setApplicantsLoading] = useState(false);
  const [jobApplicantCounts, setJobApplicantCounts] = useState({});
  const [uploadResult, setUploadResult] = useState(null);
  const [csvHistory, setCsvHistory] = useState([]);

  // Add state for NAPS establishment IDs
  const [napsEstablishmentIds, setNapsEstablishmentIds] = useState([]);
  
  // Add state for BVOC establishment IDs
  const [bvocEstablishmentIds, setBvocEstablishmentIds] = useState([]);

  // Add state for NATS establishment IDs
  const [natsEstablishmentIds, setNatsEstablishmentIds] = useState([]);
  const [showCameraModal, setShowCameraModal] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);

  // Helper function to get API URL and token
  const getApiConfig = () => {
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';
    const token = localStorage.getItem('gogig_spoc_access');
    console.log('SPOCDashboard API_URL:', API_URL);
    console.log('SPOCDashboard token:', token);
    return { API_URL, token };
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchUserProfile();
  }, []);

  useEffect(() => {
    if (activeTab === 'companies') {
      fetchCompanies();
    } else if (activeTab === 'candidates') {
      // Fetch all counts when candidates tab is first loaded
      fetchAllCandidateCounts();
      fetchCandidates();
    } else if (activeTab === 'jobs') {
      fetchJobs();
    } else if (activeTab === 'supervisors') {
      fetchSupervisors();
    } else if (activeTab === 'supervisor-attendance') {
      fetchSupervisorsForDropdown();
    } else if (activeTab === 'documents') {
      fetchNapsEstablishmentIds();
      fetchBvocEstablishmentIds();
      fetchNatsEstablishmentIds();
    } else if (activeTab === 'stipend-monitoring') {
      fetchStipendUploadStatus();
    }
  }, [activeTab, supervisorListPage]);

  useEffect(() => {
    if (activeTab === 'stipend-monitoring') {
      fetchStipendUploadStatus();
    }
  }, [stipendUploadYear]);

  useEffect(() => {
    if (activeTab === 'candidates') {
      if (candidatesSubTab === 'registered') {
      fetchCandidates();
      } else if (candidatesSubTab === 'unregistered') {
        fetchUnregisteredCandidates();
      } else if (candidatesSubTab === 'employees') {
        fetchActiveEmployees();
      } else if (candidatesSubTab === 'supervisor-candidates') {
        fetchSupervisorsForDropdown(); // Full list for filter dropdown
        fetchSupervisorCandidates();
      }
    }
  }, [activeTab, candidatesSubTab, candidatesPage, candidatesSearch, selectedCompanyFilter, unregisteredPage, supervisorCandidatesPage, supervisorCandidatesSearch, selectedSupervisorFilter]);

  // Fetch employees when company filter changes or page changes
  useEffect(() => {
    if (activeTab === 'candidates' && (candidatesSubTab === 'employees' || candidatesSubTab === 'inactive')) {
      fetchActiveEmployees();
    }
  }, [selectedCompanyFilter, candidatesSubTab, activeEmployeesPage, inactiveEmployeesPage, employeesActivityYear]);

  useEffect(() => {
    if (activeTab === 'jobs') {
      fetchJobs();
    }
  }, [jobsPage, jobsSearch, selectedJobCompanyFilter]);

  const fetchUserProfile = async () => {
    console.log('fetchUserProfile called');
    try {
      // Get SPOC data from localStorage first
      const spocData = localStorage.getItem('gogig_spoc_data');
      console.log('SPOC data from localStorage:', spocData);
      if (spocData) {
        const parsedData = JSON.parse(spocData);
        console.log('Parsed SPOC data:', parsedData);
        setUser(parsedData);
        setLoading(false);
        console.log('User set, loading set to false');
        return;
      }

      // If no data in localStorage, try to fetch from API
      const token = localStorage.getItem('gogig_spoc_access');
      console.log('Token from localStorage:', token);
      if (!token) {
        console.log('No token found, throwing error');
        throw new Error('No authentication token found');
      }

      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';
      const response = await fetch(`${API_URL}/api/spoc/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      } else {
        throw new Error('Failed to fetch profile');
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error('Failed to load profile');
      // If there's an error, try to logout
      if (onLogout) onLogout();
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      setCompaniesLoading(true);
      const { API_URL, token } = getApiConfig();
      const response = await fetch(`${API_URL}/api/spoc/companies`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        
        // Deduplicate companies by ID (safety measure)
        const uniqueCompanies = [];
        const seenIds = new Set();
        for (const company of data) {
          if (company && company.id && !seenIds.has(company.id)) {
            seenIds.add(company.id);
            uniqueCompanies.push(company);
          }
        }
        
        setCompanies(uniqueCompanies);
        
        // Fetch stats for each company
        const stats = {};
        for (const company of uniqueCompanies) {
          try {
            // Fetch candidates count (registered candidates)
            const candidatesRes = await fetch(`${API_URL}/api/spoc/candidates?page=1&limit=1&company_id=${company.id}`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            let candidatesCount = 0;
            if (candidatesRes.ok) {
              const candidatesData = await candidatesRes.json();
              candidatesCount = candidatesData.total || 0;
            }
            
            // Fetch stipend candidates count (from active-employees endpoint) - only active employees
            const stipendRes = await fetch(`${API_URL}/api/spoc/active-employees?activity_year=all&company_id=${company.id}`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            let stipendCandidatesCount = 0;
            if (stipendRes.ok) {
              const stipendData = await stipendRes.json();
              if (stipendData.success) {
                stipendCandidatesCount = stipendData.activeCount ?? 0;
              }
            }
            
            // Fetch jobs count
            const jobsRes = await fetch(`${API_URL}/api/spoc/jobs?page=1&limit=1&company_id=${company.id}`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            let jobsCount = 0;
            if (jobsRes.ok) {
              const jobsData = await jobsRes.json();
              jobsCount = jobsData.total || 0;
            } else {
              // Log the error but don't fail the whole operation
              const errorData = await jobsRes.json().catch(() => ({}));
              console.error(`Error fetching jobs for company ${company.id}:`, errorData.message || 'Unknown error');
            }
            
            stats[company.id] = {
              candidatesCount,
              stipendCandidatesCount,
              jobsCount
            };
          } catch (err) {
            console.error(`Error fetching stats for company ${company.id}:`, err);
            stats[company.id] = { candidatesCount: 0, stipendCandidatesCount: 0, jobsCount: 0 };
          }
        }
        setCompanyStats(stats);
      } else {
        throw new Error('Failed to fetch companies');
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
      toast.error('Failed to load companies');
    } finally {
      setCompaniesLoading(false);
    }
  };

  const fetchCandidates = async () => {
    try {
      setCandidatesLoading(true);
      const { API_URL, token } = getApiConfig();
      const params = new URLSearchParams({
        page: candidatesPage,
        limit: 10,
        search: candidatesSearch
      });
      // Only add company_id if it's not empty
      if (selectedCompanyFilter) {
        params.append('company_id', selectedCompanyFilter);
      }

      const response = await fetch(`${API_URL}/api/spoc/candidates?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setCandidates(data.candidates || []);
        setCandidatesTotalPages(data.totalPages || 1);
        setCandidatesTotal(data.total || 0);
        setRegisteredCount(data.total || 0); // Update count
      } else {
        throw new Error('Failed to fetch candidates');
      }
    } catch (error) {
      console.error('Error fetching candidates:', error);
      toast.error('Failed to load candidates');
    } finally {
      setCandidatesLoading(false);
    }
  };

  const fetchUnregisteredCandidates = async () => {
    try {
      setUnregisteredLoading(true);
      const { API_URL, token } = getApiConfig();
      const params = new URLSearchParams({
        page: unregisteredPage,
        limit: 10,
        search: candidatesSearch
      });
      if (selectedCompanyFilter) {
        params.append('company_id', selectedCompanyFilter);
      }

      console.log('[UNREGISTERED] Fetching with params:', {
        page: unregisteredPage,
        search: candidatesSearch,
        company_id: selectedCompanyFilter
      });

      const response = await fetch(`${API_URL}/api/spoc/unregistered-candidates?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('[UNREGISTERED] API response:', {
          success: data.success,
          total: data.total,
          candidatesCount: data.candidates?.length || 0,
          totalPages: data.totalPages
        });
        if (data.success) {
          setUnregisteredCandidates(data.candidates || []);
          setUnregisteredTotalPages(data.totalPages || 0);
          setUnregisteredCount(data.total || 0); // Update count
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('[UNREGISTERED] API error:', errorData);
        throw new Error(errorData.message || 'Failed to fetch unregistered candidates');
      }
    } catch (error) {
      console.error('Error fetching unregistered candidates:', error);
      toast.error('Failed to load unregistered candidates');
      // Reset to empty state on error
      setUnregisteredCandidates([]);
      setUnregisteredTotalPages(0);
      setUnregisteredCount(0);
    } finally {
      setUnregisteredLoading(false);
    }
  };

  const fetchActiveEmployees = async () => {
    try {
      setEmployeesLoading(true);
      const { API_URL, token } = getApiConfig();
      
      // Determine which type to fetch based on current tab
      const type = candidatesSubTab === 'inactive' ? 'inactive' : 'active';
      const currentPage = candidatesSubTab === 'inactive' ? inactiveEmployeesPage : activeEmployeesPage;
      
      const params = new URLSearchParams({
        activity_year: employeesActivityYear,
        page: currentPage,
        limit: 10,
        type: type
      });
      if (selectedCompanyFilter) {
        params.append('company_id', selectedCompanyFilter);
      }

      console.log('[ACTIVE EMPLOYEES] Fetching with params:', {
        activity_year: employeesActivityYear,
        company_id: selectedCompanyFilter || 'none (all companies)',
        type,
        page: currentPage
      });

      const response = await fetch(`${API_URL}/api/spoc/active-employees?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('[ACTIVE EMPLOYEES] API response:', {
          success: data.success,
          activeCount: data.activeCount,
          inactiveCount: data.inactiveCount,
          totalActiveEmployees: data.activeEmployees?.length || 0,
          totalInactiveEmployees: data.inactiveEmployees?.length || 0,
          page: data.page,
          totalPages: data.totalPages,
          total: data.total
        });
        if (data.success) {
          setEmployeesListMeta({
            activityYear: data.activityYear !== undefined && data.activityYear !== null ? data.activityYear : 'all',
            refYear: data.classificationReferenceYear ?? null,
            refMonth: data.classificationReferenceMonth ?? null
          });
          if (type === 'active') {
            setActiveEmployees(data.activeEmployees || []);
            setActiveEmployeesTotalPages(data.totalPages || 1);
            setActiveEmployeesTotal(data.activeCount ?? data.total ?? 0);
          } else {
            setInactiveEmployees(data.inactiveEmployees || []);
            setInactiveEmployeesTotalPages(data.totalPages || 1);
            setInactiveEmployeesTotal(data.inactiveCount ?? data.total ?? 0);
          }
          // Update counts - these should reflect the current filter state
          setEmployeesCount(data.activeCount || 0);
          setInactiveCount(data.inactiveCount || 0);
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('[ACTIVE EMPLOYEES] API error:', errorData);
        throw new Error(errorData.message || 'Failed to fetch active employees');
      }
    } catch (error) {
      console.error('Error fetching active employees:', error);
      toast.error('Failed to load active employees');
      // Reset to empty state on error
      setActiveEmployees([]);
      setInactiveEmployees([]);
      setEmployeesCount(0);
      setInactiveCount(0);
    } finally {
      setEmployeesLoading(false);
    }
  };

  const downloadInactiveEmployeesCsv = async () => {
    try {
      setExportingInactiveCsv(true);
      const { API_URL, token } = getApiConfig();
      const params = new URLSearchParams({
        activity_year: employeesActivityYear,
        type: 'inactive',
        format: 'csv'
      });
      if (selectedCompanyFilter) {
        params.append('company_id', selectedCompanyFilter);
      }
      const response = await fetch(`${API_URL}/api/spoc/active-employees?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || 'Export failed');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const companyLabel = selectedCompanyFilter
        ? (companies.find((c) => String(c.id) === String(selectedCompanyFilter))?.company_name || 'company')
            .replace(/[^\w\s-]/g, '')
            .trim()
            .replace(/\s+/g, '_')
        : 'all_companies';
      a.download = `inactive_employees_${companyLabel}_${Date.now()}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Inactive list downloaded');
    } catch (error) {
      console.error('Inactive CSV export error:', error);
      toast.error(error.message || 'Failed to download CSV');
    } finally {
      setExportingInactiveCsv(false);
    }
  };

  // Fetch all candidate counts on initial load
  // IMPORTANT: This should ALWAYS fetch without company filter to get total counts
  const fetchAllCandidateCounts = async () => {
    try {
      const { API_URL, token } = getApiConfig();
      
      console.log('[FETCH ALL COUNTS] Fetching total counts (no company filter)');
      
      // Fetch all counts in parallel - NO company filter should be applied here
      const [registeredRes, unregisteredRes, employeesRes, supervisorRes] = await Promise.all([
        // Registered count
        fetch(`${API_URL}/api/spoc/candidates?page=1&limit=1`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        // Unregistered count
        fetch(`${API_URL}/api/spoc/unregistered-candidates?page=1&limit=1`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        // Employees count - NO company_id parameter to get total across all companies
        fetch(`${API_URL}/api/spoc/active-employees?activity_year=all`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        // Supervisor candidates count
        fetch(`${API_URL}/api/spoc/supervisor-candidates?page=1&limit=1`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      // Process registered count
      if (registeredRes.ok) {
        const registeredData = await registeredRes.json();
        setRegisteredCount(registeredData.total || 0);
        console.log('[FETCH ALL COUNTS] Registered count:', registeredData.total || 0);
      }

      // Process unregistered count
      if (unregisteredRes.ok) {
        const unregisteredData = await unregisteredRes.json();
        if (unregisteredData.success) {
          setUnregisteredCount(unregisteredData.total || 0);
          console.log('[FETCH ALL COUNTS] Unregistered count:', unregisteredData.total || 0);
        }
      }

      // Process employees count
      if (employeesRes.ok) {
        const employeesData = await employeesRes.json();
        if (employeesData.success) {
          console.log('[FETCH ALL COUNTS] Employees response:', {
            activeCount: employeesData.activeCount,
            inactiveCount: employeesData.inactiveCount,
            totalActive: employeesData.activeEmployees?.length || 0,
            totalInactive: employeesData.inactiveEmployees?.length || 0
          });
          setEmployeesCount(employeesData.activeCount || 0);
          setInactiveCount(employeesData.inactiveCount || 0);
        }
      }

      // Process supervisor candidates count
      if (supervisorRes.ok) {
        const supervisorData = await supervisorRes.json();
        if (supervisorData.success) {
          setSupervisorCandidatesTotal(supervisorData.total || 0);
          console.log('[FETCH ALL COUNTS] Supervisor candidates count:', supervisorData.total || 0);
        }
      }
    } catch (error) {
      console.error('Error fetching candidate counts:', error);
      // Don't show error toast for background count fetching
    }
  };

  const fetchSupervisorCandidates = async () => {
    try {
      setSupervisorCandidatesLoading(true);
      const { API_URL, token } = getApiConfig();
      const params = new URLSearchParams({
        page: supervisorCandidatesPage,
        limit: 10,
        search: supervisorCandidatesSearch
      });
      if (selectedSupervisorFilter) {
        params.append('supervisor_id', selectedSupervisorFilter);
      }

      const response = await fetch(`${API_URL}/api/spoc/supervisor-candidates?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setSupervisorCandidates(data.candidates || []);
          setSupervisorCandidatesTotalPages(data.totalPages || 0);
          setSupervisorCandidatesTotal(data.total || 0);
        }
      } else {
        throw new Error('Failed to fetch supervisor candidates');
      }
    } catch (error) {
      console.error('Error fetching supervisor candidates:', error);
      toast.error('Failed to load supervisor candidates');
    } finally {
      setSupervisorCandidatesLoading(false);
    }
  };

  // Handle edit supervisor candidate
  const handleEditSupervisorCandidate = async (candidate) => {
    try {
      const { API_URL, token } = getApiConfig();
      const response = await fetch(`${API_URL}/api/spoc/supervisor-candidates/${candidate.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      
      if (data.success) {
        const candidateData = data.candidate;
        setEditingSupervisorCandidate(candidateData);
        setSupervisorCandidateEditForm({
          establishment_name: candidateData.establishment_name || '',
          month: candidateData.month || '',
          punching_code: candidateData.punching_code || '',
          employee_code: candidateData.employee_code || '',
          remark: candidateData.remark || '',
          contract_code: candidateData.contract_code || '',
          apprentice_code: candidateData.apprentice_code || '',
          aadhar_no: candidateData.aadhar_no || '',
          date_of_birth: candidateData.date_of_birth || '',
          gender: candidateData.gender || '',
          father_name: candidateData.father_name || '',
          mother_name: candidateData.mother_name || '',
          category: candidateData.category || '',
          address: candidateData.address || '',
          district: candidateData.district || '',
          state: candidateData.state || '',
          pincode: candidateData.pincode || '',
          qualification: candidateData.qualification || '',
          institute_name: candidateData.institute_name || '',
          specialization: candidateData.specialization || '',
          bank_name: candidateData.bank_name || '',
          account_no: candidateData.account_no || '',
          ifsc_code: candidateData.ifsc_code || '',
          seeded_account: candidateData.seeded_account || '',
          beni_id_status: candidateData.beni_id_status || '',
          date_of_joining: candidateData.date_of_joining || '',
          stipend: candidateData.stipend || '',
          job_role: candidateData.job_role || '',
          establishment_address: candidateData.establishment_address || '',
          insurance: candidateData.insurance || '',
          id_card: candidateData.id_card || '',
          location: candidateData.location || '',
          status: candidateData.status || '',
          referral_code: candidateData.referral_code || ''
        });
        setShowSupervisorCandidateEditModal(true);
      } else {
        toast.error(data.message || 'Failed to fetch candidate details');
      }
    } catch (error) {
      console.error('Error fetching supervisor candidate details:', error);
      toast.error('Failed to fetch candidate details');
    }
  };

  // Handle update supervisor candidate
  const handleUpdateSupervisorCandidate = async (e) => {
    e.preventDefault();
    if (!editingSupervisorCandidate) return;

    try {
      const { API_URL, token } = getApiConfig();
      const response = await fetch(`${API_URL}/api/spoc/supervisor-candidates/${editingSupervisorCandidate.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(supervisorCandidateEditForm)
      });
      const data = await response.json();
      
      if (data.success) {
        toast.success('Candidate updated successfully');
        setShowSupervisorCandidateEditModal(false);
        setEditingSupervisorCandidate(null);
        fetchSupervisorCandidates(); // Refresh the list
      } else {
        toast.error(data.message || 'Failed to update candidate');
      }
    } catch (error) {
      console.error('Error updating supervisor candidate:', error);
      toast.error('Failed to update candidate');
    }
  };

  // Supervisor candidates CSV upload - same as supervisor login
  const handleCandidatesCsvUpload = async (e) => {
    e.preventDefault();
    if (!candidatesUploadFile) {
      toast.error('Please select a file');
      return;
    }
    setCandidatesUploading(true);
    setCandidatesUploadResult(null);
    const formData = new FormData();
    formData.append('file', candidatesUploadFile);
    if (selectedSupervisorFilter) {
      formData.append('supervisor_id', selectedSupervisorFilter);
    }
    try {
      const { API_URL, token } = getApiConfig();
      const response = await fetch(`${API_URL}/api/spoc/candidates/bulk-upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      const data = await response.json();
      setCandidatesUploadResult(data);
      if (data.success) {
        toast.success(data.message);
        setShowCandidatesUploadModal(false);
        setCandidatesUploadFile(null);
        setCandidatesUploadResult(null);
        fetchSupervisorCandidates();
        if (data.pendingAccountCreation > 0) {
          toast(`Imported ${data.pendingAccountCreation} candidates. Accounts can be created by supervisors in Pending Accounts.`, { duration: 5000, icon: 'ℹ️' });
        }
      } else {
        toast.error(data.message || 'Upload failed');
      }
    } catch (error) {
      console.error('Candidates upload error:', error);
      toast.error('Upload failed');
      setCandidatesUploadResult({ success: false, message: error.message });
    } finally {
      setCandidatesUploading(false);
    }
  };

  const handleCandidatesFileChange = (e) => {
    const file = e.target.files[0];
    if (file && (file.type === 'text/csv' || file.name.endsWith('.csv'))) {
      setCandidatesUploadFile(file);
    } else {
      toast.error('Please select a valid CSV file');
    }
  };

  const downloadCandidatesTemplate = () => {
    const headers = ['Name', 'Email', 'Mobile', 'Referral Code', 'Qualification', 'Specialization', 'Institute Name'];
    const sampleData = ['John Doe', 'john.doe@example.com', '9876543330', 'REF001', 'Bachelor of Technology', 'Computer Science', 'Indian Institute of Technology'];
    const csvContent = [headers.join(','), sampleData.join(',')].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'candidate_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    toast.success('Template downloaded');
  };

  // Handle supervisor candidate document management
  const fetchSupervisorCandidateDocuments = async (candidateId) => {
    try {
      const { API_URL, token } = getApiConfig();
      const response = await fetch(`${API_URL}/api/spoc/supervisor-candidates/${candidateId}/documents`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setSupervisorCandidateDocuments(data.documents);
      } else {
        toast.error(data.message || 'Failed to fetch documents');
      }
    } catch (error) {
      console.error('Error fetching supervisor candidate documents:', error);
      toast.error('Failed to fetch documents');
    }
  };

  const handleManageSupervisorCandidateDocuments = (candidate) => {
    setSelectedSupervisorCandidate(candidate);
    fetchSupervisorCandidateDocuments(candidate.id);
    setShowSupervisorCandidateDocumentModal(true);
  };

  const handleDeleteSupervisorCandidateDocument = async (docId) => {
    if (!selectedSupervisorCandidate) return;
    
    if (!window.confirm('Are you sure you want to delete this document?')) {
      return;
    }

    try {
      const { API_URL, token } = getApiConfig();
      const response = await fetch(`${API_URL}/api/spoc/supervisor-candidates/${selectedSupervisorCandidate.id}/documents/${docId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      
      if (data.success) {
        toast.success('Document deleted successfully');
        fetchSupervisorCandidateDocuments(selectedSupervisorCandidate.id); // Refresh documents
      } else {
        toast.error(data.message || 'Failed to delete document');
      }
    } catch (error) {
      console.error('Error deleting supervisor candidate document:', error);
      toast.error('Failed to delete document');
    }
  };

  const handleDownloadSupervisorCandidateDocument = async (docId) => {
    if (!selectedSupervisorCandidate) return;

    try {
      const { API_URL, token } = getApiConfig();
      const response = await fetch(`${API_URL}/api/spoc/supervisor-candidates/${selectedSupervisorCandidate.id}/documents/${docId}/download`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const contentDisposition = response.headers.get('Content-Disposition');
        const fileName = contentDisposition 
          ? contentDisposition.split('filename=')[1]?.replace(/"/g, '') 
          : `document_${docId}`;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success('Document downloaded successfully');
      } else {
        const data = await response.json();
        toast.error(data.message || 'Failed to download document');
      }
    } catch (error) {
      console.error('Error downloading supervisor candidate document:', error);
      toast.error('Failed to download document');
    }
  };

  const fetchStipendUploadStatus = async () => {
    try {
      setStipendUploadLoading(true);
      const { API_URL, token } = getApiConfig();
      const response = await fetch(`${API_URL}/api/spoc/stipend-upload-status?year=${stipendUploadYear}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setStipendUploadStatus(data.companies || []);
        }
      } else {
        throw new Error('Failed to fetch stipend upload status');
      }
    } catch (error) {
      console.error('Error fetching stipend upload status:', error);
      toast.error('Failed to load stipend upload status');
    } finally {
      setStipendUploadLoading(false);
    }
  };

  const fetchSupervisors = async () => {
    try {
      setSupervisorsLoading(true);
      const { API_URL, token } = getApiConfig();
      const params = new URLSearchParams({
        page: String(supervisorListPage),
        limit: String(SUPERVISOR_LIST_PAGE_SIZE),
        search: supervisorListSearch || ''
      });
      const response = await fetch(`${API_URL}/api/spoc/supervisors?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSupervisors(data.supervisors || []);
        setSupervisorListTotal(data.total ?? 0);
        setSupervisorListTotalPages(data.totalPages ?? 1);
      } else {
        throw new Error('Failed to fetch supervisors');
      }
    } catch (error) {
      console.error('Error fetching supervisors:', error);
      toast.error('Failed to load supervisors');
    } finally {
      setSupervisorsLoading(false);
    }
  };

  const fetchSupervisorsForDropdown = async () => {
    try {
      const { API_URL, token } = getApiConfig();
      const response = await fetch(`${API_URL}/api/spoc/supervisors?page=1&limit=10000&search=`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSupervisorsForDropdown(data.supervisors || []);
      }
    } catch (error) {
      console.error('Error fetching supervisors for dropdown:', error);
    }
  };

  const handleAddSupervisor = async (e) => {
    e.preventDefault();
    if (!addSupervisorForm.name?.trim() || !addSupervisorForm.email?.trim() || !addSupervisorForm.mobile?.trim() || !addSupervisorForm.password) {
      toast.error('Please fill Name, Email, Mobile, and Password');
      return;
    }
    setAddSupervisorSubmitting(true);
    try {
      const { API_URL, token } = getApiConfig();
      const res = await fetch(`${API_URL}/api/spoc/supervisors`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          name: addSupervisorForm.name.trim(),
          email: addSupervisorForm.email.trim(),
          mobile: addSupervisorForm.mobile.trim(),
          password: addSupervisorForm.password,
          company_id: addSupervisorForm.company_id || undefined
        })
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.message || 'Failed to add supervisor');
        return;
      }
      toast.success('Supervisor added successfully');
      setShowAddSupervisorModal(false);
      setAddSupervisorForm({ name: '', email: '', mobile: '', password: '', company_id: '' });
      fetchSupervisors();
    } catch (err) {
      console.error(err);
      toast.error('Failed to add supervisor');
    } finally {
      setAddSupervisorSubmitting(false);
    }
  };

  // Fetch supervisor attendance
  const fetchSupervisorAttendance = useCallback(async () => {
    try {
      setSupervisorAttendanceLoading(true);
      setAttendanceError(null);
      const { API_URL, token } = getApiConfig();
      const params = new URLSearchParams({
        month: attendanceMonth,
        year: attendanceYear,
        page: attendancePage,
        limit: 10
      });
      if (selectedSupervisorForAttendance) {
        params.append('supervisor_id', selectedSupervisorForAttendance);
      }

      const response = await fetch(`${API_URL}/api/spoc/supervisor-attendance?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setSupervisorAttendance(data.attendance || []);
          // Update supervisors dropdown list if provided in response
          if (data.supervisors && Array.isArray(data.supervisors)) {
            setSupervisorsForDropdown(data.supervisors);
          }
          // Set pagination info if available
          if (data.total !== undefined) {
            setAttendanceTotal(data.total);
            const totalPages = Math.ceil(data.total / 10);
            setAttendanceTotalPages(totalPages || 1);
          } else if (data.totalPages !== undefined) {
            setAttendanceTotalPages(data.totalPages || 1);
            // Estimate total from pages
            setAttendanceTotal(data.totalPages * 10);
          } else {
            // If no pagination info, check if we have more records than limit
            const recordsCount = (data.attendance || []).length;
            setAttendanceTotal(recordsCount);
            if (recordsCount >= 10) {
              // Likely more pages available, set to at least 2
              setAttendanceTotalPages(2);
            } else {
              setAttendanceTotalPages(1);
            }
          }
          setAttendanceError(null); // Clear any previous errors
        } else {
          // API returned success: false but no error - likely no data
          setSupervisorAttendance([]);
          setAttendanceTotalPages(1);
          setAttendanceTotal(0);
          setAttendanceError(null); // No error, just no data
        }
      } else {
        // Handle HTTP errors properly
        try {
          const errorData = await response.json();
          if (response.status === 404 || response.status === 400) {
            // No data found - not really an error, just empty result
            setSupervisorAttendance([]);
            setAttendanceTotalPages(1);
            setAttendanceTotal(0);
            setAttendanceError(null);
          } else {
            throw new Error(errorData.message || 'Failed to fetch supervisor attendance');
          }
        } catch (parseError) {
          // If response is not JSON, treat as network/server error
          if (response.status === 404 || response.status === 400) {
            setSupervisorAttendance([]);
            setAttendanceTotalPages(1);
            setAttendanceTotal(0);
            setAttendanceError(null);
          } else {
            throw new Error('Failed to fetch supervisor attendance');
          }
        }
      }
    } catch (error) {
      console.error('Error fetching supervisor attendance:', error);
      setAttendanceError(error.message);
      setSupervisorAttendance([]);
      setAttendanceTotalPages(1);
      setAttendanceTotal(0);
      // Don't show toast for errors - handle silently
    } finally {
      setSupervisorAttendanceLoading(false);
    }
  }, [attendanceMonth, attendanceYear, selectedSupervisorForAttendance, attendancePage]);

  // Export supervisor attendance as CSV
  const exportSupervisorAttendance = async () => {
    try {
      const { API_URL, token } = getApiConfig();
      const params = new URLSearchParams({
        month: attendanceMonth,
        year: attendanceYear,
        format: 'csv'
      });
      if (selectedSupervisorForAttendance) {
        params.append('supervisor_id', selectedSupervisorForAttendance);
      }

      const response = await fetch(`${API_URL}/api/spoc/supervisor-attendance/export?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const filename = `supervisor-attendance-${attendanceYear}-${String(attendanceMonth).padStart(2, '0')}${selectedSupervisorForAttendance ? `-supervisor-${selectedSupervisorForAttendance}` : ''}.csv`;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success('Attendance exported successfully');
      } else {
        throw new Error('Failed to export attendance');
      }
    } catch (error) {
      console.error('Error exporting supervisor attendance:', error);
      toast.error('Failed to export attendance');
    }
  };

  // Ensure supervisors dropdown is loaded when supervisor-attendance tab is active
  useEffect(() => {
    if (activeTab === 'supervisor-attendance' && supervisorsForDropdown.length === 0) {
      fetchSupervisorsForDropdown();
    }
  }, [activeTab, supervisorsForDropdown.length]);

  // Fetch attendance when tab is active or filters change
  useEffect(() => {
    if (activeTab === 'supervisor-attendance') {
      setAttendancePage(1); // Reset to first page when filters change
    }
  }, [activeTab, attendanceMonth, attendanceYear, selectedSupervisorForAttendance]);

  // Fetch attendance when tab, filters, or page changes
  useEffect(() => {
    if (activeTab === 'supervisor-attendance') {
      fetchSupervisorAttendance();
    }
  }, [activeTab, fetchSupervisorAttendance]);

  const fetchJobs = async () => {
    try {
      setJobsLoading(true);
      const { API_URL, token } = getApiConfig();
      const params = new URLSearchParams({
        page: jobsPage,
        limit: 10,
        search: jobsSearch
      });
      // Only add company_id if it's not empty
      if (selectedJobCompanyFilter) {
        params.append('company_id', selectedJobCompanyFilter);
      }

      const response = await fetch(`${API_URL}/api/spoc/jobs?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setJobs(data.jobs);
        setJobsTotalPages(data.totalPages);
        
        // Fetch applicant counts for each job
        const counts = {};
        for (const job of data.jobs) {
          try {
            const applicantsRes = await fetch(`${API_URL}/api/spoc/jobs/${job.id}/applicants`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            if (applicantsRes.ok) {
              const applicantsData = await applicantsRes.json();
              counts[job.id] = applicantsData.applicants?.length || 0;
            }
          } catch (err) {
            counts[job.id] = 0;
          }
        }
        setJobApplicantCounts(counts);
      } else {
        throw new Error('Failed to fetch jobs');
      }
    } catch (error) {
      console.error('Error fetching jobs:', error);
      toast.error('Failed to load jobs');
    } finally {
      setJobsLoading(false);
    }
  };

  const handleViewApplicants = async (job) => {
    setSelectedJob(job);
    setShowApplicantsModal(true);
    setApplicantsLoading(true);
    try {
      const { API_URL, token } = getApiConfig();
      const res = await fetch(`${API_URL}/api/spoc/jobs/${job.id}/applicants`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!res.ok) {
        throw new Error(`Failed to fetch applicants: ${res.status}`);
      }
      
      const data = await res.json();
      if (data.success) {
        const applicantsList = data.applicants || [];
        setApplicants(applicantsList);
        // Update the applicant count for this job
        setJobApplicantCounts(prev => ({
          ...prev,
          [job.id]: applicantsList.length
        }));
      } else {
        setApplicants([]);
      }
    } catch (err) {
      console.error('Error fetching applicants:', err);
      toast.error('Failed to load applicants');
      setApplicants([]);
    } finally {
      setApplicantsLoading(false);
    }
  };

  const handleCopyJobShareLink = async (job) => {
    try {
      const shareUrl = `${window.location.origin}/jobs/${job.id}/share`;
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Job sharing link copied');
    } catch (err) {
      console.error('Copy share link error:', err);
      toast.error('Failed to copy sharing link');
    }
  };

  const handleSpocJobVisibility = async (job, hidden) => {
    try {
      const { API_URL, token } = getApiConfig();
      const res = await fetch(`${API_URL}/api/spoc/jobs/${job.id}/visibility`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ hidden })
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Failed to update visibility');
      toast.success(hidden ? 'Job hidden' : 'Job unhidden');
      fetchJobs();
    } catch (err) {
      console.error('Error updating SPOC job visibility:', err);
      toast.error('Failed to update job visibility');
    }
  };

  const handleApplicantStatusChange = async (appId, status) => {
    try {
      const { API_URL, token } = getApiConfig();
      const res = await fetch(`${API_URL}/api/spoc/applications/${appId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ status })
      });
      const data = await res.json();
      if (data.success) {
        setApplicants(prev => prev.map(a => a.id === appId ? { ...a, status } : a));
        toast.success('Status updated');
      } else {
        toast.error(data.error || data.message || 'Failed to update status');
      }
    } catch (err) {
      console.error('Error updating applicant status:', err);
      toast.error('Failed to update status');
    }
  };

  // Job posting handlers
  const handleJobFormChange = (e) => {
    const { name, value } = e.target;
    setJobForm(prev => ({ ...prev, [name]: value }));
  };

  const handleQualificationChange = (qual) => {
    setJobForm(prev => ({
      ...prev,
      qualification: prev.qualification.includes(qual)
        ? prev.qualification.filter(q => q !== qual)
        : [...prev.qualification, qual]
    }));
  };

  const handleJobSubmit = async (e) => {
    e.preventDefault();
    setJobFormError('');
    setJobFormSuccess('');
    
    // Validation
    if (!jobForm.title || !jobForm.description || !jobForm.age_criteria || 
        !jobForm.stipend_min || !jobForm.stipend_max || !jobForm.location || 
        !jobForm.company_id || jobForm.qualification.length === 0) {
      setJobFormError('All fields are required');
      return;
    }

    try {
      setSubmittingJob(true);
      const { API_URL, token } = getApiConfig();

      const formData = new FormData();
      formData.append('title', jobForm.title);
      formData.append('description', jobForm.description);
      formData.append('age_criteria', jobForm.age_criteria);
      formData.append('stipend_min', String(parseInt(jobForm.stipend_min, 10)));
      formData.append('stipend_max', String(parseInt(jobForm.stipend_max, 10)));
      formData.append('qualification', jobForm.qualification.join(','));
      formData.append('location', jobForm.location);
      formData.append('company_id', String(jobForm.company_id));
      if (jobLogoFile) {
        formData.append('logo', jobLogoFile);
      }

      const response = await fetch(`${API_URL}/api/spoc/jobs`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || data.message || 'Failed to post job');
      }

      if (data.success) {
        setJobFormSuccess('Job posted successfully!');
        // Reset form
        setJobForm({
          title: '',
          description: '',
          age_criteria: '',
          stipend_min: '',
          stipend_max: '',
          qualification: [],
          location: '',
          company_id: ''
        });
        setJobLogoFile(null);
        if (jobLogoInputRef.current) jobLogoInputRef.current.value = '';
        // Close form after a short delay
        setTimeout(() => {
          setShowJobForm(false);
          setJobFormSuccess('');
          fetchJobs(); // Refresh jobs list
        }, 1500);
      } else {
        throw new Error(data.message || 'Failed to post job');
      }
    } catch (error) {
      console.error('Error posting job:', error);
      setJobFormError(error.message || 'Failed to post job. Please try again.');
    } finally {
      setSubmittingJob(false);
    }
  };

  const handleLogout = async () => {
    try {
      localStorage.removeItem('gogig_spoc_access');
      localStorage.removeItem('gogig_spoc_refresh');
      localStorage.removeItem('gogig_spoc_data');
      if (onLogout) onLogout();
    } catch (error) {
      console.error('Logout error:', error);
      if (onLogout) onLogout();
    }
  };

  // Document Management Functions
  const fetchCandidateDocuments = async (candidateId) => {
    try {
      const { API_URL, token } = getApiConfig();
      const response = await fetch(`${API_URL}/api/spoc/candidates/${candidateId}/documents`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const documents = await response.json();
        setCandidateDocuments(documents);
      } else {
        throw new Error('Failed to fetch documents');
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
      toast.error('Failed to load documents');
    }
  };

  const handleManageDocuments = (candidate) => {
    setSelectedCandidate(candidate);
    setShowDocumentModal(true);
    fetchCandidateDocuments(candidate.id);
  };

  const handleDocumentUpload = async (e) => {
    e.preventDefault();
    
    if (!documentForm.document_type || !documentForm.file) {
      toast.error('Please select document type and file');
      return;
    }

    try {
      setUploadingDocument(true);
      const { API_URL, token } = getApiConfig();
      const formData = new FormData();
      formData.append('document_type', documentForm.document_type);
      formData.append('file', documentForm.file);

      const response = await fetch(`${API_URL}/api/spoc/candidates/${selectedCandidate.id}/documents`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        toast.success('Document uploaded successfully');
        setDocumentForm({ document_type: '', file: null });
        fetchCandidateDocuments(selectedCandidate.id);
      } else {
        const error = await response.json();
        throw new Error(error.message || 'Failed to upload document');
      }
    } catch (error) {
      console.error('Error uploading document:', error);
      toast.error(error.message || 'Failed to upload document');
    } finally {
      setUploadingDocument(false);
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) {
      return;
    }

    try {
      const { API_URL, token } = getApiConfig();
      const response = await fetch(`${API_URL}/api/spoc/candidates/${selectedCandidate.id}/documents/${docId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        toast.success('Document deleted successfully');
        fetchCandidateDocuments(selectedCandidate.id);
      } else {
        throw new Error('Failed to delete document');
      }
    } catch (error) {
      console.error('Error deleting document:', error);
      toast.error('Failed to delete document');
    }
  };

  // CSV Upload functionality (similar to admin)
  const handleCsvUpload = async (e) => {
    e.preventDefault();
    setUploading(true);
    setUploadResult(null);

    const form = e.target;
    const fileInput = form.querySelector('input[type="file"]');
    const file = fileInput.files[0];
    
    if (!file) {
      setUploadResult({ success: false, message: 'Please select a file' });
      setUploading(false);
      return;
    }

    const formData = new FormData();
    const docType = selectedDocumentType;
    const year = stipendReportYear;
    const month = stipendReportMonth;

    // Validate company selection
    if (!selectedCompanyId) {
      setUploadResult({ success: false, message: 'Please select a company' });
      setUploading(false);
      return;
    }

    const selectedCompany = selectedCompanyId
      ? companies.find(c => c.id === parseInt(selectedCompanyId, 10))
      : null;

    // NAPS stipend/DBT + contracts: use real code from companies table (not EST000029 dropdown IDs)
    let establishmentCodeToUse = selectedEstablishmentCode;
    if (
      docImportTab === 'naps' &&
      (docType === 'contract' || docType === 'stipend' || docType === 'dbt') &&
      selectedCompany?.establishment_code
    ) {
      establishmentCodeToUse = selectedCompany.establishment_code;
    }

    // Validate establishment code for stipend, DBT, BVOC, and NATS documents
    if (
      ((docImportTab === 'naps' && (docType === 'stipend' || docType === 'dbt')) ||
        docImportTab === 'bvoc' ||
        (docImportTab === 'nats' && docType === 'nats_stipend')) &&
      !establishmentCodeToUse
    ) {
      setUploadResult({
        success: false,
        message: docImportTab === 'naps'
          ? 'Selected company does not have an establishment code'
          : 'Please select an establishment ID for stipend/DBT/BVOC/NATS documents',
      });
      setUploading(false);
      return;
    }

    formData.append('file', file);
    formData.append('document_type', docType);

    if (establishmentCodeToUse) {
      formData.append('establishment_code', establishmentCodeToUse);
    }
    
    // For contracts: always send company_id (even without year/month)
    if (docImportTab === 'naps' && docType === 'contract' && selectedCompanyId) {
      formData.append('company_id', selectedCompanyId);
      if (selectedCompany?.company_name) {
        formData.append('company_name', selectedCompany.company_name);
      }
    }

    // Add year, month, and company_id if they exist and are for NAPS/NATS documents
    // Note: Contracts don't need year/month, but we still send company_id for validation
    if ((docImportTab === 'naps' || docImportTab === 'nats') && year && month && docType !== 'contract') {
      formData.append('year', year);
      formData.append('month', month);
      if (selectedCompanyId) {
        formData.append('company_id', selectedCompanyId);
      }
      // Keep company_name for backward compatibility
      if (companyNameInput) {
      formData.append('company_name', companyNameInput);
      }
    }
    
    // For BVOC and Salaried documents, also add company_id and company_name if available
    if ((docImportTab === 'bvoc' || docImportTab === 'salaried') && selectedCompanyId) {
      formData.append('company_id', selectedCompanyId);
      if (year && month) {
        formData.append('year', year);
        formData.append('month', month);
      }
      if (companyNameInput) {
        formData.append('company_name', companyNameInput);
      }
    }

    try {
      const { API_URL, token } = getApiConfig();
      
      // Get SPOC info to include in upload
      const spocData = localStorage.getItem('gogig_spoc_data');
      let spocId = null;
      if (spocData) {
        try {
          const parsed = JSON.parse(spocData);
          spocId = parsed.id;
        } catch (e) {
          console.error('Error parsing SPOC data:', e);
        }
      }
      
      // Add SPOC ID to form data if available
      if (spocId) {
        formData.append('spoc_id', spocId);
      }
      
      let uploadUrl = `${API_URL}/api/documents/upload`;
      if (docType === 'contract') {
        uploadUrl = `${API_URL}/api/documents/upload/contract`;
      } else if (docType === 'stipend') {
        uploadUrl = `${API_URL}/api/documents/upload/stipend`;
      } else if (docType === 'bvoc_salary') {
        uploadUrl = `${API_URL}/api/documents/upload/bvoc-salary`;
      } else if (docType === 'salary') {
        uploadUrl = `${API_URL}/api/documents/upload/salaried-salary`;
      } else if (docType === 'nats_stipend') {
        uploadUrl = `${API_URL}/api/documents/upload/nats-stipend`;
      }
      const res = await fetch(uploadUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      const data = await res.json();
      
      if (res.ok) {
        setUploadResult({ success: true, imported: data.imported || data.totalRecords });
        // Save to local history
        const newHistory = [
          { 
            fileName: file.name, 
            type: docType, 
            date: new Date().toLocaleString(), 
            imported: data.imported || data.totalRecords
          },
          ...csvHistory
        ].slice(0, 10);
        setCsvHistory(newHistory);
        localStorage.setItem('spoc_csv_history', JSON.stringify(newHistory));
      } else {
        setUploadResult({ success: false, message: data.message });
      }
    } catch (err) {
      setUploadResult({ success: false, message: err.message });
    } finally {
      setUploading(false);
    }
  };

  // Load CSV history on component mount
  useEffect(() => {
    const savedHistory = localStorage.getItem('spoc_csv_history');
    if (savedHistory) {
      setCsvHistory(JSON.parse(savedHistory));
    }
  }, []);

  // Function to fetch NAPS establishment IDs
  const fetchNapsEstablishmentIds = async () => {
    try {
      const { API_URL, token } = getApiConfig();
      const response = await fetch(`${API_URL}/api/v1/establishment/establishment-ids`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      
      if (data.success) {
        // Filter for NAPS companies only
        const napsCompanies = data.data.filter(company => 
          company.companyType === 'naps' || company.companyType === 'NAPS'
        );
        setNapsEstablishmentIds(napsCompanies);
      }
    } catch (error) {
      console.error('Error fetching NAPS establishment IDs:', error);
    }
  };

  // Function to fetch BVOC establishment IDs
  const fetchBvocEstablishmentIds = async () => {
    try {
      const { API_URL, token } = getApiConfig();
      const response = await fetch(`${API_URL}/api/v1/establishment/establishment-ids`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      
      if (data.success) {
        // Filter for BVOC companies only
        const bvocCompanies = data.data.filter(company => 
          company.companyType === 'bvoc' || company.companyType === 'BVOC'
        );
        setBvocEstablishmentIds(bvocCompanies);
      }
    } catch (error) {
      console.error('Error fetching BVOC establishment IDs:', error);
    }
  };

  // Function to fetch NATS establishment IDs
  const fetchNatsEstablishmentIds = async () => {
    try {
      const { API_URL, token } = getApiConfig();
      const response = await fetch(`${API_URL}/api/v1/establishment/establishment-ids`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      
      if (data.success) {
        const natsCompanies = data.data.filter(company => 
          company.companyType === 'nats' || company.companyType === 'NATS'
        );
        setNatsEstablishmentIds(natsCompanies);
      }
    } catch (error) {
      console.error('Error fetching NATS establishment IDs:', error);
    }
  };

  const handleDownloadDocument = async (docId, fileName) => {
    try {
      const { API_URL, token } = getApiConfig();
      const response = await fetch(`${API_URL}/api/spoc/candidates/${selectedCandidate.id}/documents/${docId}/download`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success('Document downloaded successfully');
      } else {
        throw new Error('Failed to download document');
      }
    } catch (error) {
      console.error('Error downloading document:', error);
      toast.error('Failed to download document');
    }
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          facingMode: 'environment' // Use back camera on mobile
        } 
      });
      setCameraStream(stream);
      setShowCameraModal(true);
    } catch (error) {
      console.error('Error accessing camera:', error);
      toast.error('Failed to access camera. Please check permissions.');
    }
  };

  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    setShowCameraModal(false);
  };

  const capturePhoto = () => {
    const video = document.getElementById('camera-video');
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    context.drawImage(video, 0, 0);
    
    canvas.toBlob((blob) => {
      const file = new File([blob], `camera-capture-${Date.now()}.jpg`, { type: 'image/jpeg' });
      setDocumentForm({ ...documentForm, file });
      stopCamera();
      toast.success('Photo captured successfully');
    }, 'image/jpeg', 0.8);
  };

  if (loading) {
    console.log('SPOCDashboard showing loading screen');
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        background: '#f8fafc'
      }}>
        <div style={{
          width: 40,
          height: 40,
          border: '4px solid #e5e7eb',
          borderTop: '4px solid #667eea',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
        <div style={{ marginLeft: 20, color: '#666' }}>
          Loading SPOC Dashboard...
        </div>
      </div>
    );
  }

  console.log('SPOCDashboard rendering main content, user:', user);

  const spocNavTabs = [
    { id: 'companies', label: 'Companies', icon: FaBuilding },
    { id: 'candidates', label: 'Candidates', icon: FaUsers },
    { id: 'jobs', label: 'Jobs', icon: FaBriefcase },
    { id: 'supervisors', label: 'Supervisors', icon: FaUsers },
    { id: 'supervisor-attendance', label: 'Supervisor Attendance', icon: FaClock },
    { id: 'documents', label: 'Documents', icon: FaFileAlt },
    { id: 'stipend-monitoring', label: 'Stipend Upload Monitoring', icon: FaFileAlt },
    { id: 'rewards', label: 'Rewards', icon: FaGift },
    { id: 'gamingReport', label: 'Gaming Report', icon: FaChartLine },
    { id: 'referralReport', label: 'Referral Report', icon: FaFileAlt },
    { id: 'referredCandidatesStipendReport', label: 'Referred Candidates Stipend', icon: FaChartLine },
    { id: 'spocReport', label: 'SPOC Summary', icon: FaChartLine },
    { id: 'spocUploadStatus', label: 'SPOC Upload Status', icon: FaFileAlt },
    { id: 'workDiary', label: 'Work Diary', icon: FaFileAlt },
    { id: 'salaryAdvance', label: 'Salary Advance', icon: FaMoneyCheckAlt },
    { id: 'higherEducation', label: 'Higher Education', icon: FaGraduationCap },
    { id: 'retentionReport', label: 'Retention Report', icon: FaHistory },
    { id: 'assessmentDueReport', label: 'Assessment Due Report', icon: FaHistory },
    { id: 'companyReports', label: 'Company Reports', icon: FaChartLine },
    { id: 'attritionReport', label: 'Attrition Report', icon: FaHistory }
  ];

  return (
    <div className="admin-layout">
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#fff',
            color: '#0f172a',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08), 0 0 0 1px rgba(0,0,0,0.04)',
            borderRadius: '12px',
            padding: '16px 20px',
          },
          success: { style: { background: '#f0fdfa', color: '#0f766e' } },
          error: { style: { background: '#fef2f2', color: '#991b1b' } },
        }}
      />
      <aside className="admin-sidebar">
        <div className="admin-sidebar-brand">
          <img src="/logo512.png" alt="GoGig Logo" className="admin-sidebar-brand-logo" />
          <span className="admin-sidebar-brand-title">GoGig SPOC</span>
          <span className="admin-sidebar-brand-badge">{user?.name || 'Dashboard'}</span>
        </div>
        <nav className="admin-sidebar-nav">
          {spocNavTabs.map(tabBtn => (
            <button
              key={tabBtn.id}
              type="button"
              className={`admin-sidebar-nav-btn ${activeTab === tabBtn.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tabBtn.id)}
            >
              <span className="nav-icon">{tabBtn.icon}</span>
              <span>{tabBtn.label}</span>
            </button>
          ))}
        </nav>
        <button type="button" className="admin-sidebar-logout" onClick={handleLogout}>
          <FaSignOutAlt style={{ marginRight: 8 }} />
          Logout
        </button>
      </aside>
      <div className="admin-main">
        {/* Stats strip for list tabs */}
        {(activeTab === 'companies' || activeTab === 'candidates' || activeTab === 'jobs') && (
          <div className="admin-stats-grid">
            <div className="admin-stat-card">
              <div className="admin-stat-card-icon primary">
                <FaBuilding color="#fff" size={26} />
              </div>
              <div>
                <div className="admin-stat-card-label">Companies</div>
                <div className="admin-stat-card-value">{companies.length}</div>
              </div>
            </div>
            <div className="admin-stat-card">
              <div className="admin-stat-card-icon primary">
                <FaUsers color="#fff" size={26} />
              </div>
              <div>
                <div className="admin-stat-card-label">Candidates</div>
                <div className="admin-stat-card-value">{registeredCount + (employeesCount || 0) + (unregisteredCount || 0)}</div>
              </div>
            </div>
            <div className="admin-stat-card">
              <div className="admin-stat-card-icon amber">
                <FaBriefcase color="#fff" size={26} />
              </div>
              <div>
                <div className="admin-stat-card-label">Job Posts</div>
                <div className="admin-stat-card-value">{Object.values(companyStats).reduce((s, st) => s + (st.jobsCount || 0), 0)}</div>
              </div>
            </div>
          </div>
        )}
        {/* Content Area */}
        <div style={{ flex: 1, padding: (activeTab === 'stipend-monitoring' || activeTab === 'spocReport') ? 0 : undefined, overflow: 'auto', minHeight: 0 }}>
          {activeTab === 'companies' && (
            <div>
              <h3 style={{ fontSize: 18, fontWeight: 600, color: '#374151', marginBottom: 24 }}>
                Companies Under Your Management
              </h3>
              
              {/* Summary Cards */}
              {!companiesLoading && companies.length > 0 && (
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', 
                  gap: 16, 
                  marginBottom: 24 
                }}>
                  {/* Total Companies Card */}
                  <div style={{
                    background: '#fff',
                    border: '1px solid #e5e7eb',
                    borderRadius: 12,
                    padding: 20,
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                      <div style={{
                        width: 48,
                        height: 48,
                        borderRadius: 12,
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 24
                      }}>
                        <FaBuilding style={{ color: '#fff' }} />
                      </div>
                      <div>
                        <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 500 }}>
                          Total Companies
                        </div>
                        <div style={{ fontSize: 32, fontWeight: 700, color: '#374151' }}>
                          {companies.length}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Total Stipend Candidates Card */}
                  <div style={{
                    background: '#fff',
                    border: '1px solid #e5e7eb',
                    borderRadius: 12,
                    padding: 20,
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                      <div style={{
                        width: 48,
                        height: 48,
                        borderRadius: 12,
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 24
                      }}>
                        <FaUsers style={{ color: '#fff' }} />
                      </div>
                      <div>
                        <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 500 }}>
                          Total Stipend Candidates
                        </div>
                        <div style={{ fontSize: 32, fontWeight: 700, color: '#667eea' }}>
                          {Object.values(companyStats).reduce((sum, stats) => sum + (stats.stipendCandidatesCount || 0), 0)}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Total Jobs Card */}
                  <div style={{
                    background: '#fff',
                    border: '1px solid #e5e7eb',
                    borderRadius: 12,
                    padding: 20,
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                      <div style={{
                        width: 48,
                        height: 48,
                        borderRadius: 12,
                        background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 24
                      }}>
                        <FaBriefcase style={{ color: '#fff' }} />
                      </div>
                      <div>
                        <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 500 }}>
                          Total Job Posts
                        </div>
                        <div style={{ fontSize: 32, fontWeight: 700, color: '#22c55e' }}>
                          {Object.values(companyStats).reduce((sum, stats) => sum + (stats.jobsCount || 0), 0)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {companiesLoading ? (
                <div style={{ textAlign: 'center', padding: 40 }}>
                  <div style={{
                    width: 40,
                    height: 40,
                    border: '4px solid #e5e7eb',
                    borderTop: '4px solid #667eea',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    margin: '0 auto 16px'
                  }} />
                  <p style={{ color: '#6b7280' }}>Loading companies...</p>
                </div>
              ) : companies.length > 0 ? (
                <div style={{ display: 'grid', gap: 16, gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(300px, 1fr))' }}>
                  {companies.map((company) => {
                    const stats = companyStats[company.id] || { candidatesCount: 0, stipendCandidatesCount: 0, jobsCount: 0 };
                    
                    return (
                      <div key={company.id} style={{
                        background: '#fff',
                        border: '1px solid #e5e7eb',
                        borderRadius: 12,
                        padding: 20,
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseOver={(e) => e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)'}
                      onMouseOut={(e) => e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)'}
                      >
                        <div style={{ marginBottom: 12 }}>
                          <h4 style={{ fontSize: 16, fontWeight: 600, color: '#374151', margin: '0 0 8px 0' }}>
                            {company.company_name}
                          </h4>
                          <p style={{ fontSize: 14, color: '#6b7280', margin: 0 }}>
                            Company ID: {company.id}
                          </p>
                        </div>
                        
                        {/* Stats Summary */}
                        <div style={{ display: 'flex', gap: 16, marginTop: 12, paddingTop: 12, borderTop: '1px solid #e5e7eb' }}>
                          <div style={{ flex: 1, textAlign: 'center' }}>
                            <div style={{ fontSize: 24, fontWeight: 700, color: '#667eea' }}>
                              {stats.stipendCandidatesCount || 0}
                            </div>
                            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                              Total Stipend Candidates
                            </div>
                          </div>
                          <div style={{ flex: 1, textAlign: 'center' }}>
                            <div style={{ fontSize: 24, fontWeight: 700, color: '#22c55e' }}>
                              {stats.jobsCount || 0}
                            </div>
                            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                              Jobs
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>
                  <FaBuilding style={{ fontSize: 48, marginBottom: 16, opacity: 0.5 }} />
                  <p>No companies assigned to you yet.</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'candidates' && (
            <div>
              {/* Sub-tabs for Registered, Unregistered, Employees */}
              <div style={{ 
                display: 'flex', 
                gap: 8, 
                marginBottom: 24,
                borderBottom: '2px solid #e5e7eb'
              }}>
                <button
                  onClick={() => {
                    setCandidatesSubTab('registered');
                    setCandidatesPage(1);
                  }}
                  style={{
                    padding: '12px 24px',
                    background: candidatesSubTab === 'registered' ? '#667eea' : 'transparent',
                    color: candidatesSubTab === 'registered' ? '#fff' : '#6b7280',
                    border: 'none',
                    borderBottom: candidatesSubTab === 'registered' ? '3px solid #667eea' : '3px solid transparent',
                    borderRadius: '8px 8px 0 0',
                    fontSize: 14,
                    fontWeight: candidatesSubTab === 'registered' ? 600 : 400,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                >
                  Registered ({registeredCount})
                </button>
                <button
                  onClick={() => {
                    setCandidatesSubTab('unregistered');
                    setUnregisteredPage(1);
                  }}
                  style={{
                    padding: '12px 24px',
                    background: candidatesSubTab === 'unregistered' ? '#667eea' : 'transparent',
                    color: candidatesSubTab === 'unregistered' ? '#fff' : '#6b7280',
                    border: 'none',
                    borderBottom: candidatesSubTab === 'unregistered' ? '3px solid #667eea' : '3px solid transparent',
                    borderRadius: '8px 8px 0 0',
                    fontSize: 14,
                    fontWeight: candidatesSubTab === 'unregistered' ? 600 : 400,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                >
                  Unregistered ({unregisteredCount})
                </button>
                <button
                  onClick={() => {
                    setCandidatesSubTab('employees');
                    setActiveEmployeesPage(1); // Reset to first page
                  }}
                  style={{
                    padding: '12px 24px',
                    background: candidatesSubTab === 'employees' ? '#667eea' : 'transparent',
                    color: candidatesSubTab === 'employees' ? '#fff' : '#6b7280',
                    border: 'none',
                    borderBottom: candidatesSubTab === 'employees' ? '3px solid #667eea' : '3px solid transparent',
                    borderRadius: '8px 8px 0 0',
                    fontSize: 14,
                    fontWeight: candidatesSubTab === 'employees' ? 600 : 400,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                >
                  Employees ({employeesCount})
                </button>
                <button
                  onClick={() => {
                    setCandidatesSubTab('inactive');
                    setInactiveEmployeesPage(1); // Reset to first page
                  }}
                  style={{
                    padding: '12px 24px',
                    background: candidatesSubTab === 'inactive' ? '#dc2626' : 'transparent',
                    color: candidatesSubTab === 'inactive' ? '#fff' : '#6b7280',
                    border: 'none',
                    borderBottom: candidatesSubTab === 'inactive' ? '3px solid #dc2626' : '3px solid transparent',
                    borderRadius: '8px 8px 0 0',
                    fontSize: 14,
                    fontWeight: candidatesSubTab === 'inactive' ? 600 : 400,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                >
                  Inactive ({inactiveCount})
                </button>
                <button
                  onClick={() => {
                    setCandidatesSubTab('supervisor-candidates');
                    setSupervisorCandidatesPage(1);
                  }}
                  style={{
                    padding: '12px 24px',
                    background: candidatesSubTab === 'supervisor-candidates' ? '#10b981' : 'transparent',
                    color: candidatesSubTab === 'supervisor-candidates' ? '#fff' : '#6b7280',
                    border: 'none',
                    borderBottom: candidatesSubTab === 'supervisor-candidates' ? '3px solid #10b981' : '3px solid transparent',
                    borderRadius: '8px 8px 0 0',
                    fontSize: 14,
                    fontWeight: candidatesSubTab === 'supervisor-candidates' ? 600 : 400,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                >
                  Supervisor Candidates ({supervisorCandidatesTotal})
                </button>
              </div>

              {/* Search and Filter - Only show for registered/unregistered tabs */}
              {(candidatesSubTab === 'registered' || candidatesSubTab === 'unregistered') && (
              <div style={{ 
                display: 'flex', 
                gap: 16, 
                marginBottom: 24,
                flexDirection: isMobile ? 'column' : 'row',
                alignItems: isMobile ? 'stretch' : 'center'
              }}>
                <div style={{ flex: 1, position: 'relative' }}>
                  <FaSearch style={{
                    position: 'absolute',
                    left: 12,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#9ca3af',
                    fontSize: 14
                  }} />
                  <input
                    type="text"
                    placeholder="Search candidates..."
                    value={candidatesSearch}
                    onChange={(e) => setCandidatesSearch(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px 12px 12px 40px',
                      border: '2px solid #e5e7eb',
                      borderRadius: 8,
                      fontSize: 14,
                      outline: 'none',
                      transition: 'border-color 0.2s ease'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#667eea'}
                    onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                  />
                </div>
                
                <select
                  value={selectedCompanyFilter}
                  onChange={(e) => {
                    setSelectedCompanyFilter(e.target.value);
                    // Reset to first page when company filter changes
                    if (candidatesSubTab === 'unregistered') {
                      setUnregisteredPage(1);
                    } else if (candidatesSubTab === 'registered') {
                      setCandidatesPage(1);
                    }
                  }}
                  style={{
                    padding: '12px 16px',
                    border: '2px solid #e5e7eb',
                    borderRadius: 8,
                    fontSize: 14,
                    background: '#fff',
                    minWidth: isMobile ? '100%' : 200,
                    outline: 'none'
                  }}
                >
                  <option value="">All Companies</option>
                  {companies.map(company => (
                    <option key={company.id} value={company.id}>
                      {company.company_name}
                    </option>
                  ))}
                </select>
              </div>
              )}

              {/* Registered Candidates */}
              {candidatesSubTab === 'registered' && (
                <div>
                  {/* Total Count Display */}
                  {!candidatesLoading && (
                    <div style={{ marginBottom: 16, padding: 12, background: '#f0f9ff', borderRadius: 8, border: '1px solid #bae6fd' }}>
                      <p style={{ margin: 0, fontSize: 14, color: '#0369a1', fontWeight: 600 }}>
                        Total Registered Candidates: {candidatesTotal}
                      </p>
                    </div>
                  )}

              {/* Candidates List */}
              {candidatesLoading ? (
                <div style={{ textAlign: 'center', padding: 40 }}>
                  <div style={{
                    width: 40,
                    height: 40,
                    border: '4px solid #e5e7eb',
                    borderTop: '4px solid #667eea',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    margin: '0 auto 16px'
                  }} />
                  <p style={{ color: '#6b7280' }}>Loading candidates...</p>
                </div>
              ) : candidates.length > 0 ? (
                <div>
                  {isMobile ? (
                    // Mobile Card View
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                      {candidates.map((candidate) => (
                        <div key={candidate.id} style={{
                          background: '#fff',
                          border: '1px solid #e5e7eb',
                          borderRadius: 12,
                          padding: 16,
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                            <div>
                              <h4 style={{ fontSize: 16, fontWeight: 600, color: '#374151', margin: '0 0 4px 0' }}>
                                {candidate.name}
                              </h4>
                              <p style={{ fontSize: 14, color: '#6b7280', margin: '0 0 4px 0' }}>
                                {candidate.email}
                              </p>
                              <p style={{ fontSize: 14, color: '#6b7280', margin: 0 }}>
                                {candidate.mobile}
                              </p>
                            </div>
                            <div style={{ display: 'flex', gap: 8 }}>
                              <button
                                onClick={() => handleManageDocuments(candidate)}
                                style={{
                                  padding: '8px 12px',
                                  background: '#10b981',
                                  color: '#fff',
                                  border: 'none',
                                  borderRadius: 6,
                                  fontSize: 12,
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 4
                                }}
                              >
                                <FaFileAlt />
                                Documents
                              </button>
                            </div>
                          </div>
                          {candidate.company && (
                            <div style={{ 
                              padding: '8px 12px', 
                              background: '#f3f4f6', 
                              borderRadius: 6,
                              fontSize: 12,
                              color: '#374151'
                            }}>
                              Company: {candidate.company.company_name}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    // Desktop Table View
                    <div style={{ background: '#fff', borderRadius: 12, overflow: 'hidden', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ background: '#f8fafc' }}>
                            <th style={{ padding: '16px', textAlign: 'left', fontSize: 14, fontWeight: 600, color: '#374151' }}>Name</th>
                            <th style={{ padding: '16px', textAlign: 'left', fontSize: 14, fontWeight: 600, color: '#374151' }}>Email</th>
                            <th style={{ padding: '16px', textAlign: 'left', fontSize: 14, fontWeight: 600, color: '#374151' }}>Mobile</th>
                            <th style={{ padding: '16px', textAlign: 'left', fontSize: 14, fontWeight: 600, color: '#374151' }}>Company</th>
                            <th style={{ padding: '16px', textAlign: 'center', fontSize: 14, fontWeight: 600, color: '#374151' }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {candidates.map((candidate) => (
                            <tr key={candidate.id} style={{ borderTop: '1px solid #e5e7eb' }}>
                              <td style={{ padding: '16px', fontSize: 14, color: '#374151' }}>{candidate.name}</td>
                              <td style={{ padding: '16px', fontSize: 14, color: '#6b7280' }}>{candidate.email}</td>
                              <td style={{ padding: '16px', fontSize: 14, color: '#6b7280' }}>{candidate.mobile}</td>
                              <td style={{ padding: '16px', fontSize: 14, color: '#6b7280' }}>
                                {candidate.company ? candidate.company.company_name : 'N/A'}
                              </td>
                              <td style={{ padding: '16px', textAlign: 'center' }}>
                                <button
                                  onClick={() => handleManageDocuments(candidate)}
                                  style={{
                                    padding: '8px 12px',
                                    background: '#10b981',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: 6,
                                    fontSize: 12,
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 4
                                  }}
                                >
                                  <FaFileAlt />
                                  Documents
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Pagination */}
                  {candidatesTotalPages > 1 && (
                    <div className="admin-pagination-wrap">
                      <span className="admin-pagination-info">
                        Page {candidatesPage} of {candidatesTotalPages} (Total: {candidatesTotal} candidates)
                      </span>
                      <div className="admin-pagination-btns">
                        <button
                          type="button"
                          className={`admin-pagination-btn primary`}
                          onClick={() => setCandidatesPage(Math.max(1, candidatesPage - 1))}
                          disabled={candidatesPage === 1}
                        >
                          Previous
                        </button>
                        <button
                          type="button"
                          className={`admin-pagination-btn primary`}
                          onClick={() => setCandidatesPage(Math.min(candidatesTotalPages, candidatesPage + 1))}
                          disabled={candidatesPage === candidatesTotalPages}
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>
                  <FaUsers style={{ fontSize: 48, marginBottom: 16, opacity: 0.5 }} />
                  <p>No candidates found.</p>
                    </div>
                  )}
                </div>
              )}

              {/* Unregistered Candidates */}
              {candidatesSubTab === 'unregistered' && (
                <div>
                  {unregisteredLoading ? (
                    <div style={{ textAlign: 'center', padding: 40 }}>
                      <div style={{
                        width: 40,
                        height: 40,
                        border: '4px solid #e5e7eb',
                        borderTop: '4px solid #667eea',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite',
                        margin: '0 auto 16px'
                      }} />
                      <p style={{ color: '#6b7280' }}>Loading unregistered candidates...</p>
                    </div>
                  ) : unregisteredCandidates.length > 0 ? (
                    <div>
                      {isMobile ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                          {unregisteredCandidates.map((candidate) => (
                            <div key={candidate.id} style={{
                              background: '#fff',
                              border: '1px solid #e5e7eb',
                              borderRadius: 12,
                              padding: 16,
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                            }}>
                              <h4 style={{ fontSize: 16, fontWeight: 600, color: '#374151', margin: '0 0 8px 0' }}>
                                {candidate.name}
                              </h4>
                              <p style={{ fontSize: 14, color: '#6b7280', margin: '0 0 4px 0' }}>
                                {candidate.email || 'No email'}
                              </p>
                              <p style={{ fontSize: 14, color: '#6b7280', margin: '0 0 8px 0' }}>
                                {candidate.mobile}
                              </p>
                              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                                {candidate.type && (
                                  <span style={{
                                    padding: '4px 8px',
                                    background: '#f3f4f6',
                                    borderRadius: 4,
                                    fontSize: 12,
                                    color: '#374151'
                                  }}>
                                    {candidate.type}
                                  </span>
                                )}
                                {candidate.company_name && (
                                  <span style={{
                                    padding: '4px 8px',
                                    background: '#e0f7fa',
                                    borderRadius: 4,
                                    fontSize: 12,
                                    color: '#374151'
                                  }}>
                                    {candidate.company_name}
                                  </span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div style={{ background: '#fff', borderRadius: 12, overflow: 'hidden', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                              <tr style={{ background: '#f8fafc' }}>
                                <th style={{ padding: '16px', textAlign: 'left', fontSize: 14, fontWeight: 600, color: '#374151' }}>Name</th>
                                <th style={{ padding: '16px', textAlign: 'left', fontSize: 14, fontWeight: 600, color: '#374151' }}>Email</th>
                                <th style={{ padding: '16px', textAlign: 'left', fontSize: 14, fontWeight: 600, color: '#374151' }}>Mobile</th>
                                <th style={{ padding: '16px', textAlign: 'left', fontSize: 14, fontWeight: 600, color: '#374151' }}>Type</th>
                                <th style={{ padding: '16px', textAlign: 'left', fontSize: 14, fontWeight: 600, color: '#374151' }}>Company</th>
                              </tr>
                            </thead>
                            <tbody>
                              {unregisteredCandidates.map((candidate) => (
                                <tr key={candidate.id} style={{ borderTop: '1px solid #e5e7eb' }}>
                                  <td style={{ padding: '16px', fontSize: 14, color: '#374151' }}>{candidate.name}</td>
                                  <td style={{ padding: '16px', fontSize: 14, color: '#6b7280' }}>{candidate.email || 'N/A'}</td>
                                  <td style={{ padding: '16px', fontSize: 14, color: '#6b7280' }}>{candidate.mobile}</td>
                                  <td style={{ padding: '16px', fontSize: 14, color: '#6b7280' }}>
                                    {candidate.type ? (
                                      <span style={{
                                        padding: '4px 8px',
                                        background: '#f3f4f6',
                                        borderRadius: 4,
                                        fontSize: 12
                                      }}>
                                        {candidate.type}
                                      </span>
                                    ) : 'N/A'}
                                  </td>
                                  <td style={{ padding: '16px', fontSize: 14, color: '#6b7280' }}>{candidate.company_name || 'N/A'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}

                      {/* Pagination */}
                      {unregisteredTotalPages > 1 && (
                        <div className="admin-pagination-wrap">
                          <span className="admin-pagination-info">Page {unregisteredPage} of {unregisteredTotalPages}</span>
                          <div className="admin-pagination-btns">
                            <button type="button" className="admin-pagination-btn primary" onClick={() => setUnregisteredPage(Math.max(1, unregisteredPage - 1))} disabled={unregisteredPage === 1}>Previous</button>
                            <button type="button" className="admin-pagination-btn primary" onClick={() => setUnregisteredPage(Math.min(unregisteredTotalPages, unregisteredPage + 1))} disabled={unregisteredPage === unregisteredTotalPages}>Next</button>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>
                      <FaUsers style={{ fontSize: 48, marginBottom: 16, opacity: 0.5 }} />
                      <p>No unregistered candidates found.</p>
                    </div>
                  )}
                </div>
              )}

              {/* Active Employees */}
              {candidatesSubTab === 'employees' && (
                <div>
                  {/* Company + activity year */}
                  <div style={{ marginBottom: 16, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                    <select
                      value={selectedCompanyFilter}
                      onChange={(e) => {
                        setSelectedCompanyFilter(e.target.value);
                        setActiveEmployeesPage(1); // Reset to first page
                      }}
                      style={{
                        padding: '12px 16px',
                        border: '2px solid #e5e7eb',
                        borderRadius: 8,
                        fontSize: 14,
                        background: '#fff',
                        minWidth: isMobile ? '100%' : 200,
                        outline: 'none'
                      }}
                    >
                      <option value="">All Companies</option>
                      {companies.map(company => (
                        <option key={company.id} value={company.id}>
                          {company.company_name}
                        </option>
                      ))}
                    </select>
                    <select
                      value={employeesActivityYear}
                      onChange={(e) => {
                        setEmployeesActivityYear(e.target.value);
                        setActiveEmployeesPage(1);
                        setInactiveEmployeesPage(1);
                      }}
                      aria-label="Stipend activity year"
                      style={{
                        padding: '12px 16px',
                        border: '2px solid #e5e7eb',
                        borderRadius: 8,
                        fontSize: 14,
                        background: '#fff',
                        minWidth: isMobile ? '100%' : 160,
                        outline: 'none'
                      }}
                    >
                      <option value="all">All years</option>
                      {Array.from({ length: Math.max(0, new Date().getFullYear() - 2022) }, (_, i) => {
                        const y = 2023 + i;
                        return (
                          <option key={y} value={String(y)}>
                            {y}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                  
                  {employeesLoading ? (
                    <div style={{ textAlign: 'center', padding: 40 }}>
                      <div style={{
                        width: 40,
                        height: 40,
                        border: '4px solid #e5e7eb',
                        borderTop: '4px solid #667eea',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite',
                        margin: '0 auto 16px'
                      }} />
                      <p style={{ color: '#6b7280' }}>Loading active employees...</p>
                    </div>
                  ) : activeEmployees.length > 0 ? (
                    <div>
                      <div style={{ marginBottom: 16, padding: 12, background: '#f0f9ff', borderRadius: 8, border: '1px solid #bae6fd' }}>
                        <p style={{ margin: 0, fontSize: 14, color: '#0369a1' }}>
                          {employeesListMeta.activityYear === 'all' ? (
                            <>
                              Active employees: latest stipend month matches the stipend-cycle reference (previous IST month).
                            </>
                          ) : (
                            <>
                              Stipend activity in <strong>{employeesListMeta.activityYear}</strong> only. Active = latest stipend month is at/near reference{' '}
                              {employeesListMeta.refYear != null && employeesListMeta.refMonth != null ? (
                                <strong>
                                  {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][employeesListMeta.refMonth - 1]}{' '}
                                  {employeesListMeta.refYear}
                                </strong>
                                  ) : (
                                    'period end'
                                  )}{' '}
                              (IST stipend-cycle rules).
                            </>
                          )}
                        </p>
                      </div>
                      {isMobile ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                          {activeEmployees.map((employee) => (
                            <div key={employee.id} style={{
                              background: '#fff',
                              border: '1px solid #e5e7eb',
                              borderRadius: 12,
                              padding: 16,
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                            }}>
                              <h4 style={{ fontSize: 16, fontWeight: 600, color: '#374151', margin: '0 0 8px 0' }}>
                                {employee.name}
                              </h4>
                              <p style={{ fontSize: 14, color: '#6b7280', margin: '0 0 4px 0' }}>
                                {employee.email || 'No email'}
                              </p>
                              <p style={{ fontSize: 14, color: '#6b7280', margin: '0 0 4px 0' }}>
                                {employee.mobile}
                              </p>
                              <p style={{ fontSize: 14, color: '#6b7280', margin: '0 0 4px 0', fontWeight: 500 }}>
                                Registered Company: {employee.registered_company_name || employee.company_name || 'N/A'}
                              </p>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div style={{ background: '#fff', borderRadius: 12, overflow: 'hidden', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                              <tr style={{ background: '#f8fafc' }}>
                                <th style={{ padding: '16px', textAlign: 'left', fontSize: 14, fontWeight: 600, color: '#374151' }}>Name</th>
                                <th style={{ padding: '16px', textAlign: 'left', fontSize: 14, fontWeight: 600, color: '#374151' }}>Email</th>
                                <th style={{ padding: '16px', textAlign: 'left', fontSize: 14, fontWeight: 600, color: '#374151' }}>Mobile</th>
                                <th style={{ padding: '16px', textAlign: 'left', fontSize: 14, fontWeight: 600, color: '#374151' }}>Registered Company</th>
                                <th style={{ padding: '16px', textAlign: 'left', fontSize: 14, fontWeight: 600, color: '#374151' }}>Active Months</th>
                                <th style={{ padding: '16px', textAlign: 'left', fontSize: 14, fontWeight: 600, color: '#374151' }}>Last Active</th>
                              </tr>
                            </thead>
                            <tbody>
                              {activeEmployees.map((employee) => {
                                const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                                // Count total number of months the employee appeared in
                                let activeMonthsCount = 0;
                                if (employee.activeMonths && Array.isArray(employee.activeMonths)) {
                                  // Count unique months across all years
                                  activeMonthsCount = employee.activeMonths.length;
                                }
                                const activeMonthsStr = activeMonthsCount > 0 
                                  ? `${activeMonthsCount} ${activeMonthsCount === 1 ? 'month' : 'months'}`
                                  : '0 months';
                                const lastActiveStr = employee.lastActiveMonth && employee.lastActiveYear
                                  ? `${monthNames[employee.lastActiveMonth - 1]} ${employee.lastActiveYear}`
                                  : 'N/A';
                                return (
                                  <tr key={employee.id} style={{ borderTop: '1px solid #e5e7eb' }}>
                                    <td style={{ padding: '16px', fontSize: 14, color: '#374151' }}>
                                      {employee.name}
                                      {employee.isUnregistered && (
                                        <span style={{ marginLeft: 8, padding: '2px 6px', background: '#fef3c7', color: '#92400e', borderRadius: 4, fontSize: 11 }}>
                                          Unregistered
                                        </span>
                                      )}
                                    </td>
                                    <td style={{ padding: '16px', fontSize: 14, color: '#6b7280' }}>{employee.email || 'N/A'}</td>
                                    <td style={{ padding: '16px', fontSize: 14, color: '#6b7280' }}>{employee.mobile}</td>
                                    <td style={{ padding: '16px', fontSize: 14, color: '#6b7280', fontWeight: 500 }}>
                                      {employee.registered_company_name || employee.company_name || 'N/A'}
                                    </td>
                                    <td style={{ padding: '16px', fontSize: 14, color: '#6b7280' }}>{activeMonthsStr}</td>
                                    <td style={{ padding: '16px', fontSize: 14, color: '#6b7280' }}>{lastActiveStr}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}

                      {/* Pagination */}
                      {activeEmployeesTotalPages > 1 && (
                        <div className="admin-pagination-wrap">
                          <span className="admin-pagination-info">Page {activeEmployeesPage} of {activeEmployeesTotalPages} (Total: {activeEmployeesTotal} employees)</span>
                          <div className="admin-pagination-btns">
                            <button type="button" className="admin-pagination-btn primary" onClick={() => setActiveEmployeesPage(Math.max(1, activeEmployeesPage - 1))} disabled={activeEmployeesPage === 1}>Previous</button>
                            <button type="button" className="admin-pagination-btn primary" onClick={() => setActiveEmployeesPage(Math.min(activeEmployeesTotalPages, activeEmployeesPage + 1))} disabled={activeEmployeesPage === activeEmployeesTotalPages}>Next</button>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>
                      <FaUsers style={{ fontSize: 48, marginBottom: 16, opacity: 0.5 }} />
                      <p>No active employees found.</p>
                    </div>
                  )}
                </div>
              )}

              {/* Inactive Employees */}
              {candidatesSubTab === 'inactive' && (
                <div>
                  {/* Company + year + CSV export */}
                  <div style={{ marginBottom: 16, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', flex: 1 }}>
                    <select
                      value={selectedCompanyFilter}
                      onChange={(e) => {
                        setSelectedCompanyFilter(e.target.value);
                        setInactiveEmployeesPage(1); // Reset to first page
                      }}
                      style={{
                        padding: '12px 16px',
                        border: '2px solid #e5e7eb',
                        borderRadius: 8,
                        fontSize: 14,
                        background: '#fff',
                        minWidth: isMobile ? '100%' : 200,
                        outline: 'none'
                      }}
                    >
                      <option value="">All Companies</option>
                      {companies.map(company => (
                        <option key={company.id} value={company.id}>
                          {company.company_name}
                        </option>
                      ))}
                    </select>
                    <select
                      value={employeesActivityYear}
                      onChange={(e) => {
                        setEmployeesActivityYear(e.target.value);
                        setActiveEmployeesPage(1);
                        setInactiveEmployeesPage(1);
                      }}
                      aria-label="Stipend activity year"
                      style={{
                        padding: '12px 16px',
                        border: '2px solid #e5e7eb',
                        borderRadius: 8,
                        fontSize: 14,
                        background: '#fff',
                        minWidth: isMobile ? '100%' : 160,
                        outline: 'none'
                      }}
                    >
                      <option value="all">All years</option>
                      {Array.from({ length: Math.max(0, new Date().getFullYear() - 2022) }, (_, i) => {
                        const y = 2023 + i;
                        return (
                          <option key={y} value={String(y)}>
                            {y}
                          </option>
                        );
                      })}
                    </select>
                    </div>
                    <button
                      type="button"
                      onClick={downloadInactiveEmployeesCsv}
                      disabled={exportingInactiveCsv || employeesLoading}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '10px 16px',
                        fontSize: 14,
                        fontWeight: 600,
                        color: '#fff',
                        background: exportingInactiveCsv || employeesLoading ? '#9ca3af' : '#dc2626',
                        border: 'none',
                        borderRadius: 8,
                        cursor: exportingInactiveCsv || employeesLoading ? 'not-allowed' : 'pointer',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      <FaDownload />
                      {exportingInactiveCsv ? 'Preparing…' : 'Download CSV (full list)'}
                    </button>
                  </div>
                  
                  {employeesLoading ? (
                    <div style={{ textAlign: 'center', padding: 40 }}>
                      <div style={{
                        width: 40,
                        height: 40,
                        border: '4px solid #e5e7eb',
                        borderTop: '4px solid #dc2626',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite',
                        margin: '0 auto 16px'
                      }} />
                      <p style={{ color: '#6b7280' }}>Loading inactive employees...</p>
                    </div>
                  ) : inactiveEmployees.length > 0 ? (
                    <div>
                      <div style={{ marginBottom: 16, padding: 12, background: '#fee2e2', borderRadius: 8, border: '1px solid #fecaca' }}>
                        <p style={{ margin: 0, fontSize: 14, color: '#991b1b', fontWeight: 600 }}>
                          Inactive Employees: {inactiveEmployeesTotal}
                          {employeesListMeta.activityYear === 'all'
                            ? ' — no stipend appearance for 1+ month versus stipend-cycle reference (previous IST month).'
                            : (
                                <>
                                  {' '}
                                  — in {employeesListMeta.activityYear}, gap of 1+ month before{' '}
                                  {employeesListMeta.refYear != null && employeesListMeta.refMonth != null ? (
                                    <span>
                                      {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][employeesListMeta.refMonth - 1]}{' '}
                                      {employeesListMeta.refYear}
                                    </span>
                                  ) : (
                                    'reference'
                                  )}
                                  .
                                </>
                              )}
                        </p>
                      </div>
                      {isMobile ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                          {inactiveEmployees.map((employee) => {
                            const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                            const lastActiveStr = employee.lastActiveMonth && employee.lastActiveYear
                              ? `${monthNames[employee.lastActiveMonth - 1]} ${employee.lastActiveYear}`
                              : 'N/A';
                            return (
                              <div key={employee.id} style={{
                                background: '#fff',
                                border: '1px solid #fecaca',
                                borderRadius: 12,
                                padding: 16,
                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                              }}>
                                <h4 style={{ fontSize: 16, fontWeight: 600, color: '#374151', margin: '0 0 8px 0' }}>
                                  {employee.name}
                                  {employee.isUnregistered && (
                                    <span style={{ marginLeft: 8, padding: '2px 6px', background: '#fef3c7', color: '#92400e', borderRadius: 4, fontSize: 11 }}>
                                      Unregistered
                                    </span>
                                  )}
                                </h4>
                                <p style={{ fontSize: 14, color: '#6b7280', margin: '0 0 4px 0' }}>
                                  {employee.email || 'No email'}
                                </p>
                                <p style={{ fontSize: 14, color: '#6b7280', margin: '0 0 4px 0' }}>
                                  {employee.mobile}
                                </p>
                                <p style={{ fontSize: 14, color: '#6b7280', margin: '0 0 4px 0', fontWeight: 500 }}>
                                  Registered Company: {employee.registered_company_name || employee.company_name || 'N/A'}
                                </p>
                                <p style={{ fontSize: 14, color: '#dc2626', margin: '0 0 4px 0', fontWeight: 600 }}>
                                  Last Active: {lastActiveStr} ({employee.monthsSinceLastActive} months ago)
                                </p>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div style={{ background: '#fff', borderRadius: 12, overflow: 'hidden', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                              <tr style={{ background: '#fef2f2' }}>
                                <th style={{ padding: '16px', textAlign: 'left', fontSize: 14, fontWeight: 600, color: '#374151' }}>Name</th>
                                <th style={{ padding: '16px', textAlign: 'left', fontSize: 14, fontWeight: 600, color: '#374151' }}>Email</th>
                                <th style={{ padding: '16px', textAlign: 'left', fontSize: 14, fontWeight: 600, color: '#374151' }}>Mobile</th>
                                <th style={{ padding: '16px', textAlign: 'left', fontSize: 14, fontWeight: 600, color: '#374151' }}>Registered Company</th>
                                <th style={{ padding: '16px', textAlign: 'left', fontSize: 14, fontWeight: 600, color: '#374151' }}>Last Active</th>
                                <th style={{ padding: '16px', textAlign: 'left', fontSize: 14, fontWeight: 600, color: '#374151' }}>Months Inactive</th>
                              </tr>
                            </thead>
                            <tbody>
                              {inactiveEmployees.map((employee) => {
                                const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                                const lastActiveStr = employee.lastActiveMonth && employee.lastActiveYear
                                  ? `${monthNames[employee.lastActiveMonth - 1]} ${employee.lastActiveYear}`
                                  : 'N/A';
                                return (
                                  <tr key={employee.id} style={{ borderTop: '1px solid #fecaca' }}>
                                    <td style={{ padding: '16px', fontSize: 14, color: '#374151' }}>
                                      {employee.name}
                                      {employee.isUnregistered && (
                                        <span style={{ marginLeft: 8, padding: '2px 6px', background: '#fef3c7', color: '#92400e', borderRadius: 4, fontSize: 11 }}>
                                          Unregistered
                                        </span>
                                      )}
                                    </td>
                                    <td style={{ padding: '16px', fontSize: 14, color: '#6b7280' }}>{employee.email || 'N/A'}</td>
                                    <td style={{ padding: '16px', fontSize: 14, color: '#6b7280' }}>{employee.mobile}</td>
                                    <td style={{ padding: '16px', fontSize: 14, color: '#6b7280', fontWeight: 500 }}>
                                      {employee.registered_company_name || employee.company_name || 'N/A'}
                                    </td>
                                    <td style={{ padding: '16px', fontSize: 14, color: '#6b7280' }}>{lastActiveStr}</td>
                                    <td style={{ padding: '16px', fontSize: 14, color: '#dc2626', fontWeight: 600 }}>{employee.monthsSinceLastActive} months</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}

                      {/* Pagination */}
                      {inactiveEmployeesTotalPages > 1 && (
                        <div className="admin-pagination-wrap">
                          <span className="admin-pagination-info">Page {inactiveEmployeesPage} of {inactiveEmployeesTotalPages} (Total: {inactiveEmployeesTotal} employees)</span>
                          <div className="admin-pagination-btns">
                            <button type="button" className="admin-pagination-btn primary" onClick={() => setInactiveEmployeesPage(Math.max(1, inactiveEmployeesPage - 1))} disabled={inactiveEmployeesPage === 1}>Previous</button>
                            <button type="button" className="admin-pagination-btn primary" onClick={() => setInactiveEmployeesPage(Math.min(inactiveEmployeesTotalPages, inactiveEmployeesPage + 1))} disabled={inactiveEmployeesPage === inactiveEmployeesTotalPages}>Next</button>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>
                      <FaUsers style={{ fontSize: 48, marginBottom: 16, opacity: 0.5 }} />
                      <p>No inactive employees found.</p>
                    </div>
                  )}
                </div>
              )}

              {/* Supervisor Candidates */}
              {candidatesSubTab === 'supervisor-candidates' && (
                <div>
                  {/* Search and Filter */}
                  <div style={{ 
                    display: 'flex', 
                    gap: 16, 
                    marginBottom: 24,
                    flexDirection: isMobile ? 'column' : 'row',
                    alignItems: isMobile ? 'stretch' : 'center',
                    flexWrap: 'wrap'
                  }}>
                    <div style={{ flex: 1, minWidth: isMobile ? '100%' : 200, position: 'relative' }}>
                      <FaSearch style={{
                        position: 'absolute',
                        left: 12,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: '#9ca3af',
                        fontSize: 14
                      }} />
                      <input
                        type="text"
                        placeholder="Search supervisor candidates..."
                        value={supervisorCandidatesSearch}
                        onChange={(e) => setSupervisorCandidatesSearch(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '12px 12px 12px 40px',
                          border: '2px solid #e5e7eb',
                          borderRadius: 8,
                          fontSize: 14,
                          outline: 'none',
                          transition: 'border-color 0.2s ease'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#10b981'}
                        onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                      />
                    </div>
                    
                    <select
                      value={selectedSupervisorFilter}
                      onChange={(e) => setSelectedSupervisorFilter(e.target.value)}
                      style={{
                        padding: '12px 16px',
                        border: '2px solid #e5e7eb',
                        borderRadius: 8,
                        fontSize: 14,
                        background: '#fff',
                        minWidth: isMobile ? '100%' : 200,
                        outline: 'none'
                      }}
                    >
                      <option value="">All Supervisors</option>
                      {(supervisorsForDropdown.length ? supervisorsForDropdown : supervisors).map(supervisor => (
                        <option key={supervisor.id} value={supervisor.id}>
                          {supervisor.name}
                        </option>
                      ))}
                    </select>

                    <button
                      type="button"
                      onClick={() => setShowCandidatesUploadModal(true)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '12px 20px',
                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 8,
                        fontSize: 14,
                        fontWeight: 600,
                        cursor: 'pointer',
                        boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)'
                      }}
                    >
                      <FaUpload style={{ fontSize: 14 }} />
                      Upload CSV
                    </button>
                  </div>

                  {/* Total Count Display */}
                  {!supervisorCandidatesLoading && (
                    <div style={{ marginBottom: 16, padding: 12, background: '#f0fdf4', borderRadius: 8, border: '1px solid #86efac' }}>
                      <p style={{ margin: 0, fontSize: 14, color: '#166534', fontWeight: 600 }}>
                        Total Supervisor Candidates: {supervisorCandidatesTotal}
                      </p>
                    </div>
                  )}

                  {supervisorCandidatesLoading ? (
                    <div style={{ textAlign: 'center', padding: 40 }}>
                      <div style={{
                        width: 40,
                        height: 40,
                        border: '4px solid #e5e7eb',
                        borderTop: '4px solid #10b981',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite',
                        margin: '0 auto 16px'
                      }} />
                      <p style={{ color: '#6b7280' }}>Loading supervisor candidates...</p>
                    </div>
                  ) : supervisorCandidates.length > 0 ? (
                    <div>
                      {isMobile ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                          {supervisorCandidates.map((candidate) => (
                            <div key={candidate.id} style={{
                              background: '#fff',
                              border: '1px solid #e5e7eb',
                              borderRadius: 12,
                              padding: 16,
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                            }}>
                              <h4 style={{ fontSize: 16, fontWeight: 600, color: '#374151', margin: '0 0 8px 0' }}>
                                {candidate.name}
                              </h4>
                              <p style={{ fontSize: 14, color: '#6b7280', margin: '0 0 4px 0' }}>
                                {candidate.email}
                              </p>
                              <p style={{ fontSize: 14, color: '#6b7280', margin: '0 0 4px 0' }}>
                                {candidate.mobile}
                              </p>
                              {candidate.supervisor && (
                                <div style={{ 
                                  marginTop: 8,
                                  padding: '8px 12px', 
                                  background: '#f0fdf4', 
                                  borderRadius: 6,
                                  fontSize: 12,
                                  color: '#166534'
                                }}>
                                  Supervisor: {candidate.supervisor.name}
                                </div>
                              )}
                              {candidate.qualification && (
                                <p style={{ fontSize: 14, color: '#6b7280', margin: '4px 0 0 0' }}>
                                  Qualification: {candidate.qualification}
                                </p>
                              )}
                              {candidate.course && (
                                <p style={{ fontSize: 14, color: '#6b7280', margin: '4px 0 0 0' }}>
                                  Course: {candidate.course}
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div style={{ background: '#fff', borderRadius: 12, overflow: 'hidden', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                              <tr style={{ background: '#f8fafc' }}>
                                <th style={{ padding: '16px', textAlign: 'left', fontSize: 14, fontWeight: 600, color: '#374151' }}>Name</th>
                                <th style={{ padding: '16px', textAlign: 'left', fontSize: 14, fontWeight: 600, color: '#374151' }}>Email</th>
                                <th style={{ padding: '16px', textAlign: 'left', fontSize: 14, fontWeight: 600, color: '#374151' }}>Mobile</th>
                                <th style={{ padding: '16px', textAlign: 'left', fontSize: 14, fontWeight: 600, color: '#374151' }}>Supervisor</th>
                                <th style={{ padding: '16px', textAlign: 'left', fontSize: 14, fontWeight: 600, color: '#374151' }}>Qualification</th>
                                <th style={{ padding: '16px', textAlign: 'left', fontSize: 14, fontWeight: 600, color: '#374151' }}>Course</th>
                                <th style={{ padding: '16px', textAlign: 'left', fontSize: 14, fontWeight: 600, color: '#374151' }}>Created</th>
                                <th style={{ padding: '16px', textAlign: 'center', fontSize: 14, fontWeight: 600, color: '#374151' }}>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {supervisorCandidates.map((candidate) => (
                                <tr key={candidate.id} style={{ borderTop: '1px solid #e5e7eb' }}>
                                  <td style={{ padding: '16px', fontSize: 14, color: '#374151', fontWeight: 600 }}>{candidate.name}</td>
                                  <td style={{ padding: '16px', fontSize: 14, color: '#6b7280' }}>{candidate.email}</td>
                                  <td style={{ padding: '16px', fontSize: 14, color: '#6b7280' }}>{candidate.mobile}</td>
                                  <td style={{ padding: '16px', fontSize: 14, color: '#6b7280' }}>
                                    {candidate.supervisor ? (
                                      <span style={{
                                        padding: '4px 12px',
                                        borderRadius: 16,
                                        fontSize: 12,
                                        fontWeight: 600,
                                        background: '#dbeafe',
                                        color: '#1e40af'
                                      }}>
                                        {candidate.supervisor.name}
                                      </span>
                                    ) : 'N/A'}
                                  </td>
                                  <td style={{ padding: '16px', fontSize: 14, color: '#6b7280' }}>{candidate.qualification || 'N/A'}</td>
                                  <td style={{ padding: '16px', fontSize: 14, color: '#6b7280' }}>{candidate.course || 'N/A'}</td>
                                  <td style={{ padding: '16px', fontSize: 14, color: '#6b7280' }}>
                                    {new Date(candidate.created_at).toLocaleDateString()}
                                  </td>
                                  <td style={{ padding: '16px', textAlign: 'center' }}>
                                    <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
                                      <button
                                        onClick={() => handleEditSupervisorCandidate(candidate)}
                                        style={{
                                          padding: '6px 12px',
                                          background: '#667eea',
                                          color: '#fff',
                                          border: 'none',
                                          borderRadius: 6,
                                          fontSize: 11,
                                          fontWeight: 600,
                                          cursor: 'pointer',
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: 4
                                        }}
                                        title="Edit Candidate"
                                      >
                                        <FaEdit style={{ fontSize: 10 }} />
                                        Edit
                                      </button>
                                      <button
                                        onClick={() => handleManageSupervisorCandidateDocuments(candidate)}
                                        style={{
                                          padding: '6px 12px',
                                          background: '#10b981',
                                          color: '#fff',
                                          border: 'none',
                                          borderRadius: 6,
                                          fontSize: 11,
                                          fontWeight: 600,
                                          cursor: 'pointer',
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: 4
                                        }}
                                        title="Manage Documents"
                                      >
                                        <FaFileAlt style={{ fontSize: 10 }} />
                                        Documents
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}

                      {/* Pagination */}
                      {supervisorCandidatesTotalPages > 1 && (
                        <div className="admin-pagination-wrap">
                          <span className="admin-pagination-info">Page {supervisorCandidatesPage} of {supervisorCandidatesTotalPages} (Total: {supervisorCandidatesTotal} candidates)</span>
                          <div className="admin-pagination-btns">
                            <button type="button" className="admin-pagination-btn primary" onClick={() => setSupervisorCandidatesPage(Math.max(1, supervisorCandidatesPage - 1))} disabled={supervisorCandidatesPage === 1}>Previous</button>
                            <button type="button" className="admin-pagination-btn primary" onClick={() => setSupervisorCandidatesPage(Math.min(supervisorCandidatesTotalPages, supervisorCandidatesPage + 1))} disabled={supervisorCandidatesPage === supervisorCandidatesTotalPages}>Next</button>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>
                      <FaUsers style={{ fontSize: 48, marginBottom: 16, opacity: 0.5 }} />
                      <p>No supervisor candidates found.</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {activeTab === 'jobs' && (
            <div>
              {/* Header with Post Job Button */}
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                marginBottom: 24,
                flexDirection: isMobile ? 'column' : 'row',
                gap: 16
              }}>
                <h2 style={{ color: '#667eea', fontWeight: 700, margin: 0 }}>Jobs</h2>
                <button
                  onClick={() => {
                    setShowJobForm(true);
                    setJobFormError('');
                    setJobFormSuccess('');
                    setJobLogoFile(null);
                    if (jobLogoInputRef.current) jobLogoInputRef.current.value = '';
                  }}
                  style={{
                    padding: '12px 24px',
                    background: '#667eea',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 8,
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    whiteSpace: 'nowrap'
                  }}
                >
                  <FaPlus /> Post New Job
                </button>
              </div>

              {/* Search and Filter */}
              <div style={{ 
                display: 'flex', 
                gap: 16, 
                marginBottom: 24,
                flexDirection: isMobile ? 'column' : 'row',
                alignItems: isMobile ? 'stretch' : 'center'
              }}>
                <div style={{ flex: 1, position: 'relative' }}>
                  <FaSearch style={{
                    position: 'absolute',
                    left: 12,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#9ca3af',
                    fontSize: 14
                  }} />
                  <input
                    type="text"
                    placeholder="Search jobs..."
                    value={jobsSearch}
                    onChange={(e) => setJobsSearch(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px 12px 12px 40px',
                      border: '2px solid #e5e7eb',
                      borderRadius: 8,
                      fontSize: 14,
                      outline: 'none',
                      transition: 'border-color 0.2s ease'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#667eea'}
                    onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                  />
                </div>
                
                <select
                  value={selectedJobCompanyFilter}
                  onChange={(e) => setSelectedJobCompanyFilter(e.target.value)}
                  style={{
                    padding: '12px 16px',
                    border: '2px solid #e5e7eb',
                    borderRadius: 8,
                    fontSize: 14,
                    background: '#fff',
                    minWidth: isMobile ? '100%' : 200,
                    outline: 'none'
                  }}
                >
                  <option value="">All Companies</option>
                  {companies.map(company => (
                    <option key={company.id} value={company.id}>
                      {company.company_name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Jobs List */}
              {jobsLoading ? (
                <div style={{ textAlign: 'center', padding: 40 }}>
                  <div style={{
                    width: 40,
                    height: 40,
                    border: '4px solid #e5e7eb',
                    borderTop: '4px solid #667eea',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    margin: '0 auto 16px'
                  }} />
                  <p style={{ color: '#6b7280' }}>Loading jobs...</p>
                </div>
              ) : jobs.length > 0 ? (
                <div>
                  {isMobile ? (
                    // Mobile Card View
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                      {jobs.map((job) => (
                        <div key={job.id} style={{
                          background: '#fff',
                          border: '1px solid #e5e7eb',
                          borderRadius: 12,
                          padding: 16,
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}>
                          <div style={{ marginBottom: 12 }}>
                            <h4 style={{ fontSize: 16, fontWeight: 600, color: '#374151', margin: '0 0 8px 0' }}>
                              {job.title}
                            </h4>
                            <p style={{ fontSize: 14, color: '#6b7280', margin: '0 0 8px 0' }}>
                              {job.location}
                            </p>
                            {(job.stipend_min || job.salary_min) && (job.stipend_max || job.salary_max) && (
                              <p style={{ fontSize: 14, color: '#059669', fontWeight: 600, margin: '0 0 8px 0' }}>
                                ₹{(job.stipend_min || job.salary_min || 0).toLocaleString()} - ₹{(job.stipend_max || job.salary_max || 0).toLocaleString()}
                              </p>
                            )}
                          </div>
                          {job.company && (
                            <div style={{ 
                              padding: '8px 12px', 
                              background: '#f3f4f6', 
                              borderRadius: 6,
                              fontSize: 12,
                              color: '#374151',
                              marginBottom: 8
                            }}>
                              Company: {job.company.company_name}
                            </div>
                          )}
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
                            <span style={{
                              padding: '4px 12px',
                              borderRadius: 16,
                              fontSize: 12,
                              fontWeight: 600,
                              background: '#e0e7ff',
                              color: '#4338ca'
                            }}>
                              {jobApplicantCounts[job.id] || 0} applicant{jobApplicantCounts[job.id] !== 1 ? 's' : ''}
                            </span>
                            <div style={{ display: 'flex', gap: 8 }}>
                              <button
                                onClick={() => handleCopyJobShareLink(job)}
                                style={{
                                  padding: '6px 12px',
                                  background: '#0d9488',
                                  color: '#fff',
                                  border: 'none',
                                  borderRadius: 6,
                                  fontSize: 12,
                                  fontWeight: 600,
                                  cursor: 'pointer'
                                }}
                              >
                                Share Link
                              </button>
                              <button
                                onClick={() => handleViewApplicants(job)}
                                style={{
                                  padding: '6px 12px',
                                  background: '#667eea',
                                  color: '#fff',
                                  border: 'none',
                                  borderRadius: 6,
                                  fontSize: 12,
                                  fontWeight: 600,
                                  cursor: 'pointer',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 6
                                }}
                              >
                                <FaUsers /> View Applicants
                              </button>
                              <button
                                onClick={() => handleSpocJobVisibility(job, !job.hidden_by_user)}
                                style={{
                                  padding: '6px 12px',
                                  background: job.hidden_by_user ? '#0ea5e9' : '#ef4444',
                                  color: '#fff',
                                  border: 'none',
                                  borderRadius: 6,
                                  fontSize: 12,
                                  fontWeight: 600,
                                  cursor: 'pointer'
                                }}
                              >
                                {job.hidden_by_user ? 'Unhide' : 'Hide'}
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    // Desktop Table View
                    <div style={{ background: '#fff', borderRadius: 12, overflow: 'hidden', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ background: '#f8fafc' }}>
                            <th style={{ padding: '16px', textAlign: 'left', fontSize: 14, fontWeight: 600, color: '#374151' }}>Title</th>
                            <th style={{ padding: '16px', textAlign: 'left', fontSize: 14, fontWeight: 600, color: '#374151' }}>Location</th>
                            <th style={{ padding: '16px', textAlign: 'left', fontSize: 14, fontWeight: 600, color: '#374151' }}>Salary</th>
                            <th style={{ padding: '16px', textAlign: 'left', fontSize: 14, fontWeight: 600, color: '#374151' }}>Company</th>
                            <th style={{ padding: '16px', textAlign: 'center', fontSize: 14, fontWeight: 600, color: '#374151' }}>Applicants</th>
                            <th style={{ padding: '16px', textAlign: 'center', fontSize: 14, fontWeight: 600, color: '#374151' }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {jobs.map((job) => (
                            <tr key={job.id} style={{ borderTop: '1px solid #e5e7eb' }}>
                              <td style={{ padding: '16px', fontSize: 14, color: '#374151', fontWeight: 600 }}>{job.title}</td>
                              <td style={{ padding: '16px', fontSize: 14, color: '#6b7280' }}>{job.location}</td>
                              <td style={{ padding: '16px', fontSize: 14, color: '#059669', fontWeight: 600 }}>
                                {(job.stipend_min || job.salary_min) && (job.stipend_max || job.salary_max) ? 
                                  `₹${(job.stipend_min || job.salary_min || 0).toLocaleString()} - ₹${(job.stipend_max || job.salary_max || 0).toLocaleString()}` : 
                                  'Not specified'
                                }
                              </td>
                              <td style={{ padding: '16px', fontSize: 14, color: '#6b7280' }}>
                                {job.company ? job.company.company_name : 'N/A'}
                              </td>
                              <td style={{ padding: '16px', textAlign: 'center' }}>
                                <span style={{
                                  padding: '4px 12px',
                                  borderRadius: 16,
                                  fontSize: 12,
                                  fontWeight: 600,
                                  background: '#e0e7ff',
                                  color: '#4338ca',
                                  display: 'inline-block'
                                }}>
                                  {jobApplicantCounts[job.id] || 0} applicant{jobApplicantCounts[job.id] !== 1 ? 's' : ''}
                                </span>
                              </td>
                              <td style={{ padding: '16px', textAlign: 'center' }}>
                                <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                                  <button
                                    onClick={() => handleCopyJobShareLink(job)}
                                    style={{
                                      padding: '6px 10px',
                                      background: '#0d9488',
                                      color: '#fff',
                                      border: 'none',
                                      borderRadius: 6,
                                      fontSize: 12,
                                      fontWeight: 600,
                                      cursor: 'pointer'
                                    }}
                                  >
                                    Share
                                  </button>
                                  <button
                                    onClick={() => handleViewApplicants(job)}
                                    style={{
                                      padding: '6px 12px',
                                      background: '#667eea',
                                      color: '#fff',
                                      border: 'none',
                                      borderRadius: 6,
                                      fontSize: 12,
                                      fontWeight: 600,
                                      cursor: 'pointer',
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: 6
                                    }}
                                  >
                                    <FaUsers /> View
                                  </button>
                                  <button
                                    onClick={() => handleSpocJobVisibility(job, !job.hidden_by_user)}
                                    style={{
                                      padding: '6px 10px',
                                      background: job.hidden_by_user ? '#0ea5e9' : '#ef4444',
                                      color: '#fff',
                                      border: 'none',
                                      borderRadius: 6,
                                      fontSize: 12,
                                      fontWeight: 600,
                                      cursor: 'pointer'
                                    }}
                                  >
                                    {job.hidden_by_user ? 'Unhide' : 'Hide'}
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Pagination */}
                  {jobsTotalPages > 1 && (
                    <div className="admin-pagination-wrap">
                      <span className="admin-pagination-info">Page {jobsPage} of {jobsTotalPages}</span>
                      <div className="admin-pagination-btns">
                        <button type="button" className="admin-pagination-btn primary" onClick={() => setJobsPage(Math.max(1, jobsPage - 1))} disabled={jobsPage === 1}>Previous</button>
                        <button type="button" className="admin-pagination-btn primary" onClick={() => setJobsPage(Math.min(jobsTotalPages, jobsPage + 1))} disabled={jobsPage === jobsTotalPages}>Next</button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>
                  <FaBriefcase style={{ fontSize: 48, marginBottom: 16, opacity: 0.5 }} />
                  <p>No jobs found.</p>
                </div>
              )}

              {/* Job Posting Modal */}
              {showJobForm && (
                <div style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'rgba(0, 0, 0, 0.5)',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  zIndex: 1000,
                  padding: 20
                }}
                onClick={(e) => {
                  if (e.target === e.currentTarget) {
                    setShowJobForm(false);
                    setJobFormError('');
                    setJobFormSuccess('');
                    setJobLogoFile(null);
                    if (jobLogoInputRef.current) jobLogoInputRef.current.value = '';
                  }
                }}
                >
                  <div style={{
                    background: '#fff',
                    borderRadius: 12,
                    padding: 24,
                    maxWidth: 600,
                    width: '100%',
                    maxHeight: '90vh',
                    overflowY: 'auto',
                    boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
                  }}
                  onClick={(e) => e.stopPropagation()}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                      <h3 style={{ fontSize: 20, fontWeight: 700, color: '#374151', margin: 0 }}>Post New Job</h3>
                      <button
                        onClick={() => {
                          setShowJobForm(false);
                          setJobFormError('');
                          setJobFormSuccess('');
                          setJobLogoFile(null);
                          if (jobLogoInputRef.current) jobLogoInputRef.current.value = '';
                          setJobForm({
                            title: '',
                            description: '',
                            age_criteria: '',
                            stipend_min: '',
                            stipend_max: '',
                            qualification: [],
                            location: '',
                            company_id: ''
                          });
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          fontSize: 24,
                          color: '#6b7280',
                          cursor: 'pointer',
                          padding: 0,
                          width: 32,
                          height: 32,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <FaTimes />
                      </button>
                    </div>

                    <form onSubmit={handleJobSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                      {/* Company Selection */}
                      <div>
                        <label style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 8 }}>
                          Company <span style={{ color: '#ef4444' }}>*</span>
                        </label>
                        <CompanyDropdown
                          companies={companies}
                          value={jobForm.company_id}
                          onSelect={(id) => {
                            handleJobFormChange({ target: { name: 'company_id', value: id } });
                          }}
                        />
                      </div>

                      <div>
                        <label style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 8 }}>
                          Company logo <span style={{ color: '#64748b', fontWeight: 500 }}>(optional)</span>
                        </label>
                        <p style={{ margin: '0 0 8px 0', fontSize: 12, color: '#64748b' }}>
                          Shown on the public job share page for this company. Max 2MB, images only.
                        </p>
                        <input
                          ref={jobLogoInputRef}
                          type="file"
                          accept="image/jpeg,image/png,image/gif,image/webp"
                          onChange={(e) => setJobLogoFile(e.target.files?.[0] || null)}
                          style={{ fontSize: 14, width: '100%' }}
                        />
                      </div>

                      {/* Job Title */}
                      <div>
                        <label style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 8 }}>
                          Job Title <span style={{ color: '#ef4444' }}>*</span>
                        </label>
                        <input
                          type="text"
                          name="title"
                          value={jobForm.title}
                          onChange={handleJobFormChange}
                          required
                          placeholder="e.g. Apprentice Electrician"
                          style={{
                            width: '100%',
                            padding: '12px 16px',
                            border: '2px solid #e5e7eb',
                            borderRadius: 8,
                            fontSize: 14,
                            outline: 'none'
                          }}
                        />
                      </div>

                      {/* Location */}
                      <div>
                        <label style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 8 }}>
                          Location <span style={{ color: '#ef4444' }}>*</span>
                        </label>
                        <input
                          type="text"
                          name="location"
                          value={jobForm.location}
                          onChange={handleJobFormChange}
                          required
                          placeholder="e.g. Mumbai, Maharashtra"
                          style={{
                            width: '100%',
                            padding: '12px 16px',
                            border: '2px solid #e5e7eb',
                            borderRadius: 8,
                            fontSize: 14,
                            outline: 'none'
                          }}
                        />
                      </div>

                      {/* Age Criteria and Stipend */}
                      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: 16 }}>
                        <div>
                          <label style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 8 }}>
                            Age Criteria <span style={{ color: '#ef4444' }}>*</span>
                          </label>
                          <input
                            type="text"
                            name="age_criteria"
                            value={jobForm.age_criteria}
                            onChange={handleJobFormChange}
                            required
                            placeholder="e.g. 18-25"
                            style={{
                              width: '100%',
                              padding: '12px 16px',
                              border: '2px solid #e5e7eb',
                              borderRadius: 8,
                              fontSize: 14,
                              outline: 'none'
                            }}
                          />
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 8 }}>
                            Min Stipend (₹) <span style={{ color: '#ef4444' }}>*</span>
                          </label>
                          <input
                            type="number"
                            name="stipend_min"
                            value={jobForm.stipend_min}
                            onChange={handleJobFormChange}
                            required
                            placeholder="5000"
                            min="0"
                            style={{
                              width: '100%',
                              padding: '12px 16px',
                              border: '2px solid #e5e7eb',
                              borderRadius: 8,
                              fontSize: 14,
                              outline: 'none'
                            }}
                          />
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 8 }}>
                            Max Stipend (₹) <span style={{ color: '#ef4444' }}>*</span>
                          </label>
                          <input
                            type="number"
                            name="stipend_max"
                            value={jobForm.stipend_max}
                            onChange={handleJobFormChange}
                            required
                            placeholder="10000"
                            min="0"
                            style={{
                              width: '100%',
                              padding: '12px 16px',
                              border: '2px solid #e5e7eb',
                              borderRadius: 8,
                              fontSize: 14,
                              outline: 'none'
                            }}
                          />
                        </div>
                      </div>

                      {/* Description */}
                      <div>
                        <label style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 8 }}>
                          Job Description <span style={{ color: '#ef4444' }}>*</span>
                        </label>
                        <textarea
                          name="description"
                          value={jobForm.description}
                          onChange={handleJobFormChange}
                          required
                          placeholder="Enter detailed job description..."
                          rows={6}
                          style={{
                            width: '100%',
                            padding: '12px 16px',
                            border: '2px solid #e5e7eb',
                            borderRadius: 8,
                            fontSize: 14,
                            outline: 'none',
                            resize: 'vertical',
                            fontFamily: 'inherit'
                          }}
                        />
                      </div>

                      {/* Qualifications */}
                      <div>
                        <label style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 12 }}>
                          Qualification Requirements <span style={{ color: '#ef4444' }}>*</span>
                        </label>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                          {['10th pass', '12th pass', 'ITI', 'Diploma', 'Graduate'].map(qual => (
                            <button
                              key={qual}
                              type="button"
                              onClick={() => handleQualificationChange(qual)}
                              style={{
                                padding: '8px 16px',
                                border: `2px solid ${jobForm.qualification.includes(qual) ? '#667eea' : '#e5e7eb'}`,
                                borderRadius: 20,
                                fontSize: 13,
                                fontWeight: 600,
                                background: jobForm.qualification.includes(qual) ? '#667eea' : '#fff',
                                color: jobForm.qualification.includes(qual) ? '#fff' : '#374151',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease'
                              }}
                            >
                              {qual}
                            </button>
                          ))}
                        </div>
                        {jobForm.qualification.length === 0 && (
                          <p style={{ fontSize: 12, color: '#ef4444', marginTop: 8, margin: 0 }}>
                            Please select at least one qualification
                          </p>
                        )}
                      </div>

                      {/* Error and Success Messages */}
                      {jobFormError && (
                        <div style={{
                          padding: '12px 16px',
                          background: '#fee2e2',
                          border: '1px solid #fecaca',
                          borderRadius: 8,
                          color: '#991b1b',
                          fontSize: 14
                        }}>
                          {jobFormError}
                        </div>
                      )}
                      {jobFormSuccess && (
                        <div style={{
                          padding: '12px 16px',
                          background: '#dcfce7',
                          border: '1px solid #bbf7d0',
                          borderRadius: 8,
                          color: '#166534',
                          fontSize: 14
                        }}>
                          {jobFormSuccess}
                        </div>
                      )}

                      {/* Submit Buttons */}
                      <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 8 }}>
                        <button
                          type="button"
                          onClick={() => {
                            setShowJobForm(false);
                            setJobFormError('');
                            setJobFormSuccess('');
                            setJobLogoFile(null);
                            if (jobLogoInputRef.current) jobLogoInputRef.current.value = '';
                            setJobForm({
                              title: '',
                              description: '',
                              age_criteria: '',
                              stipend_min: '',
                              stipend_max: '',
                              qualification: [],
                              location: '',
                              company_id: ''
                            });
                          }}
                          style={{
                            padding: '12px 24px',
                            background: '#f3f4f6',
                            color: '#374151',
                            border: 'none',
                            borderRadius: 8,
                            fontSize: 14,
                            fontWeight: 600,
                            cursor: 'pointer'
                          }}
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          disabled={submittingJob}
                          style={{
                            padding: '12px 24px',
                            background: submittingJob ? '#9ca3af' : '#667eea',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 8,
                            fontSize: 14,
                            fontWeight: 600,
                            cursor: submittingJob ? 'not-allowed' : 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 8
                          }}
                        >
                          {submittingJob ? 'Posting...' : 'Post Job'}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'supervisors' && (
            <div style={{ marginTop: 32 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16, marginBottom: 24 }}>
                <h2 style={{ color: '#667eea', fontWeight: 700, margin: 0 }}>Supervisors</h2>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <input
                    type="text"
                    placeholder="Search by name, email, mobile..."
                    value={supervisorListSearch}
                    onChange={(e) => setSupervisorListSearch(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { setSupervisorListPage(1); fetchSupervisors(); } }}
                    style={{
                      padding: '8px 14px',
                      borderRadius: 8,
                      border: '1px solid #e2e8f0',
                      fontSize: 14,
                      minWidth: 220
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => { setSupervisorListPage(1); fetchSupervisors(); }}
                    style={{
                      padding: '8px 16px',
                      borderRadius: 8,
                      border: '1px solid #e2e8f0',
                      background: '#fff',
                      fontWeight: 500,
                      cursor: 'pointer',
                      fontSize: 14
                    }}
                  >
                    Search
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowAddSupervisorModal(true)}
                    className="gogig-card-btn"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '10px 20px',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 12,
                      fontWeight: 600,
                      fontSize: 14,
                      cursor: 'pointer',
                      boxShadow: '0 2px 8px rgba(102, 126, 234, 0.4)'
                    }}
                  >
                    <FaPlus /> Add Supervisor
                  </button>
                </div>
              </div>

              {supervisorsLoading ? (
                <div style={{ textAlign: 'center', padding: 40 }}>
                  <div style={{
                    width: 40,
                    height: 40,
                    border: '4px solid #e5e7eb',
                    borderTop: '4px solid #667eea',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    margin: '0 auto 16px'
                  }} />
                  <p style={{ color: '#6b7280' }}>Loading supervisors...</p>
                </div>
              ) : supervisors.length > 0 ? (
                <div>
                  {isMobile ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                      {supervisors.map((supervisor) => (
                        <div key={supervisor.id} style={{
                          background: '#fff',
                          border: '1px solid #e5e7eb',
                          borderRadius: 12,
                          padding: 16,
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}>
                          <h4 style={{ fontSize: 16, fontWeight: 600, color: '#374151', margin: '0 0 8px 0' }}>
                            {supervisor.name}
                          </h4>
                          <p style={{ fontSize: 14, color: '#6b7280', margin: '0 0 4px 0' }}>
                            {supervisor.email}
                          </p>
                          <p style={{ fontSize: 14, color: '#6b7280', margin: '0 0 8px 0' }}>
                            {supervisor.mobile}
                          </p>
                          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                            <span style={{
                              padding: '4px 12px',
                              borderRadius: 16,
                              fontSize: 12,
                              fontWeight: 600,
                              background: supervisor.status === 'active' ? '#dcfce7' : '#fee2e2',
                              color: supervisor.status === 'active' ? '#166534' : '#991b1b'
                            }}>
                              {supervisor.status}
                            </span>
                            {supervisor.role && (
                              <span style={{
                                padding: '4px 12px',
                                borderRadius: 16,
                                fontSize: 12,
                                fontWeight: 600,
                                background: '#dbeafe',
                                color: '#1e40af'
                              }}>
                                {supervisor.role}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ background: '#fff', borderRadius: 12, overflow: 'hidden', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ background: '#f8fafc' }}>
                            <th style={{ padding: '16px', textAlign: 'left', fontSize: 14, fontWeight: 600, color: '#374151' }}>Name</th>
                            <th style={{ padding: '16px', textAlign: 'left', fontSize: 14, fontWeight: 600, color: '#374151' }}>Email</th>
                            <th style={{ padding: '16px', textAlign: 'left', fontSize: 14, fontWeight: 600, color: '#374151' }}>Mobile</th>
                            <th style={{ padding: '16px', textAlign: 'left', fontSize: 14, fontWeight: 600, color: '#374151' }}>Status</th>
                            <th style={{ padding: '16px', textAlign: 'left', fontSize: 14, fontWeight: 600, color: '#374151' }}>Role</th>
                            <th style={{ padding: '16px', textAlign: 'left', fontSize: 14, fontWeight: 600, color: '#374151' }}>Created</th>
                          </tr>
                        </thead>
                        <tbody>
                          {supervisors.map((supervisor) => (
                            <tr key={supervisor.id} style={{ borderTop: '1px solid #e5e7eb' }}>
                              <td style={{ padding: '16px', fontSize: 14, color: '#374151', fontWeight: 600 }}>{supervisor.name}</td>
                              <td style={{ padding: '16px', fontSize: 14, color: '#6b7280' }}>{supervisor.email}</td>
                              <td style={{ padding: '16px', fontSize: 14, color: '#6b7280' }}>{supervisor.mobile}</td>
                              <td style={{ padding: '16px' }}>
                                <span style={{
                                  padding: '4px 12px',
                                  borderRadius: 16,
                                  fontSize: 12,
                                  fontWeight: 600,
                                  background: supervisor.status === 'active' ? '#dcfce7' : '#fee2e2',
                                  color: supervisor.status === 'active' ? '#166534' : '#991b1b'
                                }}>
                                  {supervisor.status}
                                </span>
                              </td>
                              <td style={{ padding: '16px', fontSize: 14, color: '#6b7280' }}>
                                {supervisor.role || 'N/A'}
                              </td>
                              <td style={{ padding: '16px', fontSize: 14, color: '#6b7280' }}>
                                {new Date(supervisor.created_at).toLocaleDateString()}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>
                  <FaUsers style={{ fontSize: 48, marginBottom: 16, opacity: 0.5 }} />
                  <p>No supervisors assigned to you yet.</p>
                  <button
                    type="button"
                    onClick={() => setShowAddSupervisorModal(true)}
                    className="gogig-card-btn"
                    style={{ marginTop: 16, display: 'inline-flex', alignItems: 'center', gap: 8 }}
                  >
                    <FaPlus /> Add Supervisor
                  </button>
                </div>
              )}

              {/* Supervisor list pagination */}
              {activeTab === 'supervisors' && supervisorListTotal > 0 && (
                <div className="admin-pagination-wrap" style={{ marginTop: 20 }}>
                  <span className="admin-pagination-info">
                    Showing {((supervisorListPage - 1) * SUPERVISOR_LIST_PAGE_SIZE) + 1}–{Math.min(supervisorListPage * SUPERVISOR_LIST_PAGE_SIZE, supervisorListTotal)} of {supervisorListTotal} supervisors
                  </span>
                  <div className="admin-pagination-btns">
                    <button
                      type="button"
                      className="admin-pagination-btn primary"
                      onClick={() => setSupervisorListPage(p => Math.max(1, p - 1))}
                      disabled={supervisorListPage <= 1}
                    >
                      Previous
                    </button>
                    <span className="admin-pagination-info">
                      Page {supervisorListPage} of {supervisorListTotalPages}
                    </span>
                    <button
                      type="button"
                      className="admin-pagination-btn primary"
                      onClick={() => setSupervisorListPage(p => Math.min(supervisorListTotalPages, p + 1))}
                      disabled={supervisorListPage >= supervisorListTotalPages}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}

              {/* Add Supervisor Modal */}
              {showAddSupervisorModal && (
                <div
                  style={{
                    position: 'fixed',
                    inset: 0,
                    background: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 9999,
                    padding: 24
                  }}
                  onClick={() => !addSupervisorSubmitting && setShowAddSupervisorModal(false)}
                >
                  <div
                    style={{
                      background: '#fff',
                      borderRadius: 16,
                      padding: 28,
                      maxWidth: 440,
                      width: '100%',
                      boxShadow: '0 20px 60px rgba(0,0,0,0.2)'
                    }}
                    onClick={e => e.stopPropagation()}
                  >
                    <h3 style={{ margin: '0 0 20px', color: '#374151', fontSize: 20 }}>Add Supervisor</h3>
                    <form onSubmit={handleAddSupervisor}>
                      <div style={{ marginBottom: 16 }}>
                        <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, color: '#374151', fontSize: 14 }}>Name *</label>
                        <input
                          type="text"
                          value={addSupervisorForm.name}
                          onChange={e => setAddSupervisorForm(f => ({ ...f, name: e.target.value }))}
                          placeholder="Full name"
                          className="gogig-input"
                          style={{ width: '100%', padding: '10px 14px', borderRadius: 10 }}
                          required
                        />
                      </div>
                      <div style={{ marginBottom: 16 }}>
                        <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, color: '#374151', fontSize: 14 }}>Email *</label>
                        <input
                          type="email"
                          value={addSupervisorForm.email}
                          onChange={e => setAddSupervisorForm(f => ({ ...f, email: e.target.value }))}
                          placeholder="email@example.com"
                          className="gogig-input"
                          style={{ width: '100%', padding: '10px 14px', borderRadius: 10 }}
                          required
                        />
                      </div>
                      <div style={{ marginBottom: 16 }}>
                        <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, color: '#374151', fontSize: 14 }}>Mobile *</label>
                        <input
                          type="tel"
                          value={addSupervisorForm.mobile}
                          onChange={e => setAddSupervisorForm(f => ({ ...f, mobile: e.target.value }))}
                          placeholder="10-digit mobile"
                          className="gogig-input"
                          style={{ width: '100%', padding: '10px 14px', borderRadius: 10 }}
                          required
                        />
                      </div>
                      <div style={{ marginBottom: 16 }}>
                        <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, color: '#374151', fontSize: 14 }}>Password *</label>
                        <input
                          type="password"
                          value={addSupervisorForm.password}
                          onChange={e => setAddSupervisorForm(f => ({ ...f, password: e.target.value }))}
                          placeholder="Login password"
                          className="gogig-input"
                          style={{ width: '100%', padding: '10px 14px', borderRadius: 10 }}
                          required
                          minLength={6}
                        />
                      </div>
                      {companies.length > 0 && (
                        <div style={{ marginBottom: 20 }}>
                          <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, color: '#374151', fontSize: 14 }}>Company (optional)</label>
                          <select
                            value={addSupervisorForm.company_id}
                            onChange={e => setAddSupervisorForm(f => ({ ...f, company_id: e.target.value }))}
                            className="gogig-input"
                            style={{ width: '100%', padding: '10px 14px', borderRadius: 10 }}
                          >
                            <option value="">Default (first company)</option>
                            {companies.map(c => (
                              <option key={c.id} value={c.id}>{c.company_name}</option>
                            ))}
                          </select>
                        </div>
                      )}
                      <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 24 }}>
                        <button
                          type="button"
                          onClick={() => !addSupervisorSubmitting && setShowAddSupervisorModal(false)}
                          style={{ padding: '10px 20px', borderRadius: 10, border: '1px solid #e5e7eb', background: '#fff', cursor: addSupervisorSubmitting ? 'not-allowed' : 'pointer' }}
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          disabled={addSupervisorSubmitting}
                          className="gogig-card-btn"
                          style={{
                            padding: '10px 24px',
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 10,
                            fontWeight: 600,
                            cursor: addSupervisorSubmitting ? 'not-allowed' : 'pointer'
                          }}
                        >
                          {addSupervisorSubmitting ? 'Adding...' : 'Add Supervisor'}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'documents' && (
            <div style={{ padding: 24, background: 'rgba(255,255,255,0.5)', borderRadius: 24, boxShadow: '0 4px 32px #22c55e11', maxWidth: 900, margin: '0 auto', marginTop: 32 }}>
              {/* Modern Tabs */}
              <div style={{ display: 'flex', gap: 24, marginBottom: 32, justifyContent: 'center', flexWrap: 'wrap' }}>
                <button
                  className="gogig-card-btn"
                  style={{
                    borderRadius: 32,
                    padding: '12px 36px',
                    fontWeight: 700,
                    fontSize: 18,
                    background: docImportTab === 'naps' ? 'linear-gradient(90deg, #22c55e 0%, #2ad2c9 100%)' : 'rgba(255,255,255,0.7)',
                    color: docImportTab === 'naps' ? '#fff' : '#22c55e',
                    boxShadow: docImportTab === 'naps' ? '0 2px 16px #22c55e44' : '0 1.5px 8px #2ad2c911',
                    border: docImportTab === 'naps' ? 'none' : '1.5px solid #22c55e',
                    transition: 'all 0.18s cubic-bezier(.4,2,.6,1)',
                    outline: 'none',
                    cursor: 'pointer',
                    backdropFilter: 'blur(8px)',
                  }}
                  onClick={() => setDocImportTab('naps')}
                >
                  NAPS Documents
                </button>
                <button
                  className="gogig-card-btn"
                  style={{
                    borderRadius: 32,
                    padding: '12px 36px',
                    fontWeight: 700,
                    fontSize: 18,
                    background: docImportTab === 'bvoc' ? 'linear-gradient(90deg, #22c55e 0%, #2ad2c9 100%)' : 'rgba(255,255,255,0.7)',
                    color: docImportTab === 'bvoc' ? '#fff' : '#22c55e',
                    boxShadow: docImportTab === 'bvoc' ? '0 2px 16px #22c55e44' : '0 1.5px 8px #2ad2c911',
                    border: docImportTab === 'bvoc' ? 'none' : '1.5px solid #22c55e',
                    transition: 'all 0.18s cubic-bezier(.4,2,.6,1)',
                    outline: 'none',
                    cursor: 'pointer',
                    backdropFilter: 'blur(8px)',
                  }}
                  onClick={() => setDocImportTab('bvoc')}
                >
                  B.Voc Documents
                </button>
                <button
                  className="gogig-card-btn"
                  style={{
                    borderRadius: 32,
                    padding: '12px 36px',
                    fontWeight: 700,
                    fontSize: 18,
                    background: docImportTab === 'salaried' ? 'linear-gradient(90deg, #22c55e 0%, #2ad2c9 100%)' : 'rgba(255,255,255,0.7)',
                    color: docImportTab === 'salaried' ? '#fff' : '#22c55e',
                    boxShadow: docImportTab === 'salaried' ? '0 2px 16px #22c55e44' : '0 1.5px 8px #2ad2c911',
                    border: docImportTab === 'salaried' ? 'none' : '1.5px solid #22c55e',
                    transition: 'all 0.18s cubic-bezier(.4,2,.6,1)',
                    outline: 'none',
                    cursor: 'pointer',
                    backdropFilter: 'blur(8px)',
                  }}
                  onClick={() => setDocImportTab('salaried')}
                >
                  Salaried Employee Documents
                </button>
                <button
                  className="gogig-card-btn"
                  style={{
                    borderRadius: 32,
                    padding: '12px 36px',
                    fontWeight: 700,
                    fontSize: 18,
                    background: docImportTab === 'nats' ? 'linear-gradient(90deg, #22c55e 0%, #2ad2c9 100%)' : 'rgba(255,255,255,0.7)',
                    color: docImportTab === 'nats' ? '#fff' : '#22c55e',
                    boxShadow: docImportTab === 'nats' ? '0 2px 16px #22c55e44' : '0 1.5px 8px #2ad2c911',
                    border: docImportTab === 'nats' ? 'none' : '1.5px solid #22c55e',
                    transition: 'all 0.18s cubic-bezier(.4,2,.6,1)',
                    outline: 'none',
                    cursor: 'pointer',
                    backdropFilter: 'blur(8px)',
                  }}
                  onClick={() => setDocImportTab('nats')}
                >
                  NATS Documents
                </button>
              </div>

              {/* Modern Card Form */}
              <form onSubmit={handleCsvUpload} style={{
                marginBottom: 32,
                background: 'rgba(255,255,255,0.85)',
                padding: 36,
                borderRadius: 24,
                boxShadow: '0 4px 24px #22c55e11',
                maxWidth: 600,
                margin: '0 auto',
                display: 'flex',
                flexDirection: 'column',
                gap: 28,
                border: '1.5px solid #e0f7fa',
                backdropFilter: 'blur(8px)',
              }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24 }}>
                  <div style={{ flex: 1, minWidth: 180 }}>
                    <label style={{ fontWeight: 600, color: '#22c55e', marginBottom: 6, fontSize: 15, display: 'block' }}>Document Type</label>
                    <select 
                      name="document_type" 
                      className="gogig-input" 
                      required 
                      value={selectedDocumentType}
                      onChange={(e) => setSelectedDocumentType(e.target.value)}
                      style={{ width: '100%', borderRadius: 16, padding: '10px 16px', fontSize: 16, border: '1.5px solid #e0f7fa', background: '#f8fafc' }}
                    >
                      <option value="">Select Document Type</option>
                      {docImportTab === 'naps' && (
                        <>
                          <option value="contract">Contract</option>
                          <option value="stipend">Stipend</option>
                          <option value="dbt">DBT</option>
                        </>
                      )}
                      {docImportTab === 'bvoc' && (
                        <option value="bvoc_salary">Stipend</option>
                      )}
                      {docImportTab === 'salaried' && (
                        <option value="salary">Salary Slip</option>
                      )}
                      {docImportTab === 'nats' && (
                        <option value="nats_stipend">NATS Stipend</option>
                      )}
                    </select>
                  </div>
                  {/* Year and Month fields for all document types */}
                  <div style={{ flex: 1, minWidth: 120 }}>
                    <label style={{ fontWeight: 600, color: '#22c55e', marginBottom: 6, fontSize: 15, display: 'block' }}>Year</label>
                    <input type="number" name="year" className="gogig-input" required min="2000" max="2100" style={{ width: '100%', borderRadius: 16, padding: '10px 16px', fontSize: 16, border: '1.5px solid #e0f7fa', background: '#f8fafc' }} value={stipendReportYear} onChange={e => setStipendReportYear(Number(e.target.value))} />
                  </div>
                  <div style={{ flex: 1, minWidth: 120 }}>
                    <label style={{ fontWeight: 600, color: '#22c55e', marginBottom: 6, fontSize: 15, display: 'block' }}>Month</label>
                    <select name="month" className="gogig-input" required style={{ width: '100%', borderRadius: 16, padding: '10px 16px', fontSize: 16, border: '1.5px solid #e0f7fa', background: '#f8fafc' }} value={stipendReportMonth} onChange={e => setStipendReportMonth(Number(e.target.value))}>
                      <option value="1">January</option>
                      <option value="2">February</option>
                      <option value="3">March</option>
                      <option value="4">April</option>
                      <option value="5">May</option>
                      <option value="6">June</option>
                      <option value="7">July</option>
                      <option value="8">August</option>
                      <option value="9">September</option>
                      <option value="10">October</option>
                      <option value="11">November</option>
                      <option value="12">December</option>
                    </select>
                  </div>
                </div>
                {/* Establishment ID Dropdown - Required for stipend, DBT, BVOC, and NATS uploads */}
                {((docImportTab === 'naps' && selectedDocumentType !== 'contract') || docImportTab === 'bvoc' || docImportTab === 'nats') && (
                  <div>
                    <label style={{ fontWeight: 600, color: '#22c55e', marginBottom: 6, fontSize: 15, display: 'block' }}>Establishment ID *</label>
                    <EstablishmentDropdown 
                      establishments={
                        docImportTab === 'naps'
                          ? napsEstablishmentIds
                          : docImportTab === 'nats'
                            ? natsEstablishmentIds
                            : bvocEstablishmentIds
                      }
                      onSelect={(value, establishment) => {
                        setSelectedEstablishmentCode(value);
                        if (establishment && establishment.companyName) {
                          setCompanyNameInput(establishment.companyName);
                        }
                      }}
                    />
                    {/* Hidden select for form submission */}
                    <select 
                      name="establishment_code" 
                      value={selectedEstablishmentCode}
                      onChange={(e) => setSelectedEstablishmentCode(e.target.value)}
                      style={{ display: 'none' }}
                    />
                  </div>
                )}
                {/* Company Dropdown - For all document types (searchable) */}
                <div>
                  <label style={{ fontWeight: 600, color: '#22c55e', marginBottom: 6, fontSize: 15, display: 'block' }}>Company *</label>
                  <CompanyDropdown
                    companies={companies}
                    value={selectedCompanyId}
                    onSelect={(id, company) => {
                      setSelectedCompanyId(id);
                      if (company?.company_name) {
                        setCompanyNameInput(company.company_name);
                      }
                      // Prefer this company's establishment code when available
                      if (company?.establishment_code) {
                        setSelectedEstablishmentCode(company.establishment_code);
                      }
                    }}
                  />
                  <select
                    name="company_id"
                    value={selectedCompanyId}
                    required
                    onChange={() => {}}
                    style={{ display: 'none' }}
                    aria-hidden="true"
                  >
                    <option value="">Select Company</option>
                    {companies.map(company => (
                      <option key={company.id} value={company.id}>
                        {company.company_name}
                      </option>
                    ))}
                  </select>
                </div>
                
                {/* Company Name Input - Hidden: We now use the selected company's name automatically */}
                {/* {docImportTab === 'naps' && selectedDocumentType === 'contract' && (
                  <div>
                    <label style={{ fontWeight: 600, color: '#22c55e', marginBottom: 6, fontSize: 15, display: 'block' }}>Company Name (if different from above)</label>
                    <input 
                      type="text" 
                      name="company_name" 
                      className="gogig-input" 
                      value={companyNameInput}
                      onChange={(e) => setCompanyNameInput(e.target.value)}
                      style={{ width: '100%', borderRadius: 16, padding: '10px 16px', fontSize: 16, border: '1.5px solid #e0f7fa', background: '#f8fafc' }}
                      placeholder="Enter company name (optional)"
                    />
                  </div>
                )} */}
                {/* Download Template Buttons */}
                {docImportTab === 'salaried' && (
                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                    <button
                      type="button"
                      onClick={() => {
                        const { API_URL } = getApiConfig();
                        const link = document.createElement('a');
                        link.href = `${API_URL}/api/documents/templates/salaried-salary`;
                        link.download = 'salaried_salary_slip_template.csv';
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        toast.success('Salary slip template downloaded');
                      }}
                      style={{
                        padding: '10px 20px',
                        background: 'linear-gradient(135deg, #22c55e 0%, #059669 100%)',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 12,
                        fontSize: 14,
                        fontWeight: 600,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        boxShadow: '0 4px 15px rgba(34, 197, 94, 0.3)',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseOver={(e) => e.target.style.transform = 'translateY(-2px)'}
                      onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
                    >
                      <FaDownload />
                      Download Salary Slip Template
                    </button>
                    <span style={{ fontSize: 13, color: '#64748b' }}>Use this CSV format for salaried salary slip upload</span>
                  </div>
                )}
                {docImportTab === 'nats' && (
                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                    <button
                      type="button"
                      onClick={() => {
                        const { API_URL } = getApiConfig();
                        const link = document.createElement('a');
                        link.href = `${API_URL}/api/documents/templates/nats-stipend`;
                        link.download = 'nats_stipend_template.csv';
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        toast.success('Template downloaded successfully');
                      }}
                      style={{
                        padding: '10px 20px',
                        background: 'linear-gradient(135deg, #22c55e 0%, #059669 100%)',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 12,
                        fontSize: 14,
                        fontWeight: 600,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        boxShadow: '0 4px 15px rgba(34, 197, 94, 0.3)',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseOver={(e) => e.target.style.transform = 'translateY(-2px)'}
                      onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
                    >
                      <FaDownload />
                      Download NATS Stipend Template
                    </button>
                    <span style={{ fontSize: 13, color: '#64748b' }}>Same CSV format as NAPS stipend</span>
                  </div>
                )}
                {docImportTab === 'naps' && selectedDocumentType && (
                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                    {selectedDocumentType === 'stipend' && (
                      <button
                        type="button"
                        onClick={() => {
                          const { API_URL } = getApiConfig();
                          const link = document.createElement('a');
                          link.href = `${API_URL}/api/documents/templates/naps-stipend`;
                          link.download = 'naps_stipend_template.csv';
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                          toast.success('Template downloaded successfully');
                        }}
                        style={{
                          padding: '10px 20px',
                          background: 'linear-gradient(135deg, #22c55e 0%, #059669 100%)',
                          color: '#fff',
                          border: 'none',
                          borderRadius: 12,
                          fontSize: 14,
                          fontWeight: 600,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          boxShadow: '0 4px 15px rgba(34, 197, 94, 0.3)',
                          transition: 'all 0.3s ease'
                        }}
                        onMouseOver={(e) => e.target.style.transform = 'translateY(-2px)'}
                        onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
                      >
                        <FaDownload />
                        Download Stipend Template
                      </button>
                    )}
                    {selectedDocumentType === 'contract' && (
                      <button
                        type="button"
                        onClick={() => {
                          const { API_URL } = getApiConfig();
                          const link = document.createElement('a');
                          link.href = `${API_URL}/api/documents/templates/naps-contract`;
                          link.download = 'naps_contract_template.csv';
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                          toast.success('Template downloaded successfully');
                        }}
                        style={{
                          padding: '10px 20px',
                          background: 'linear-gradient(135deg, #22c55e 0%, #059669 100%)',
                          color: '#fff',
                          border: 'none',
                          borderRadius: 12,
                          fontSize: 14,
                          fontWeight: 600,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          boxShadow: '0 4px 15px rgba(34, 197, 94, 0.3)',
                          transition: 'all 0.3s ease'
                        }}
                        onMouseOver={(e) => e.target.style.transform = 'translateY(-2px)'}
                        onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
                      >
                        <FaDownload />
                        Download Contract Template
                      </button>
                    )}
                  </div>
                )}
                <div>
                  <label style={{ fontWeight: 600, color: '#22c55e', marginBottom: 6, fontSize: 15, display: 'block' }}>Upload CSV</label>
                  <input type="file" name="file" accept=".csv" required className="gogig-input" style={{ padding: 10, borderRadius: 16, border: '1.5px solid #e0f7fa', background: '#f8fafc', width: '100%' }} />
                </div>
                <button type="submit" className="gogig-card-btn" disabled={uploading} style={{
                  borderRadius: 32,
                  padding: '14px 0',
                  fontWeight: 800,
                  fontSize: 20,
                  background: 'linear-gradient(90deg, #22c55e 0%, #2ad2c9 100%)',
                  color: '#fff',
                  boxShadow: '0 2px 16px #22c55e44',
                  border: 'none',
                  marginTop: 8,
                  transition: 'background 0.18s, transform 0.18s',
                  outline: 'none',
                  cursor: uploading ? 'not-allowed' : 'pointer',
                  width: '100%',
                  letterSpacing: 1,
                }}>
                  {uploading ? 'Uploading...' : 'Upload'}
                </button>
                {uploadResult && (
                  <div style={{
                    marginTop: 8,
                    padding: 12,
                    borderRadius: 12,
                    background: uploadResult.success ? 'linear-gradient(90deg, #dcfce7 0%, #bbf7d0 100%)' : 'linear-gradient(90deg, #fee2e2 0%, #fecaca 100%)',
                    color: uploadResult.success ? '#166534' : '#991b1b',
                    fontWeight: 700,
                    fontSize: 16,
                    textAlign: 'center',
                    boxShadow: '0 2px 8px #22c55e22',
                  }}>
                    {uploadResult.success
                      ? `Successfully imported ${uploadResult.imported} records`
                      : `Error: ${uploadResult.message}`}
                  </div>
                )}
              </form>
              
              {/* Upload History Card */}
              <div style={{ marginTop: 40, background: 'rgba(255,255,255,0.85)', borderRadius: 20, boxShadow: '0 4px 24px #22c55e11', padding: 24, border: '1.5px solid #e0f7fa', maxWidth: 800, margin: '0 auto' }}>
                <h3 style={{ fontSize: 20, fontWeight: 800, marginBottom: 18, color: '#22c55e', letterSpacing: 0.5 }}>Upload History</h3>
                <div style={{ background: 'transparent', borderRadius: 12, overflow: 'hidden', border: 'none' }}>
                  {csvHistory.length === 0 ? (
                    <div style={{ padding: 16, color: '#666', textAlign: 'center' }}>No upload history</div>
                  ) : (
                    <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, background: 'transparent', borderRadius: 12, fontSize: 16, fontWeight: 500, color: '#222', boxShadow: 'none' }}>
                      <thead style={{ position: 'sticky', top: 0, zIndex: 2, background: 'linear-gradient(90deg, #e0f7fa 0%, #f8fafc 100%)' }}>
                        <tr>
                          <th style={{ padding: 14, textAlign: 'left', fontWeight: 800, color: '#22c55e', fontSize: 15, letterSpacing: 0.5 }}>File Name</th>
                          <th style={{ padding: 14, textAlign: 'left', fontWeight: 800, color: '#22c55e', fontSize: 15, letterSpacing: 0.5 }}>Type</th>
                          <th style={{ padding: 14, textAlign: 'left', fontWeight: 800, color: '#22c55e', fontSize: 15, letterSpacing: 0.5 }}>Date</th>
                          <th style={{ padding: 14, textAlign: 'left', fontWeight: 800, color: '#22c55e', fontSize: 15, letterSpacing: 0.5 }}>Records</th>
                        </tr>
                      </thead>
                      <tbody>
                        {csvHistory.map((item, i) => (
                          <tr key={i} style={{ background: i % 2 === 0 ? 'rgba(34,197,94,0.06)' : 'rgba(42,210,201,0.06)', transition: 'background 0.2s', cursor: 'pointer' }} onMouseOver={e => e.currentTarget.style.background='rgba(34,197,94,0.13)'} onMouseOut={e => e.currentTarget.style.background=i%2===0?'rgba(34,197,94,0.06)':'rgba(42,210,201,0.06)'}>
                            <td style={{ padding: 14 }}>{item.fileName}</td>
                            <td style={{ padding: 14 }}>{item.type}</td>
                            <td style={{ padding: 14 }}>{item.date}</td>
                            <td style={{ padding: 14 }}>{item.imported}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

      {/* Document Management Modal */}
      {showDocumentModal && selectedCandidate && (
        <div className="gogig-modal-bg" onClick={() => setShowDocumentModal(false)}>
          <div className="gogig-modal" style={{ 
            minWidth: isMobile ? '100%' : 800, 
            maxWidth: isMobile ? '100%' : 1000, 
            maxHeight: isMobile ? '100vh' : '90vh', 
            overflowY: 'auto',
            background: '#fff',
            borderRadius: isMobile ? 0 : 16,
            boxShadow: isMobile ? 'none' : '0 20px 60px rgba(0, 0, 0, 0.3)',
            margin: isMobile ? 0 : 'auto',
            position: isMobile ? 'fixed' : 'relative',
            top: isMobile ? 0 : 'auto',
            left: isMobile ? 0 : 'auto',
            right: isMobile ? 0 : 'auto',
            bottom: isMobile ? 0 : 'auto'
          }} onClick={e => e.stopPropagation()}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              padding: isMobile ? '16px 20px' : '24px 24px 0 24px',
              borderBottom: '1px solid #e5e7eb',
              marginBottom: isMobile ? 16 : 24,
              background: isMobile ? '#f8fafc' : 'transparent',
              borderRadius: isMobile ? '0 0 12px 12px' : 0
            }}>
              <h3 style={{ 
                fontSize: isMobile ? 16 : 24, 
                fontWeight: 700, 
                color: '#667eea', 
                margin: 0,
                display: 'flex',
                alignItems: 'center',
                gap: isMobile ? 8 : 12,
                flex: 1
              }}>
                <FaFileAlt style={{ fontSize: isMobile ? 16 : 20 }} />
                <span style={{ fontSize: isMobile ? 14 : 'inherit' }}>
                  {isMobile ? 'Documents' : `Manage Documents: ${selectedCandidate.name}`}
                </span>
              </h3>
              <button
                onClick={() => setShowDocumentModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: isMobile ? 18 : 24,
                  cursor: 'pointer',
                  color: '#6b7280',
                  padding: isMobile ? 8 : 4,
                  borderRadius: isMobile ? 6 : 0
                }}
              >
                <FaTimes />
              </button>
            </div>

            <div style={{ padding: isMobile ? '0 20px 20px 20px' : '0 24px 24px 24px' }}>
              {/* Document Upload Form */}
              <div style={{ 
                background: isMobile ? '#fff' : '#f8fafc', 
                padding: isMobile ? 16 : 20, 
                borderRadius: isMobile ? 8 : 12, 
                marginBottom: isMobile ? 16 : 24,
                border: isMobile ? '1px solid #e5e7eb' : '1px solid #e5e7eb',
                boxShadow: isMobile ? '0 2px 8px rgba(0,0,0,0.1)' : 'none'
              }}>
                <h4 style={{ 
                  fontSize: isMobile ? 16 : 16, 
                  fontWeight: 600, 
                  color: '#374151', 
                  margin: '0 0 16px 0',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8
                }}>
                  <FaUpload style={{ color: '#667eea', fontSize: isMobile ? 14 : 16 }} />
                  Upload New Document
                </h4>
                
                <form onSubmit={handleDocumentUpload} style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 20 : 16 }}>
                  <div>
                    <label style={{ 
                      display: 'block', 
                      fontSize: isMobile ? 14 : 14, 
                      fontWeight: 600, 
                      color: '#374151', 
                      marginBottom: isMobile ? 12 : 8 
                    }}>
                      Document Type *
                    </label>
                    <select
                      value={documentForm.document_type}
                      onChange={e => setDocumentForm({ ...documentForm, document_type: e.target.value })}
                      style={{
                        width: '100%',
                        padding: isMobile ? '16px 20px' : '12px 16px',
                        border: '2px solid #e5e7eb',
                        borderRadius: isMobile ? 12 : 8,
                        fontSize: isMobile ? 16 : 14,
                        background: '#fff',
                        transition: 'border-color 0.2s ease',
                        outline: 'none',
                        appearance: 'none',
                        backgroundImage: 'url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'currentColor\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3e%3cpolyline points=\'6,9 12,15 18,9\'%3e%3c/polyline%3e%3c/svg%3e")',
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right 12px center',
                        backgroundSize: '16px',
                        paddingRight: '40px'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#667eea'}
                      onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                      required
                    >
                      <option value="">Select Document Type *</option>
                      <option value="aadhar">🆔 Aadhar Card</option>
                      <option value="school_certificate">🎓 School Certificate</option>
                      <option value="educational_certificate">📜 Educational Certificate (Graduate/ITI/Diploma)</option>
                      <option value="bank_passbook">🏦 Bank Passbook (Front Page)</option>
                      <option value="candidate_image">📷 Candidate Photo</option>
                      <option value="pancard">🪪 PAN Card</option>
                      <option value="signature">✍️ Signature</option>
                    </select>
                  </div>

                  <div>
                    <label style={{ 
                      display: 'block', 
                      fontSize: isMobile ? 14 : 14, 
                      fontWeight: 600, 
                      color: '#374151', 
                      marginBottom: isMobile ? 12 : 8 
                    }}>
                      Document File *
                    </label>
                    
                    {isMobile ? (
                      // Mobile-optimized file upload
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                        <div style={{
                          border: '2px dashed #d1d5db',
                          borderRadius: 12,
                          padding: 20,
                          textAlign: 'center',
                          background: '#f9fafb',
                          transition: 'all 0.2s ease'
                        }}>
                          <input
                            type="file"
                            accept="image/*,.pdf"
                            onChange={e => setDocumentForm({ ...documentForm, file: e.target.files[0] })}
                            style={{
                              position: 'absolute',
                              opacity: 0,
                              width: '100%',
                              height: '100%',
                              cursor: 'pointer'
                            }}
                            required
                          />
                          <div style={{ pointerEvents: 'none' }}>
                            <FaFileAlt style={{ fontSize: 32, color: '#9ca3af', marginBottom: 8 }} />
                            <div style={{ fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 4 }}>
                              {documentForm.file ? documentForm.file.name : 'Tap to choose file'}
                            </div>
                            <div style={{ fontSize: 12, color: '#6b7280' }}>
                              or use camera to scan
                            </div>
                          </div>
                        </div>
                        
                        <button
                          type="button"
                          onClick={startCamera}
                          style={{
                            padding: '16px 20px',
                            background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 12,
                            fontSize: 16,
                            fontWeight: 600,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 12,
                            transition: 'all 0.2s ease',
                            boxShadow: '0 4px 16px rgba(14,165,233,0.3)'
                          }}
                          onMouseOver={(e) => e.target.style.transform = 'translateY(-2px)'}
                          onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
                        >
                          <FaCamera style={{ fontSize: 18 }} />
                          Scan with Camera
                        </button>
                      </div>
                    ) : (
                      // Desktop file upload
                      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                        <input
                          type="file"
                          accept="image/*,.pdf"
                          onChange={e => setDocumentForm({ ...documentForm, file: e.target.files[0] })}
                          style={{
                            flex: 1,
                            padding: '12px 16px',
                            border: '2px solid #e5e7eb',
                            borderRadius: 8,
                            fontSize: 14,
                            background: '#fff',
                            transition: 'border-color 0.2s ease',
                            outline: 'none'
                          }}
                          onFocus={(e) => e.target.style.borderColor = '#667eea'}
                          onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                          required
                        />
                      </div>
                    )}
                    
                    <p style={{ 
                      fontSize: isMobile ? 12 : 12, 
                      color: '#6b7280', 
                      margin: isMobile ? '12px 0 0 0' : '8px 0 0 0',
                      textAlign: isMobile ? 'center' : 'left'
                    }}>
                      Supported formats: JPEG, PNG, GIF, PDF (Max 10MB)
                    </p>
                  </div>

                  <button
                    type="submit"
                    disabled={uploadingDocument}
                    style={{
                      padding: isMobile ? '16px 24px' : '12px 24px',
                      background: uploadingDocument ? '#9ca3af' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: '#fff',
                      border: 'none',
                      borderRadius: isMobile ? 12 : 8,
                      fontSize: isMobile ? 16 : 14,
                      fontWeight: 600,
                      cursor: uploadingDocument ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s ease',
                      boxShadow: uploadingDocument ? 'none' : '0 4px 16px rgba(102,126,234,0.3)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 8
                    }}
                    onMouseOver={(e) => !uploadingDocument && (e.target.style.transform = 'translateY(-2px)')}
                    onMouseOut={(e) => !uploadingDocument && (e.target.style.transform = 'translateY(0)')}
                  >
                    {uploadingDocument ? (
                      <>
                        <div style={{
                          width: 16,
                          height: 16,
                          border: '2px solid #fff',
                          borderTop: '2px solid transparent',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite'
                        }} />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <FaUpload />
                        Upload Document
                      </>
                    )}
                  </button>
                </form>
              </div>

              {/* Existing Documents */}
              <div>
                <h4 style={{ 
                  fontSize: isMobile ? 16 : 16, 
                  fontWeight: 600, 
                  color: '#374151', 
                  margin: '0 0 16px 0',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8
                }}>
                  <FaFileAlt style={{ color: '#667eea', fontSize: isMobile ? 14 : 16 }} />
                  Existing Documents
                </h4>
                
                {candidateDocuments.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 16 : 12 }}>
                    {candidateDocuments.map((doc) => (
                      <div key={doc.id} style={{
                        background: '#fff',
                        border: '1px solid #e5e7eb',
                        borderRadius: isMobile ? 12 : 8,
                        padding: isMobile ? 16 : 16,
                        display: 'flex',
                        flexDirection: isMobile ? 'column' : 'row',
                        justifyContent: 'space-between',
                        alignItems: isMobile ? 'stretch' : 'center',
                        gap: isMobile ? 12 : 0,
                        boxShadow: isMobile ? '0 2px 8px rgba(0,0,0,0.1)' : 'none'
                      }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ 
                            fontSize: isMobile ? 15 : 14, 
                            fontWeight: 600, 
                            color: '#374151',
                            textTransform: 'capitalize',
                            marginBottom: isMobile ? 8 : 4,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8
                          }}>
                            {doc.document_type === 'aadhar' && '🆔'}
                            {doc.document_type === 'school_certificate' && '🎓'}
                            {doc.document_type === 'graduation_certificate' && '📜'}
                            {doc.document_type === 'iti_diploma_certificate' && '🔧'}
                            {doc.document_type === 'pancard' && '🪪'}
                            {doc.document_type === 'signature' && '✍️'}
                            {doc.document_type.replace('_', ' ')}
                          </div>
                          <div style={{ 
                            fontSize: isMobile ? 13 : 12, 
                            color: '#6b7280',
                            marginBottom: isMobile ? 8 : 4,
                            wordBreak: 'break-word'
                          }}>
                            {doc.file_name}
                          </div>
                          <div style={{ 
                            fontSize: isMobile ? 12 : 12, 
                            color: '#9ca3af'
                          }}>
                            Uploaded: {new Date(doc.created_at).toLocaleDateString()}
                          </div>
                        </div>
                        <div style={{ 
                          display: 'flex', 
                          gap: isMobile ? 8 : 8,
                          flexDirection: isMobile ? 'row' : 'row',
                          justifyContent: isMobile ? 'stretch' : 'flex-end'
                        }}>
                          <button
                            onClick={() => handleDownloadDocument(doc.id, doc.file_name)}
                            style={{
                              padding: isMobile ? '12px 16px' : '8px 12px',
                              background: '#10b981',
                              color: '#fff',
                              border: 'none',
                              borderRadius: isMobile ? 8 : 6,
                              fontSize: isMobile ? 14 : 12,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: isMobile ? 8 : 4,
                              transition: 'all 0.2s ease',
                              flex: isMobile ? 1 : 'none',
                              fontWeight: isMobile ? 600 : 'normal'
                            }}
                            onMouseOver={(e) => e.target.style.background = '#059669'}
                            onMouseOut={(e) => e.target.style.background = '#10b981'}
                          >
                            <FaDownload />
                            {isMobile ? 'Download' : 'Download'}
                          </button>
                          <button
                            onClick={() => handleDeleteDocument(doc.id)}
                            style={{
                              padding: isMobile ? '12px 16px' : '8px 12px',
                              background: '#ef4444',
                              color: '#fff',
                              border: 'none',
                              borderRadius: isMobile ? 8 : 6,
                              fontSize: isMobile ? 14 : 12,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: isMobile ? 8 : 4,
                              transition: 'all 0.2s ease',
                              flex: isMobile ? 1 : 'none',
                              fontWeight: isMobile ? 600 : 'normal'
                            }}
                            onMouseOver={(e) => e.target.style.background = '#dc2626'}
                            onMouseOut={(e) => e.target.style.background = '#ef4444'}
                          >
                            <FaTrash />
                            {isMobile ? 'Delete' : 'Delete'}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ 
                    textAlign: 'center', 
                    padding: isMobile ? 32 : 40, 
                    color: '#6b7280',
                    background: '#f9fafb',
                    borderRadius: isMobile ? 12 : 8,
                    border: '1px solid #e5e7eb'
                  }}>
                    <FaFileAlt style={{ fontSize: isMobile ? 40 : 48, marginBottom: 16, opacity: 0.5 }} />
                    <p style={{ fontSize: isMobile ? 14 : 14, margin: 0, lineHeight: 1.5 }}>
                      No documents uploaded yet.<br />
                      Upload documents using the form above.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stipend Upload Monitoring Tab - Table only, full page */}
      {activeTab === 'stipend-monitoring' && (
        <div style={{
          padding: isMobile ? 16 : 24,
          width: '100%',
          maxWidth: '100%',
          minHeight: '100%',
          display: 'flex',
          flexDirection: 'column'
        }}>
          {/* Minimal year selector above table */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
            <select
              value={stipendUploadYear}
              onChange={(e) => setStipendUploadYear(parseInt(e.target.value))}
              style={{
                padding: '8px 12px',
                border: '1px solid #e2e8f0',
                borderRadius: 8,
                fontSize: 14,
                fontWeight: 500,
                background: '#fff',
                color: '#0f172a',
                outline: 'none',
                cursor: 'pointer'
              }}
            >
              {[new Date().getFullYear() - 1, new Date().getFullYear(), new Date().getFullYear() + 1].map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            {stipendUploadLoading ? (
              <div style={{ textAlign: 'center', padding: 48, background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0' }}>
                <div style={{ width: 44, height: 44, border: '3px solid #e2e8f0', borderTopColor: '#6366f1', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 16px' }} />
                <p style={{ color: '#64748b', fontSize: 14, margin: 0 }}>Loading...</p>
              </div>
            ) : stipendUploadStatus.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 48, background: '#fff', borderRadius: 16, border: '1px dashed #e2e8f0' }}>
                <FaChartLine style={{ fontSize: 40, color: '#cbd5e1', marginBottom: 12 }} />
                <p style={{ fontSize: 15, fontWeight: 600, color: '#475569', margin: '0 0 4px 0' }}>No data</p>
                <p style={{ fontSize: 13, color: '#94a3b8', margin: 0 }}>No companies or upload data for this year.</p>
              </div>
            ) : (
              <>
                {/* Company rows - compact table-like */}
                <div style={{
                  background: '#fff',
                  borderRadius: 16,
                  border: '1px solid #e2e8f0',
                  overflow: 'hidden',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
                }}>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? '1fr' : 'minmax(160px, 1fr) 100px 1fr 80px',
                    gap: 12,
                    padding: '14px 20px',
                    background: '#f8fafc',
                    borderBottom: '1px solid #e2e8f0',
                    fontSize: 11,
                    fontWeight: 700,
                    color: '#64748b',
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em'
                  }}>
                    <span>Company</span>
                    {!isMobile && <span style={{ textAlign: 'center' }}>Progress</span>}
                    <span>{isMobile ? 'Months' : 'Jan–Dec'}</span>
                    <span style={{ textAlign: 'center' }}>Done</span>
                  </div>
                  {stipendUploadStatus.map((company) => {
                    const missingMonths = company.months.filter(m => !m.uploaded);
                    const uploadedMonths = company.months.filter(m => m.uploaded);
                    const pct = company.months.length ? Math.round((uploadedMonths.length / company.months.length) * 100) : 0;
                    return (
                      <div
                        key={company.id}
                        style={{
                          display: 'grid',
                          gridTemplateColumns: isMobile ? '1fr' : 'minmax(160px, 1fr) 100px 1fr 80px',
                          gap: 12,
                          alignItems: 'center',
                          padding: '16px 20px',
                          borderBottom: '1px solid #f1f5f9',
                          transition: 'background 0.15s'
                        }}
                        onMouseOver={(e) => { e.currentTarget.style.background = '#fafafa'; }}
                        onMouseOut={(e) => { e.currentTarget.style.background = '#fff'; }}
                      >
                        <div>
                          <div style={{ fontSize: 15, fontWeight: 600, color: '#0f172a', marginBottom: 2 }}>{company.company_name}</div>
                          {company.establishment_code && (
                            <div style={{ fontSize: 12, color: '#64748b' }}>{company.establishment_code}</div>
                          )}
                        </div>
                        {!isMobile && (
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <div style={{
                              width: 44,
                              height: 44,
                              borderRadius: '50%',
                              background: pct === 100 ? '#dcfce7' : pct >= 50 ? '#fef9c3' : '#fee2e2',
                              border: `2px solid ${pct === 100 ? '#22c55e' : pct >= 50 ? '#eab308' : '#fca5a5'}`,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: 12,
                              fontWeight: 800,
                              color: pct === 100 ? '#16a34a' : pct >= 50 ? '#a16207' : '#dc2626'
                            }}>
                              {pct}%
                            </div>
                          </div>
                        )}
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                          {company.months.map((month) => {
                            const monthLabel = (month.monthName || '').length >= 3 ? (month.monthName || '').substring(0, 3) : (month.monthName || '');
                            return (
                              <div
                                key={month.month}
                                title={month.uploaded ? `${month.monthName}: ${month.file_count} file(s)` : `${month.monthName}: Not uploaded`}
                                style={{
                                  minWidth: isMobile ? 36 : 40,
                                  padding: '4px 2px',
                                  borderRadius: 8,
                                  background: month.uploaded ? '#22c55e' : '#e2e8f0',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  flexShrink: 0,
                                  gap: 0
                                }}
                              >
                                <span style={{ fontSize: 9, fontWeight: 700, color: month.uploaded ? '#fff' : '#64748b', lineHeight: 1, textTransform: 'uppercase' }}>{monthLabel}</span>
                                {month.uploaded ? <FaCheck style={{ fontSize: 10, color: '#fff', marginTop: 1 }} /> : <FaExclamationTriangle style={{ fontSize: 8, color: '#94a3b8', marginTop: 1 }} />}
                              </div>
                            );
                          })}
                        </div>
                        <div style={{ textAlign: 'center', fontSize: 14, fontWeight: 600, color: '#16a34a' }}>{uploadedMonths.length}</div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Reports & Notifications - same as Admin */}
      {activeTab === 'rewards' && (
        <div style={{ width: '100%', padding: 24 }}>
          <AdminRewardsDashboard getToken={() => localStorage.getItem('gogig_spoc_access')} />
        </div>
      )}
      {activeTab === 'gamingReport' && (
        <div style={{ width: '100%', padding: 24 }}>
          <AdminGamingReport apiBase="api/spoc" getToken={() => localStorage.getItem('gogig_spoc_access')} />
        </div>
      )}
      {activeTab === 'referralReport' && (
        <div style={{ width: '100%', padding: 24 }}>
          <AdminReferralReport getToken={() => localStorage.getItem('gogig_spoc_access')} fetch={spocFetch} hideConfig />
        </div>
      )}
      {activeTab === 'referredCandidatesStipendReport' && (
        <div style={{ width: '100%', padding: 24 }}>
          <AdminReferredCandidatesStipendReport apiBase="api/spoc" getToken={() => localStorage.getItem('gogig_spoc_access')} fetch={spocFetch} />
        </div>
      )}
      {activeTab === 'spocReport' && (
        <div style={{ width: '100%', padding: 24 }}>
          <AdminSPOCReport apiBase="api/spoc" getToken={() => localStorage.getItem('gogig_spoc_access')} fetch={spocFetch} />
        </div>
      )}
      {activeTab === 'spocUploadStatus' && (
        <div style={{ width: '100%', padding: 24 }}>
          <AdminSPOCUploadStatusReport apiBase="api/spoc" getToken={() => localStorage.getItem('gogig_spoc_access')} fetch={spocFetch} />
        </div>
      )}
      {activeTab === 'workDiary' && (
        <div style={{ width: '100%', padding: 24 }}>
          <AdminWorkDiaryDashboard apiUrl={API_URL} adminFetch={spocFetch} apiBasePath="api/spoc" />
        </div>
      )}
      {activeTab === 'salaryAdvance' && (
        <div style={{ width: '100%', padding: 24 }}>
          <AdminSalaryAdvanceDashboard apiBase="api/spoc" getToken={() => localStorage.getItem('gogig_spoc_access')} />
        </div>
      )}
      {activeTab === 'higherEducation' && (
        <div style={{ width: '100%', padding: 24 }}>
          <AdminHigherEducationDashboard getToken={() => localStorage.getItem('gogig_spoc_access')} />
        </div>
      )}
      {activeTab === 'retentionReport' && (
        <div style={{ width: '100%', padding: 24 }}>
          <AdminRetentionReport getToken={() => localStorage.getItem('gogig_spoc_access')} fetch={spocFetch} retentionReportPath="/api/spoc/retention-report" />
        </div>
      )}
      {activeTab === 'assessmentDueReport' && (
        <div style={{ width: '100%', padding: 24 }}>
          <AdminAssessmentDueReport getToken={() => localStorage.getItem('gogig_spoc_access')} fetch={spocFetch} />
        </div>
      )}
      {activeTab === 'companyReports' && (
        <div style={{ width: '100%', minHeight: 0, overflow: 'auto' }}>
          <SPOCCompanyReports />
        </div>
      )}
      {activeTab === 'attritionReport' && (
        <div style={{ width: '100%', minHeight: 0, overflow: 'auto' }}>
          <SPOCAttritionReport />
        </div>
      )}

      {/* Supervisor Attendance View */}
      {activeTab === 'supervisor-attendance' && (
        <div style={{ 
          width: '100%', 
          maxWidth: '100%',
          padding: isMobile ? '16px' : '32px',
          boxSizing: 'border-box',
          minHeight: 'calc(100vh - 200px)',
          display: 'flex',
          flexDirection: 'column'
        }}>
          {/* Header Section - Removed duplicate title, using the one from main header */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'flex-end', 
            alignItems: isMobile ? 'flex-start' : 'center',
            marginBottom: 24,
            flexWrap: 'wrap',
            gap: 16,
            flexDirection: isMobile ? 'column' : 'row'
          }}>
            <div style={{ 
              display: 'flex', 
              gap: 12, 
              flexWrap: 'wrap',
              width: isMobile ? '100%' : 'auto',
              alignItems: isMobile ? 'stretch' : 'center'
            }}>
              <select
                value={selectedSupervisorForAttendance}
                onChange={(e) => setSelectedSupervisorForAttendance(e.target.value)}
                style={{
                  padding: '10px 14px',
                  border: '2px solid #e5e7eb',
                  borderRadius: 8,
                  fontSize: 14,
                  outline: 'none',
                  cursor: 'pointer',
                  background: '#fff',
                  minWidth: isMobile ? '100%' : 200,
                  flex: isMobile ? '1 1 100%' : '0 1 auto',
                  transition: 'border-color 0.2s',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                }}
                onFocus={(e) => e.target.style.borderColor = '#667eea'}
                onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
              >
                <option value="">All Supervisors</option>
                {(supervisorsForDropdown.length ? supervisorsForDropdown : supervisors).map(supervisor => (
                  <option key={supervisor.id} value={supervisor.id}>
                    {supervisor.name}
                  </option>
                ))}
              </select>
              <select
                value={attendanceMonth}
                onChange={(e) => setAttendanceMonth(parseInt(e.target.value))}
                style={{
                  padding: '10px 14px',
                  border: '2px solid #e5e7eb',
                  borderRadius: 8,
                  fontSize: 14,
                  outline: 'none',
                  cursor: 'pointer',
                  background: '#fff',
                  minWidth: isMobile ? '100%' : 150,
                  flex: isMobile ? '1 1 100%' : '0 1 auto',
                  transition: 'border-color 0.2s',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                }}
                onFocus={(e) => e.target.style.borderColor = '#667eea'}
                onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
              >
                {[1,2,3,4,5,6,7,8,9,10,11,12].map(month => (
                  <option key={month} value={month}>
                    {new Date(2000, month - 1).toLocaleString('default', { month: 'long' })}
                  </option>
                ))}
              </select>
              <select
                value={attendanceYear}
                onChange={(e) => setAttendanceYear(parseInt(e.target.value))}
                style={{
                  padding: '10px 14px',
                  border: '2px solid #e5e7eb',
                  borderRadius: 8,
                  fontSize: 14,
                  outline: 'none',
                  cursor: 'pointer',
                  background: '#fff',
                  minWidth: isMobile ? '100%' : 120,
                  flex: isMobile ? '1 1 100%' : '0 1 auto',
                  transition: 'border-color 0.2s',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                }}
                onFocus={(e) => e.target.style.borderColor = '#667eea'}
                onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
              >
                {[new Date().getFullYear() - 1, new Date().getFullYear(), new Date().getFullYear() + 1].map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
              <button
                onClick={exportSupervisorAttendance}
                style={{
                  padding: '10px 20px',
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 8,
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  minWidth: isMobile ? '100%' : 'auto',
                  justifyContent: 'center',
                  boxShadow: '0 2px 4px rgba(16, 185, 129, 0.3)',
                  transition: 'transform 0.2s, box-shadow 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 4px 8px rgba(16, 185, 129, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 4px rgba(16, 185, 129, 0.3)';
                }}
              >
                <FaFileExcel />
                Export CSV
              </button>
            </div>
          </div>

          {/* Content Section */}
          {supervisorAttendanceLoading ? (
            <div style={{ 
              textAlign: 'center', 
              padding: 60,
              background: '#fff',
              borderRadius: 12,
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
            }}>
              <div style={{
                width: 48,
                height: 48,
                border: '4px solid #e5e7eb',
                borderTop: '4px solid #667eea',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto 20px'
              }} />
              <p style={{ color: '#6b7280', fontSize: 15, fontWeight: 500 }}>Loading attendance records...</p>
            </div>
          ) : supervisorAttendance.length === 0 ? (
            <div style={{ 
              textAlign: 'center', 
              padding: 60, 
              color: '#6b7280',
              background: '#fff',
              borderRadius: 12,
              border: '1px solid #e5e7eb',
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
            }}>
              <FaClock style={{ fontSize: 56, marginBottom: 20, opacity: 0.4, color: '#9ca3af' }} />
              <p style={{ fontSize: 16, margin: 0, fontWeight: 500 }}>No attendance records found for the selected period.</p>
            </div>
          ) : (
            <div style={{ 
              background: '#fff', 
              borderRadius: 12, 
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              border: '1px solid #e5e7eb',
              overflow: 'hidden',
              width: '100%',
              flex: 1,
              display: 'flex',
              flexDirection: 'column'
            }}>
              <div style={{ overflowX: 'auto', width: '100%', flex: 1 }}>
                <table style={{ 
                  width: '100%', 
                  borderCollapse: 'collapse',
                  minWidth: isMobile ? 800 : '100%'
                }}>
                  <thead>
                    <tr style={{ background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)', borderBottom: '2px solid #e5e7eb' }}>
                      <th style={{ 
                        padding: '16px', 
                        textAlign: 'left', 
                        fontWeight: 700, 
                        color: '#1f2937', 
                        fontSize: 14,
                        whiteSpace: 'nowrap',
                        minWidth: 200
                      }}>Supervisor</th>
                      <th style={{ 
                        padding: '16px', 
                        textAlign: 'left', 
                        fontWeight: 700, 
                        color: '#1f2937', 
                        fontSize: 14,
                        whiteSpace: 'nowrap',
                        minWidth: 160
                      }}>Date</th>
                      <th style={{ 
                        padding: '16px', 
                        textAlign: 'left', 
                        fontWeight: 700, 
                        color: '#1f2937', 
                        fontSize: 14,
                        whiteSpace: 'nowrap',
                        minWidth: 120
                      }}>Check-In</th>
                      <th style={{ 
                        padding: '16px', 
                        textAlign: 'left', 
                        fontWeight: 700, 
                        color: '#1f2937', 
                        fontSize: 14,
                        whiteSpace: 'nowrap',
                        minWidth: 120
                      }}>Check-Out</th>
                      <th style={{ 
                        padding: '16px', 
                        textAlign: 'left', 
                        fontWeight: 700, 
                        color: '#1f2937', 
                        fontSize: 14,
                        whiteSpace: 'nowrap',
                        minWidth: 130
                      }}>Status</th>
                      <th style={{ 
                        padding: '16px', 
                        textAlign: 'left', 
                        fontWeight: 700, 
                        color: '#1f2937', 
                        fontSize: 14,
                        whiteSpace: 'nowrap',
                        minWidth: 140
                      }}>Images</th>
                    </tr>
                  </thead>
                  <tbody>
                    {supervisorAttendance.map((att, index) => (
                      <tr 
                        key={att.id} 
                        style={{ 
                          borderBottom: index < supervisorAttendance.length - 1 ? '1px solid #f3f4f6' : 'none',
                          background: index % 2 === 0 ? '#fff' : '#fafbfc',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#f0f9ff'}
                        onMouseLeave={(e) => e.currentTarget.style.background = index % 2 === 0 ? '#fff' : '#fafbfc'}
                      >
                        <td style={{ padding: '16px', fontSize: 14, color: '#374151' }}>
                          {att.Supervisor ? (
                            <div>
                              <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: 4 }}>{att.Supervisor.name}</div>
                              <div style={{ fontSize: 12, color: '#6b7280' }}>{att.Supervisor.email}</div>
                            </div>
                          ) : (
                            <span style={{ color: '#9ca3af' }}>-</span>
                          )}
                        </td>
                        <td style={{ padding: '16px', fontSize: 14, color: '#374151', whiteSpace: 'nowrap' }}>
                          {formatDateIST(att.date)}
                        </td>
                        <td style={{ padding: '16px', fontSize: 14, color: '#374151', fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                          {formatTimeIST(att.check_in_time)}
                        </td>
                        <td style={{ padding: '16px', fontSize: 14, color: '#374151', fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                          {formatTimeIST(att.check_out_time)}
                        </td>
                        <td style={{ padding: '16px' }}>
                          <span style={{
                            padding: '6px 14px',
                            borderRadius: 20,
                            fontSize: 12,
                            fontWeight: 600,
                            display: 'inline-block',
                            background: att.status === 'checked_out' ? '#dcfce7' : att.status === 'checked_in' ? '#fef3c7' : '#fee2e2',
                            color: att.status === 'checked_out' ? '#166534' : att.status === 'checked_in' ? '#92400e' : '#991b1b',
                            whiteSpace: 'nowrap'
                          }}>
                            {att.status === 'checked_out' ? 'Complete' : att.status === 'checked_in' ? 'Checked In' : 'Absent'}
                          </span>
                        </td>
                        <td style={{ padding: '16px' }}>
                          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                            {att.check_in_image && (
                              <img 
                                src={`${process.env.REACT_APP_API_URL || 'http://localhost:5001'}${att.check_in_image}`}
                                alt="Check-in"
                                style={{
                                  width: 56,
                                  height: 56,
                                  borderRadius: 8,
                                  objectFit: 'cover',
                                  border: '2px solid #e5e7eb',
                                  cursor: 'pointer',
                                  transition: 'transform 0.2s, box-shadow 0.2s',
                                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                }}
                                onClick={() => window.open(`${process.env.REACT_APP_API_URL || 'http://localhost:5001'}${att.check_in_image}`, '_blank')}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.transform = 'scale(1.05)';
                                  e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.transform = 'scale(1)';
                                  e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                                }}
                              />
                            )}
                            {att.check_out_image && (
                              <img 
                                src={`${process.env.REACT_APP_API_URL || 'http://localhost:5001'}${att.check_out_image}`}
                                alt="Check-out"
                                style={{
                                  width: 56,
                                  height: 56,
                                  borderRadius: 8,
                                  objectFit: 'cover',
                                  border: '2px solid #e5e7eb',
                                  cursor: 'pointer',
                                  transition: 'transform 0.2s, box-shadow 0.2s',
                                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                }}
                                onClick={() => window.open(`${process.env.REACT_APP_API_URL || 'http://localhost:5001'}${att.check_out_image}`, '_blank')}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.transform = 'scale(1.05)';
                                  e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.transform = 'scale(1)';
                                  e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                                }}
                              />
                            )}
                            {!att.check_in_image && !att.check_out_image && (
                              <span style={{ color: '#9ca3af', fontSize: 12 }}>-</span>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* Pagination - Always show if there are records */}
              {supervisorAttendance.length > 0 && (
                <div className="admin-pagination-wrap">
                  <span className="admin-pagination-info">
                    {attendanceTotal > 0 ? (
                      <>Showing {((attendancePage - 1) * 10) + 1} to {Math.min(attendancePage * 10, attendanceTotal)} of {attendanceTotal} records</>
                    ) : (
                      <>Showing {supervisorAttendance.length} record{supervisorAttendance.length !== 1 ? 's' : ''}</>
                    )}
                  </span>
                  <div className="admin-pagination-btns">
                    <button type="button" className="admin-pagination-btn primary" onClick={() => setAttendancePage(Math.max(1, attendancePage - 1))} disabled={attendancePage === 1}>Previous</button>
                    <span className="admin-pagination-info">Page {attendancePage} {attendanceTotalPages > 1 ? `of ${attendanceTotalPages}` : ''}</span>
                    <button type="button" className="admin-pagination-btn primary" onClick={() => setAttendancePage(Math.min(attendanceTotalPages, attendancePage + 1))} disabled={attendancePage >= attendanceTotalPages}>Next</button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Camera Modal for Mobile Document Scanning */}
      {showCameraModal && (
        <div className="gogig-modal-bg" onClick={stopCamera}>
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.95)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
            padding: isMobile ? 0 : 20
          }} onClick={e => e.stopPropagation()}>
            <div style={{
              background: isMobile ? 'transparent' : '#fff',
              borderRadius: isMobile ? 0 : 16,
              padding: isMobile ? 0 : 20,
              maxWidth: isMobile ? '100%' : '90%',
              maxHeight: isMobile ? '100%' : '90%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: isMobile ? 0 : 20,
              width: isMobile ? '100%' : 'auto',
              height: isMobile ? '100%' : 'auto'
            }}>
              {!isMobile && (
                <h3 style={{ 
                  fontSize: 18, 
                  fontWeight: 700, 
                  color: '#374151', 
                  margin: 0,
                  textAlign: 'center'
                }}>
                  Scan Document
                </h3>
              )}
              
              <div style={{
                position: 'relative',
                borderRadius: isMobile ? 0 : 12,
                overflow: 'hidden',
                border: isMobile ? 'none' : '2px solid #e5e7eb',
                width: isMobile ? '100%' : 'auto',
                height: isMobile ? '100%' : 'auto',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <video
                  id="camera-video"
                  autoPlay
                  playsInline
                  style={{
                    width: isMobile ? '100%' : '100%',
                    maxWidth: isMobile ? 'none' : 400,
                    height: isMobile ? '100%' : 'auto',
                    display: 'block',
                    objectFit: isMobile ? 'cover' : 'contain'
                  }}
                  ref={video => {
                    if (video && cameraStream) {
                      video.srcObject = cameraStream;
                    }
                  }}
                />
                <div style={{
                  position: 'absolute',
                  top: isMobile ? '20%' : 0,
                  left: isMobile ? '10%' : 0,
                  right: isMobile ? '10%' : 0,
                  bottom: isMobile ? '30%' : 0,
                  border: '3px solid #667eea',
                  borderRadius: isMobile ? 16 : 8,
                  pointerEvents: 'none',
                  boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.5)'
                }} />
                
                {/* Mobile overlay text */}
                {isMobile && (
                  <div style={{
                    position: 'absolute',
                    top: 20,
                    left: 20,
                    right: 20,
                    textAlign: 'center',
                    color: '#fff',
                    background: 'rgba(0, 0, 0, 0.7)',
                    padding: '12px 16px',
                    borderRadius: 12,
                    fontSize: 16,
                    fontWeight: 600
                  }}>
                    📄 Position document within the frame
                  </div>
                )}
              </div>
              
              <div style={{ 
                display: 'flex', 
                gap: isMobile ? 16 : 12,
                position: isMobile ? 'absolute' : 'relative',
                bottom: isMobile ? 40 : 'auto',
                left: isMobile ? 20 : 'auto',
                right: isMobile ? 20 : 'auto',
                width: isMobile ? 'calc(100% - 40px)' : 'auto'
              }}>
                <button
                  onClick={capturePhoto}
                  style={{
                    padding: isMobile ? '16px 24px' : '12px 24px',
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: isMobile ? 12 : 8,
                    fontSize: isMobile ? 16 : 14,
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: isMobile ? 12 : 8,
                    transition: 'all 0.2s ease',
                    flex: isMobile ? 1 : 'none',
                    boxShadow: '0 4px 16px rgba(16,185,129,0.3)'
                  }}
                  onMouseOver={(e) => e.target.style.transform = 'translateY(-2px)'}
                  onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
                >
                  <FaCamera style={{ fontSize: isMobile ? 18 : 16 }} />
                  {isMobile ? 'Capture' : 'Capture'}
                </button>
                <button
                  onClick={stopCamera}
                  style={{
                    padding: isMobile ? '16px 24px' : '12px 24px',
                    background: isMobile ? 'rgba(107, 114, 128, 0.9)' : '#6b7280',
                    color: '#fff',
                    border: 'none',
                    borderRadius: isMobile ? 12 : 8,
                    fontSize: isMobile ? 16 : 14,
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    flex: isMobile ? 1 : 'none'
                  }}
                  onMouseOver={(e) => e.target.style.background = isMobile ? 'rgba(75, 85, 99, 0.9)' : '#4b5563'}
                  onMouseOut={(e) => e.target.style.background = isMobile ? 'rgba(107, 114, 128, 0.9)' : '#6b7280'}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Applicants Modal */}
      {showApplicantsModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: 20
        }}>
          <div style={{
            background: '#fff',
            borderRadius: 12,
            width: '100%',
            maxWidth: 900,
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
          }}>
            {/* Header */}
            <div style={{
              padding: 20,
              borderBottom: '1px solid #e5e7eb',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: '#374151' }}>
                Applicants for {selectedJob?.title}
              </h3>
              <button
                onClick={() => {
                  setShowApplicantsModal(false);
                  setSelectedJob(null);
                  setApplicants([]);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: 24,
                  color: '#6b7280',
                  cursor: 'pointer',
                  padding: 0,
                  width: 32,
                  height: 32,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ×
              </button>
            </div>

            {/* Content */}
            <div style={{
              padding: 20,
              overflowY: 'auto',
              flex: 1
            }}>
              {applicantsLoading ? (
                <div style={{ textAlign: 'center', padding: 40 }}>
                  <div style={{
                    width: 40,
                    height: 40,
                    border: '4px solid #e5e7eb',
                    borderTop: '4px solid #667eea',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    margin: '0 auto'
                  }} />
                  <p style={{ marginTop: 16, color: '#6b7280' }}>Loading applicants...</p>
                </div>
              ) : applicants.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>
                  No applicants yet for this job.
                </div>
              ) : (
                <div>
                  <div style={{ background: '#fff', borderRadius: 8, overflow: 'hidden', border: '1px solid #e5e7eb', marginBottom: 16 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ background: '#f8fafc' }}>
                          <th style={{ padding: '12px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#374151', borderBottom: '2px solid #e5e7eb' }}>Name</th>
                          <th style={{ padding: '12px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#374151', borderBottom: '2px solid #e5e7eb' }}>Email</th>
                          <th style={{ padding: '12px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#374151', borderBottom: '2px solid #e5e7eb' }}>Mobile</th>
                          <th style={{ padding: '12px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#374151', borderBottom: '2px solid #e5e7eb' }}>Qualification</th>
                          <th style={{ padding: '12px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#374151', borderBottom: '2px solid #e5e7eb' }}>Course</th>
                          <th style={{ padding: '12px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#374151', borderBottom: '2px solid #e5e7eb' }}>Status</th>
                          <th style={{ padding: '12px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#374151', borderBottom: '2px solid #e5e7eb' }}>Applied Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {applicants.map((app, index) => (
                          <tr key={app.id} style={{ backgroundColor: index % 2 === 0 ? '#fff' : '#fafafa' }}>
                            <td style={{ padding: '12px', borderBottom: '1px solid #e5e7eb', fontSize: 13 }}>{app.name || '-'}</td>
                            <td style={{ padding: '12px', borderBottom: '1px solid #e5e7eb', fontSize: 13 }}>{app.email || '-'}</td>
                            <td style={{ padding: '12px', borderBottom: '1px solid #e5e7eb', fontSize: 13 }}>{app.mobile || '-'}</td>
                            <td style={{ padding: '12px', borderBottom: '1px solid #e5e7eb', fontSize: 13 }}>{app.qualification || '-'}</td>
                            <td style={{ padding: '12px', borderBottom: '1px solid #e5e7eb', fontSize: 13 }}>{app.course || '-'}</td>
                            <td style={{ padding: '12px', borderBottom: '1px solid #e5e7eb', fontSize: 13 }}>
                              <select
                                value={app.status || 'applied'}
                                onChange={(e) => handleApplicantStatusChange(app.id, e.target.value)}
                                style={{
                                  padding: '4px 8px',
                                  borderRadius: 4,
                                  fontSize: 12,
                                  fontWeight: 600,
                                  border: '1px solid #e5e7eb',
                                  background: app.status === 'selected' ? '#dcfce7' : app.status === 'rejected' ? '#fee2e2' : app.status === 'interview scheduled' ? '#fef3c7' : '#f3f4f6',
                                  color: app.status === 'selected' ? '#166534' : app.status === 'rejected' ? '#991b1b' : app.status === 'interview scheduled' ? '#92400e' : '#374151',
                                  cursor: 'pointer',
                                  minWidth: 140
                                }}
                              >
                                <option value="applied">Applied</option>
                                <option value="interview scheduled">Interview scheduled</option>
                                <option value="selected">Selected</option>
                                <option value="rejected">Rejected</option>
                              </select>
                            </td>
                            <td style={{ padding: '12px', borderBottom: '1px solid #e5e7eb', fontSize: 13 }}>
                              {app.applied_at ? new Date(app.applied_at).toLocaleDateString() : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p style={{ fontSize: 13, color: '#6b7280', marginTop: 8 }}>
                    Total: {applicants.length} applicant{applicants.length !== 1 ? 's' : ''}
                  </p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div style={{
              padding: 20,
              borderTop: '1px solid #e5e7eb',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <button
                onClick={() => {
                  const header = ['Name', 'Email', 'Mobile', 'Qualification', 'Course', 'Status', 'Applied Date'];
                  const rows = applicants.map(app => [
                    app.name || '',
                    app.email || '',
                    app.mobile || '',
                    app.qualification || '',
                    app.course || '',
                    app.status || 'applied',
                    app.applied_at ? new Date(app.applied_at).toLocaleDateString() : ''
                  ]);
                  const csvContent = [header, ...rows]
                    .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(","))
                    .join("\n");
                  
                  const BOM = '\uFEFF';
                  const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  const jobTitle = selectedJob?.title?.replace(/[^a-z0-9]/gi, '_') || 'job';
                  const dateStr = new Date().toISOString().split('T')[0];
                  a.download = `applicants_${jobTitle}_${dateStr}.csv`;
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  URL.revokeObjectURL(url);
                  toast.success('CSV downloaded successfully');
                }}
                disabled={applicants.length === 0}
                style={{
                  padding: '10px 20px',
                  background: applicants.length === 0 ? '#f3f4f6' : '#667eea',
                  color: applicants.length === 0 ? '#9ca3af' : '#fff',
                  border: 'none',
                  borderRadius: 8,
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: applicants.length === 0 ? 'not-allowed' : 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8
                }}
              >
                <FaDownload /> Download CSV ({applicants.length})
              </button>
              <button
                onClick={() => {
                  setShowApplicantsModal(false);
                  setSelectedJob(null);
                  setApplicants([]);
                }}
                style={{
                  padding: '10px 20px',
                  background: '#6b7280',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 8,
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Candidates CSV Modal - Supervisor Candidates tab */}
      {showCandidatesUploadModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: 20,
          overflowY: 'auto'
        }} onClick={() => setShowCandidatesUploadModal(false)}>
          <div style={{
            background: '#fff',
            borderRadius: 12,
            width: '100%',
            maxWidth: 600,
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }} onClick={e => e.stopPropagation()}>
            <h3 style={{ color: '#10b981', fontWeight: 700, fontSize: 24, marginBottom: 20, padding: '20px 24px 0' }}>
              Upload Candidates CSV
            </h3>
            <div style={{ padding: '0 24px 24px' }}>
              <p style={{ color: '#666', marginBottom: 16, fontSize: 14 }}>
                Upload a CSV with candidate information. Columns: Name, Email, Mobile (required), Referral Code, Qualification, Specialization, Institute Name.
              </p>
              <p style={{ color: '#92400e', marginBottom: 16, fontSize: 13, background: '#fef3c7', padding: 12, borderRadius: 8, border: '1px solid #fbbf24' }}>
                <strong>Assign to supervisor:</strong> If you selected a supervisor in the filter above, candidates will be assigned to that supervisor. Otherwise they are imported without a supervisor.
              </p>
              <button
                type="button"
                onClick={downloadCandidatesTemplate}
                style={{
                  padding: '8px 16px',
                  background: '#f0fdf4',
                  color: '#10b981',
                  border: '2px solid #10b981',
                  borderRadius: 8,
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  marginBottom: 20
                }}
              >
                <FaDownload />
                Download Template
              </button>
              <form onSubmit={handleCandidatesCsvUpload}>
                <div style={{ marginBottom: 20 }}>
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#333', fontSize: 14 }}>
                    Select CSV File *
                  </label>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleCandidatesFileChange}
                    required
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '2px solid #e5e7eb',
                      borderRadius: 8,
                      fontSize: 16,
                      outline: 'none'
                    }}
                  />
                </div>
                {candidatesUploadResult && (
                  <div style={{
                    padding: 12,
                    borderRadius: 8,
                    marginBottom: 16,
                    background: candidatesUploadResult.success ? '#dcfce7' : '#fee2e2',
                    color: candidatesUploadResult.success ? '#166534' : '#991b1b',
                    border: `1px solid ${candidatesUploadResult.success ? '#bbf7d0' : '#fecaca'}`
                  }}>
                    <strong>{candidatesUploadResult.message}</strong>
                  </div>
                )}
                <div style={{ display: 'flex', gap: 12 }}>
                  <button
                    type="submit"
                    disabled={candidatesUploading}
                    style={{
                      flex: 1,
                      padding: '12px 24px',
                      background: candidatesUploading ? '#9ca3af' : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 8,
                      fontWeight: 600,
                      cursor: candidatesUploading ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {candidatesUploading ? 'Uploading...' : 'Upload CSV'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowCandidatesUploadModal(false)}
                    style={{
                      flex: 1,
                      padding: '12px 24px',
                      background: '#f8fafc',
                      color: '#10b981',
                      border: '2px solid #10b981',
                      borderRadius: 8,
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Supervisor Candidate Edit Modal */}
      {showSupervisorCandidateEditModal && editingSupervisorCandidate && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: 20,
          overflowY: 'auto'
        }} onClick={() => setShowSupervisorCandidateEditModal(false)}>
          <div style={{
            background: '#fff',
            borderRadius: 12,
            width: '100%',
            maxWidth: 1200,
            maxHeight: '90vh',
            overflowY: 'auto',
            padding: 24,
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
          }} onClick={e => e.stopPropagation()}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 20
            }}>
              <h3 style={{
                color: '#667eea',
                fontWeight: 700,
                fontSize: 24,
                margin: 0
              }}>
                Edit: {editingSupervisorCandidate.name}
              </h3>
              <button
                onClick={() => setShowSupervisorCandidateEditModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: 24,
                  cursor: 'pointer',
                  color: '#666',
                  padding: 4,
                  borderRadius: 4
                }}
              >
                <FaTimes />
              </button>
            </div>

            <form onSubmit={handleUpdateSupervisorCandidate}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: 16
              }}>
                {Object.keys(supervisorCandidateEditForm).map((key) => (
                  <div key={key} style={key === 'address' || key === 'establishment_address' ? { gridColumn: '1 / -1' } : {}}>
                    <label style={{
                      display: 'block',
                      marginBottom: 4,
                      fontWeight: 600,
                      color: '#333',
                      fontSize: 14,
                      textTransform: 'capitalize'
                    }}>
                      {key.replace(/_/g, ' ')}
                    </label>
                    {key === 'address' || key === 'establishment_address' ? (
                      <textarea
                        value={supervisorCandidateEditForm[key]}
                        onChange={(e) => setSupervisorCandidateEditForm({
                          ...supervisorCandidateEditForm,
                          [key]: e.target.value
                        })}
                        rows={3}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          border: '1px solid #d1d5db',
                          borderRadius: 6,
                          fontSize: 14,
                          resize: 'vertical'
                        }}
                      />
                    ) : key === 'gender' || key === 'insurance' || key === 'id_card' || key === 'status' ? (
                      <select
                        value={supervisorCandidateEditForm[key]}
                        onChange={(e) => setSupervisorCandidateEditForm({
                          ...supervisorCandidateEditForm,
                          [key]: e.target.value
                        })}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          border: '1px solid #d1d5db',
                          borderRadius: 6,
                          fontSize: 14
                        }}
                      >
                        <option value="">Select {key.replace(/_/g, ' ')}</option>
                        {key === 'gender' && (
                          <>
                            <option value="Male">Male</option>
                            <option value="Female">Female</option>
                            <option value="Other">Other</option>
                          </>
                        )}
                        {(key === 'insurance' || key === 'id_card') && (
                          <>
                            <option value="Yes">Yes</option>
                            <option value="No">No</option>
                          </>
                        )}
                        {key === 'status' && [
                          'Data Record', 'Document Pending', 'Registration Pending',
                          'Email Pending', 'User ID Not Generated', 'Contract Not Generated',
                          'Active', 'Inactive', 'Complete', 'Terminate'
                        ].map(status => (
                          <option key={status} value={status}>{status}</option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type={key.includes('date') ? 'date' : key === 'stipend' ? 'number' : 'text'}
                        value={supervisorCandidateEditForm[key]}
                        onChange={(e) => setSupervisorCandidateEditForm({
                          ...supervisorCandidateEditForm,
                          [key]: e.target.value
                        })}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          border: '1px solid #d1d5db',
                          borderRadius: 6,
                          fontSize: 14
                        }}
                      />
                    )}
                  </div>
                ))}
              </div>

              <div style={{
                display: 'flex',
                gap: 12,
                marginTop: 24,
                justifyContent: 'flex-end'
              }}>
                <button
                  type="button"
                  onClick={() => setShowSupervisorCandidateEditModal(false)}
                  style={{
                    padding: '10px 20px',
                    background: '#f3f4f6',
                    color: '#374151',
                    border: 'none',
                    borderRadius: 6,
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    padding: '10px 20px',
                    background: '#667eea',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 6,
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  Update Candidate
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Supervisor Candidate Document Modal */}
      {showSupervisorCandidateDocumentModal && selectedSupervisorCandidate && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: 20
        }} onClick={() => setShowSupervisorCandidateDocumentModal(false)}>
          <div style={{
            background: '#fff',
            borderRadius: 12,
            width: '100%',
            maxWidth: 800,
            maxHeight: '90vh',
            overflowY: 'auto',
            padding: 24,
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
          }} onClick={e => e.stopPropagation()}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 20
            }}>
              <h3 style={{
                color: '#10b981',
                fontWeight: 700,
                fontSize: 24,
                margin: 0
              }}>
                Documents: {selectedSupervisorCandidate.name}
              </h3>
              <button
                onClick={() => setShowSupervisorCandidateDocumentModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: 24,
                  cursor: 'pointer',
                  color: '#666',
                  padding: 4,
                  borderRadius: 4
                }}
              >
                <FaTimes />
              </button>
            </div>

            {supervisorCandidateDocuments.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {supervisorCandidateDocuments.map((doc) => (
                  <div key={doc.id} style={{
                    padding: 16,
                    border: '1px solid #e5e7eb',
                    borderRadius: 8,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <div>
                      <p style={{ margin: 0, fontWeight: 600, color: '#374151' }}>
                        {doc.document_type}
                      </p>
                      <p style={{ margin: '4px 0 0 0', fontSize: 12, color: '#6b7280' }}>
                        {doc.file_name} • {doc.file_size ? `${(doc.file_size / 1024).toFixed(2)} KB` : 'N/A'}
                      </p>
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        onClick={() => handleDownloadSupervisorCandidateDocument(doc.id)}
                        style={{
                          padding: '6px 12px',
                          background: '#10b981',
                          color: '#fff',
                          border: 'none',
                          borderRadius: 6,
                          fontSize: 12,
                          fontWeight: 600,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 4
                        }}
                      >
                        <FaDownload style={{ fontSize: 10 }} />
                        Download
                      </button>
                      <button
                        onClick={() => handleDeleteSupervisorCandidateDocument(doc.id)}
                        style={{
                          padding: '6px 12px',
                          background: '#ef4444',
                          color: '#fff',
                          border: 'none',
                          borderRadius: 6,
                          fontSize: 12,
                          fontWeight: 600,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 4
                        }}
                      >
                        <FaTrash style={{ fontSize: 10 }} />
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>
                <FaFileAlt style={{ fontSize: 48, marginBottom: 16, opacity: 0.5 }} />
                <p>No documents found for this candidate.</p>
              </div>
            )}
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default SPOCDashboard;
